import type { Messages } from './en.js';

/**
 * The Arabic catalogue.
 *
 * Typed as an exact mirror of the English one, so a key added there without a
 * translation here is a compile error rather than a string that quietly falls
 * back to English in front of an Arabic-speaking customer. Excess keys are an
 * error too, so a key removed from English cannot linger here.
 *
 * Numerals stay Western Arabic unless the workspace opts into Eastern Arabic -
 * that is a separate setting from language, and the Gulf default is Latin
 * digits in an Arabic interface.
 */
export const ar: Messages = {
  'app.name': 'وين إي آر بي — المبيعات والتسويق',
  'app.shortName': 'وين إي آر بي',

  'object.lead': 'عميل محتمل',
  'object.lead.plural': 'العملاء المحتملون',
  'object.contact': 'جهة اتصال',
  'object.contact.plural': 'جهات الاتصال',
  'object.account': 'شركة',
  'object.account.plural': 'الشركات',
  'object.deal': 'صفقة',
  'object.deal.plural': 'الصفقات',
  'object.activity': 'نشاط',
  'object.activity.plural': 'الأنشطة',
  'object.nextStep': 'الخطوة التالية',
  'object.nextStep.plural': 'الخطوات التالية',
  'object.campaign': 'حملة',
  'object.campaign.plural': 'الحملات',
  'object.segment': 'جمهور',
  'object.segment.plural': 'الجماهير',
  'object.case': 'تذكرة',
  'object.case.plural': 'التذاكر',
  'object.quote': 'عرض سعر',
  'object.quote.plural': 'عروض الأسعار',
  'object.product': 'منتج',
  'object.product.plural': 'المنتجات',
  'object.subscription': 'اشتراك',
  'object.subscription.plural': 'الاشتراكات',
  'object.workspace': 'مساحة العمل',
  'object.permissionSet': 'مجموعة صلاحيات',
  'object.module': 'وحدة',
  'object.agentRun': 'نشاط الوكيل',
  'object.proposal': 'اقتراح',
  'object.proposal.plural': 'الاقتراحات',

  'nav.group.dashboard': 'لوحة القيادة',
  'nav.group.crm': 'إدارة العملاء',
  'nav.group.sales': 'المبيعات',
  'nav.group.marketing': 'التسويق',
  'nav.group.service': 'الخدمة',
  'nav.group.agents': 'الوكلاء',
  'nav.group.insight': 'التحليلات',
  'nav.group.admin': 'الإعداد',
  'nav.promotions': 'العروض',
  'nav.chat': 'الدردشة المباشرة',
  'nav.agentActivity': 'نشاط الوكلاء',
  'nav.modules': 'الوحدات',
  'nav.import': 'الاستيراد',
  'nav.plan': 'الخطة والاستخدام',
  'nav.inbox': 'صندوق الوارد',
  'nav.home': 'الرئيسية',
  'nav.work': 'مهامي',
  'nav.contacts': 'جهات الاتصال',
  'nav.leads': 'العملاء المحتملون',
  'nav.forecast': 'التوقعات',
  'nav.revenue': 'الإيرادات',
  'nav.deals': 'الصفقات',
  'nav.campaigns': 'الحملات',
  'nav.companies': 'الشركات',
  'nav.reportsList': 'التقارير',
  'nav.segments': 'الجماهير',
  'nav.journeys': 'الرحلات',
  'nav.automations': 'الأتمتة',
  'nav.products': 'المنتجات',
  'nav.pricelists': 'قوائم الأسعار',
  'nav.support': 'الدعم',
  'nav.agentsList': 'الوكلاء',
  'nav.agents': 'بانتظارك',
  'nav.reports': 'لوحات المعلومات',
  'nav.setup': 'الإعدادات',
  'nav.more': 'المزيد',
  'nav.allAreas': 'كل الأقسام',
  'nav.expand': 'توسيع القائمة',
  'nav.collapse': 'طي القائمة',
  'nav.primary': 'التنقل الرئيسي',

  'topbar.search': 'بحث',
  'topbar.searchPlaceholder': 'ابحث في جهات الاتصال والصفقات والشركات',
  'topbar.commandPalette': 'الأوامر',
  'topbar.notifications': 'الإشعارات',
  'topbar.help': 'المساعدة',
  'topbar.setup': 'الإعدادات',
  'topbar.account': 'حسابك',
  'topbar.workspaceSwitcher': 'تبديل مساحة العمل',

  'action.new': 'جديد',
  'action.import': 'استيراد',
  'action.save': 'حفظ',
  'action.saveAs': 'حفظ باسم',
  'action.cancel': 'إلغاء',
  'action.discard': 'تجاهل',
  'action.close': 'إغلاق',
  'action.add': 'إضافة',
  'action.remove': 'إزالة',
  'action.delete': 'حذف',
  'action.edit': 'تعديل',
  'action.retry': 'أعد المحاولة',
  'action.refresh': 'تحديث',
  'action.more': 'إجراءات أخرى',
  'action.back': 'رجوع',
  'action.next': 'التالي',
  'action.skip': 'تخطٍ',
  'action.done': 'تم',
  'action.undo': 'تراجع',
  'action.signIn': 'تسجيل الدخول',
  'action.signOut': 'تسجيل الخروج',
  'action.signUp': 'إنشاء مساحة عمل',
  'action.continue': 'متابعة',
  'action.complete': 'إتمام',
  'action.snooze': 'تأجيل',
  'action.reassign': 'إعادة التعيين',
  'action.follow': 'متابعة',
  'action.unfollow': 'إلغاء المتابعة',
  'action.loadSample': 'تحميل بيانات تجريبية',
  'action.clearFilters': 'مسح عوامل التصفية',
  'action.copyLink': 'نسخ الرابط',

  'state.loading': 'جارٍ التحميل',
  'state.empty': 'لا يوجد شيء بعد',
  'state.error': 'حدث خطأ ما',
  'state.saving': 'جارٍ الحفظ',
  'state.saved': 'تم الحفظ',
  'state.unsaved': 'تغييرات غير محفوظة',
  'state.filteredToNothing': 'لا توجد سجلات مطابقة لعوامل التصفية',
  'state.filteredToNothing.body': 'كل السجلات ما زالت موجودة، لكن عوامل التصفية تخفيها.',
  'state.permissionDenied': 'ليس لديك صلاحية للوصول إلى هذا',
  'state.nothingScheduled': 'لا يوجد إجراء مجدول',
  'state.notSet': 'غير محدد',
  'state.hidden': 'مخفي',

  'capability.planLocked.title': 'متاح في باقة أعلى',
  'capability.planLocked.body': 'هذه الميزة ضمن باقة {plan}. مساحة عملك على باقة {current}.',
  'capability.planLocked.action': 'اطّلع على ما تشمله باقة {plan}',
  'capability.notConfigured.title': 'غير متصل بعد',
  'capability.notConfigured.body': 'يجب توصيل {capability} قبل تشغيل هذه الميزة.',
  'capability.notConfigured.action': 'توصيل {capability}',
  'capability.notBuilt.title': 'غير متاح في هذه النسخة',
  'capability.notBuilt.body': '{capability} موصوفة في المواصفات لكنها غير منفّذة هنا بعد.',
  'capability.notBuilt.action': 'اقرأ الخطة',
  'capability.moduleOff.title': 'موقوفة في مساحة العمل هذه',
  'capability.moduleOff.body': '{capability} متاحة في باقتك لكنها موقوفة.',
  'capability.moduleOff.action': 'افتح الإعدادات ثم الوحدات',

  'auth.signup.title': 'أنشئ مساحة عملك',
  'auth.signup.subtitle': 'مساحة عمل واحدة تجمع فريقك كله.',
  'auth.signup.orgName': 'اسم مساحة العمل',
  'auth.signup.orgNameHint': 'اسم شركتك أو فريقك. يمكنك تغييره لاحقًا.',
  'auth.signup.email': 'البريد الإلكتروني للعمل',
  'auth.signup.password': 'كلمة المرور',
  'auth.signup.passwordHint': 'اثنا عشر حرفًا على الأقل.',
  'auth.signup.useMagicLink': 'أرسل لي رابط دخول بدلاً من ذلك',
  'auth.signup.usePassword': 'استخدم كلمة مرور بدلاً من ذلك',
  'auth.signup.submit': 'إنشاء مساحة العمل',
  'auth.signup.duplicateEmail': 'يوجد حساب بالفعل للبريد {email}.',
  'auth.signup.duplicateEmailAction': 'سجّل الدخول بدلاً من ذلك',
  'auth.signup.provisioning': 'جارٍ تجهيز مساحة عملك',
  'auth.signup.step.tenant': 'إنشاء مساحة العمل',
  'auth.signup.step.roles': 'إضافة الأدوار والصلاحيات',
  'auth.signup.step.schema': 'تجهيز الكائنات والحقول',
  'auth.signup.step.modules': 'تشغيل الوحدات',
  'auth.signup.step.ready': 'جاهزة',
  'auth.signup.slow': 'يستغرق هذا وقتًا أطول من المعتاد، لكنه ما زال قيد التنفيذ.',
  'auth.signin.title': 'تسجيل الدخول',
  'auth.signin.email': 'البريد الإلكتروني للعمل',
  'auth.signin.magicLink': 'أرسل لي رابط دخول',
  'auth.signin.usePassword': 'استخدم كلمة مرور بدلاً من ذلك',
  'auth.signin.useMagicLink': 'أرسل لي رابطًا بدلاً من ذلك',
  'auth.signin.sso': 'المتابعة بتسجيل دخول موحّد',
  'auth.signin.sso.body': 'تسجّل مؤسستك الدخول عبر موفّر الهوية الخاص بها.',
  'auth.signin.submit': 'تسجيل الدخول',
  'auth.signin.failed': 'البريد الإلكتروني وكلمة المرور غير متطابقين.',
  'auth.signin.linkSent': 'تحقق من {email} لرابط الدخول. تنتهي صلاحيته خلال خمس عشرة دقيقة.',
  'auth.signin.deactivated': 'هذا الحساب موقوف. اطلب من المسؤول إعادة تفعيله.',
  'auth.mfa.title': 'أدخل رمز التحقق',
  'auth.mfa.subtitle': 'افتح تطبيق المصادقة وأدخل الرمز المكوّن من ستة أرقام.',
  'auth.mfa.code': 'رمز التحقق',
  'auth.mfa.useRecovery': 'استخدم رمز استرداد بدلاً من ذلك',
  'auth.mfa.recovery': 'رمز الاسترداد',
  'auth.mfa.failed': 'الرمز غير صحيح. تتغيّر الرموز كل ثلاثين ثانية.',
  'auth.mfa.submit': 'تحقق',

  'onboarding.title': 'خمسة أسئلة، ثم نظام جاهز للعمل',
  'onboarding.progress': 'السؤال {current} من {total}',
  'onboarding.q.industry': 'ما طبيعة عمل شركتك؟',
  'onboarding.q.teamSize': 'كم عدد الأشخاص الذين سيستخدمون النظام؟',
  'onboarding.q.sells': 'ماذا تبيع؟',
  'onboarding.q.cycle': 'كم تستغرق عملية البيع عادةً؟',
  'onboarding.q.channels': 'كيف تصل إلى عملائك؟',
  'onboarding.applying': 'جارٍ تجهيز مساحة عملك',
  'onboarding.applying.modules': 'تشغيل الوحدات التي تحتاجها',
  'onboarding.applying.pipeline': 'إنشاء مراحل خط المبيعات',
  'onboarding.applying.fields': 'إضافة الحقول الخاصة بمجالك',
  'onboarding.applying.automations': 'تثبيت الأتمتة الأولية',
  'onboarding.applying.sample': 'إضافة سجلات تجريبية يمكنك حذفها',
  'onboarding.failed': 'لم تكتمل هذه الخطوة. إجاباتك محفوظة.',

  'work.title': 'مهامي',
  'work.greeting.morning': 'صباح الخير، {name}',
  'work.greeting.afternoon': 'طاب يومك، {name}',
  'work.greeting.evening': 'مساء الخير، {name}',
  'work.section.overdue': 'متأخرة',
  'work.section.today': 'اليوم',
  'work.section.thisWeek': 'هذا الأسبوع',
  'work.section.later': 'لاحقًا',
  'work.empty.title': 'لا يوجد إجراء مجدول',
  'work.empty.body': 'افتح جهة اتصال أو صفقة وحدّد خطوتك التالية من هناك.',
  'work.empty.action': 'اذهب إلى جهات الاتصال',
  'work.complete.title': 'كيف سارت الأمور؟',
  'work.complete.outcome': 'النتيجة',
  'work.complete.note': 'ملاحظة',
  'work.complete.nextStep': 'وما الخطوة التالية؟',
  'work.complete.nextStepType': 'الخطوة التالية',
  'work.complete.nextStepDue': 'متى',
  'work.complete.submit': 'إتمام وجدولة التالي',
  'work.complete.noNext': 'لا شيء تاليًا في الوقت الحالي',
  'work.complete.noNextWarning': 'لن تبقى لهذا السجل خطوة تالية.',

  'work.section.overdueLabel': 'متأخرة',
  'work.snooze': 'النقل إلى الغد',
  'work.reassign': 'تمرير إلى شخص آخر',
  'work.complete.chooseNothing': 'لا شيء تاليًا في الوقت الحالي',
  'work.complete.keepScheduling': 'جدولة الخطوة التالية بدلاً من ذلك',
  'work.complete.nothingWarning': 'لن تبقى لهذا السجل خطوة تالية، ولن يظهر في مهام أي شخص حتى تُجدول واحدة.',
  'work.complete.nothingReason': 'لماذا لا يوجد شيء تالٍ؟',
  'work.complete.submitNothing': 'إتمام دون جدولة',
  'work.outcome.reached': 'تم التواصل',
  'work.outcome.noAnswer': 'لا يوجد رد',
  'work.outcome.rescheduled': 'طلبوا إعادة الجدولة',
  'work.outcome.notInterested': 'غير مهتم',
  'work.outcome.done': 'تم',
  'work.unscheduled': '{count} من جهات الاتصال بلا إجراء مجدول',
  'work.unscheduled.action': 'عرضها',
  'list.status.records': '{count} سجلات',
  'list.status.sortedBy': 'مرتّبة حسب {field}',
  'list.status.filteredBy': 'مصفّاة حسب {filters}',
  'list.status.updated': 'حُدّثت {when}',
  'list.selected': '{count} محدد',
  'list.selectAll': 'تحديد الكل ({count})',
  'list.clearSelection': 'إلغاء التحديد',
  'list.companies.title': 'الشركات',
  'list.companies.empty.title': 'لا توجد شركات بعد',
  'list.companies.empty.body': 'الشركة هي المؤسسة التي يعمل بها جهة الاتصال، وما تُبرم الصفقة معه.',
  'list.companies.contacts': 'الأشخاص',
  'list.companies.industry': 'القطاع',
  'list.companies.owner': 'المالك',
  'list.journeys.title': 'الرحلات',
  'list.journeys.empty.title': 'لا توجد رحلات بعد',
  'list.journeys.empty.body': 'الرحلة تأخذ شخصاً عبر سلسلة من الخطوات على مدى أيام أو أسابيع.',
  'list.automations.title': 'الأتمتة',
  'list.automations.empty.title': 'لا توجد أتمتة بعد',
  'list.automations.empty.body': 'الأتمتة تتفاعل مع حدث وتقوم بالخطوة التالية دون طلب.',
  'list.reports.title': 'التقارير',
  'list.reports.empty.title': 'لا توجد تقارير محفوظة بعد',
  'list.reports.empty.body': 'التقرير يجمّع السجلات ويلخّصها، ويمكن وضعه على لوحة معلومات.',
  'list.agents.title': 'الوكلاء',
  'list.agents.empty.title': 'لا يوجد وكلاء بعد',
  'list.agents.empty.body': 'الوكيل يقوم بالعمل نيابة عنك، ضمن حدود تحددها.',
  'product.editor.new': 'منتج جديد',
  'product.editor.edit': 'تغيير المنتج',
  'product.editor.sku': 'الرمز',
  'product.editor.skuHelp': 'ما يشير إليه بند عرض السعر. فريد في مساحة العمل هذه.',
  'product.editor.name': 'الاسم',
  'product.editor.family': 'العائلة',
  'product.editor.description': 'الوصف',
  'product.editor.price': 'سعر القائمة',
  'product.editor.priceHelp': 'ما تعدّله قاعدة قائمة الأسعار. الخصم مكانه القاعدة لا هنا.',
  'product.editor.unit': 'يُباع بـ',
  'product.editor.currency': 'العملة',
  'product.editor.save': 'حفظ المنتج',
  'product.editor.cancel': 'إلغاء',
  'product.editor.archive': 'إيقاف بيع هذا',
  'product.editor.archived': 'لم يعد يُباع',
  'product.editor.archiveHelp': 'يبقى على عروض الأسعار والطلبات التي تذكره بالفعل.',
  'product.editor.restore': 'بيع هذا مجدداً',
  'list.products.title': 'المنتجات',
  'list.products.empty.title': 'لا توجد منتجات بعد',
  'list.products.empty.body': 'المنتج هو ما تبيعه، بسعر قائمة يبدأ منه عرض السعر.',
  'list.pricelists.title': 'قوائم الأسعار',
  'list.pricelists.empty.title': 'لا توجد قوائم أسعار بعد',
  'list.pricelists.empty.body': 'قائمة الأسعار تحدد ما يدفعه عميل معيّن، بقواعد لا يدوياً.',
  'list.deals.title': 'الصفقات',
  'list.deals.empty.title': 'لا توجد صفقات بعد',
  'list.deals.empty.body': 'الصفقة هي عملية بيع تعمل عليها، بمبلغ وتاريخ إغلاق.',
  'list.deals.board': 'عرض كلوحة',
  'list.deals.value': 'قيمة هذه الصفقات',
  'list.cases.title': 'الحالات',
  'list.cases.empty.title': 'لا توجد حالات بعد',
  'list.cases.empty.body': 'الحالة هي مشكلة عميل، تُعالج حتى الحل وفق مهلة متفق عليها.',
  'list.cases.showAsList': 'عرض كقائمة',
  'list.cases.queue': 'عرض كقائمة انتظار',
  'list.campaigns.title': 'الحملات',
  'list.campaigns.empty.title': 'لا توجد حملات بعد',
  'list.campaigns.empty.body': 'الحملة هي إرسال واحد لجمهور واحد، مع ما حدث بعده.',
  'list.campaigns.showAsList': 'عرض كقائمة',
  'list.campaigns.cards': 'عرض كبطاقات',
  'list.column.amount': 'المبلغ',
  'list.column.closeDate': 'يُغلق',
  'list.column.company': 'الشركة',
  'list.column.subject': 'الموضوع',
  'list.column.priority': 'الأولوية',
  'list.column.channel': 'القناة',
  'list.column.sent': 'أُرسلت',
  'list.column.audience': 'الجمهور',
  'list.column.name': 'الاسم',
  'list.column.status': 'الحالة',
  'list.column.updated': 'آخر تحديث',
  'list.column.visibility': 'من يمكنه الاطلاع',
  'list.column.about': 'عن',
  'list.column.purpose': 'الغرض',
  'list.column.sku': 'الرمز',
  'list.column.price': 'سعر القائمة',
  'list.column.currency': 'العملة',
  'list.column.rules': 'القواعد',
  'list.column.validity': 'صالح',
  'list.expired': 'منتهي',
  'list.active': 'نشط',
  'list.searchThisList': 'ابحث في هذه القائمة',
  'list.settings': 'إعدادات القائمة',
  'list.density': 'ارتفاع الصف',
  'list.density.comfortable': 'مريح',
  'list.density.compact': 'مضغوط',
  'list.columns': 'الأعمدة',
  'list.inlineEdit': 'التحرير المباشر',
  'list.chartView': 'رسم بياني',
  'list.tableView': 'جدول',
  'list.filters': 'عوامل التصفية',
  'list.view': 'العرض',
  'list.view.pin': 'تثبيت هذا العرض',
  'list.view.unpin': 'إلغاء تثبيت العرض',
  'list.view.unsaved': 'هذا العرض يحتوي تغييرات غير محفوظة',
  'list.moreActions': 'إجراءات أخرى',
  'list.removeFilter': 'إزالة عامل التصفية {filter}',
  'list.pinNamed': 'تثبيت {name}',
  'list.unpinNamed': 'إلغاء تثبيت {name}',
  'list.bulkToolbar': 'إجراءات على الصفوف المحددة',
  'list.sortBy': 'ترتيب حسب {column}',
  'list.selectRow': 'تحديد الصف {row}',
  'list.rowsShown': 'يُعرض {count} صفًا',
  'list.page.next': 'الصفحة التالية',
  'list.page.previous': 'الصفحة السابقة',
  'list.page.showing': 'عرض {from} إلى {to}',

  'error.network': 'تعذّر الوصول إلى الخادم. تحقق من اتصالك وأعد المحاولة.',
  'error.unexpected': 'حدث خطأ لدينا. لم يتم تغيير أي شيء.',
  'error.notFound': 'لم يُعثر على هذا السجل. ربما تم حذفه.',
  'error.forbidden': 'ليس لديك صلاحية للقيام بذلك.',
  'error.validation': 'بعض التفاصيل تحتاج تصحيحًا قبل الحفظ.',
  'error.conflict': 'قام شخص آخر بتعديل هذا أثناء تحريرك له.',
  'error.traceId': 'المرجع {traceId}',

  'account.profile': 'ملفك الشخصي',
  'account.preferences': 'التفضيلات',
  'account.theme': 'المظهر',
  'account.theme.light': 'فاتح',
  'account.theme.dark': 'داكن',
  'account.theme.system': 'حسب النظام',
  'account.language': 'اللغة',
  'account.language.en': 'English',
  'account.language.ar': 'العربية',
  'setup.category.pricing': 'التسعير',
  'setup.pricing.body': 'قوائم الأسعار والقواعد التي تحدد السعر.',
  'pricelist.ruleCount': '{count} قاعدة',
  'pricelist.empty': 'لا توجد قواعد بعد',
  'pricelist.expired': 'لا شيء ساري',
  'pricelist.inactive': 'موقوف',
  'pricelist.none': 'لا توجد قوائم أسعار بعد. يُستخدم سعر القائمة لكل منتج حتى تتوفر واحدة.',
  'agent.budget': 'الميزانية',
  'agent.budget.workspace': 'مساحة العمل',
  'agent.budget.ofLimit': '{spent} من {limit}',
  'agent.budget.ok': 'ضمن الميزانية',
  'agent.budget.warning': 'يقترب من الحد',
  'agent.budget.exceeded': 'تجاوز الحد',
  'agent.budget.stops': 'تتوقف الوكلاء عند بلوغ هذا الحد',
  'agent.budget.reports': 'للإبلاغ فقط؛ تستمر الوكلاء في العمل',
  'agent.budget.none': 'لم يتم تعيين حد للإنفاق.',
  'agent.spend.byAgent': 'حسب الوكيل',
  'agent.spend.byUser': 'حسب الشخص',
  'agent.spend.who': 'أين ذهب',
  'agent.spend.runs': 'التشغيلات',
  'agent.spend.unnamed': 'محذوف',
  'report.countedOf': '{counted} من {count} سجل',
  'report.inWorkspaceCurrency': '{counted} من {count} سجل، بعملة مساحة العمل',
  'chart.ofPrevious': '{change} من {step}',
  'chart.rose': 'أعلى من {step}',
  'variants.title': 'المتغيرات',
  'variants.attributes': 'السمات',
  'variants.attribute.name': 'السمة',
  'variants.attribute.values': 'القيم، مفصولة بفواصل',
  'variants.attribute.add': 'إضافة سمة',
  'variants.attribute.remove': 'إزالة {name}',
  'variants.save': 'حفظ السمات',
  'variants.generate': 'إنشاء المتغيرات',
  'variants.generate.count': '{count} تركيبة',
  'variants.generate.existing': '{count} موجودة بالفعل وستُترك كما هي',
  'variants.generate.warn': 'سيؤدي ذلك إلى إنشاء {count} متغير، وهو عدد كبير. هل تريد المتابعة؟',
  'variants.generated': 'تم إنشاء {created}، وتُركت {skipped}',
  'variants.table': 'المتغيرات المُنشأة',
  'variants.sku': 'رمز المنتج',
  'variants.adjustment': 'تعديل السعر',
  'variants.price': 'السعر',
  'variants.active': 'متاح',
  'variants.inactive': 'غير متاح',
  'variants.empty.title': 'لا توجد سمات بعد',
  'variants.empty.body': 'السمة هي ما يتغير به المنتج، مثل اللون أو المقاس. أضف سمة وقيمها، وستصبح كل تركيبة متغيرًا له رمز وسعر خاص.',
  'variants.grid': 'الطلب عبر الشبكة',
  'variants.grid.needsTwo': 'تحتاج الشبكة إلى سمتين بالضبط: واحدة أفقيًا وأخرى رأسيًا.',
  'variants.grid.total': '{count} وحدة عبر {variants} متغير',
  'variants.grid.missing': 'لا يوجد متغير',
  'promotions.title': 'العروض والولاء',
  'promotions.coupons': 'الكوبونات',
  'promotions.automatic': 'تلقائية',
  'promotions.all': 'الكل',
  'promotions.code': 'الرمز',
  'promotions.noCode': 'يُطبق دون رمز',
  'promotions.used': 'استُخدم {count} مرة',
  'promotions.usedOf': 'استُخدم {count} من {max}',
  'promotions.unlimited': 'بلا حد',
  'promotions.until': 'حتى {date}',
  'promotions.state.inactive': 'موقوف',
  'promotions.state.expired': 'منتهي',
  'promotions.state.not-started': 'لم يبدأ بعد',
  'promotions.state.exhausted': 'استُنفد',
  'promotions.state.live': 'فعّال',
  'promotions.conditions': 'يُطبق عندما',
  'promotions.log': 'الاستخدامات الأخيرة',
  'promotions.log.empty': 'لم تُسجل أي استخدامات بعد.',
  'promotions.log.gap': 'العدد أقدم من السجل المحفوظ هنا.',
  'promotions.select': 'اختر عرضًا لمعرفة كيفية عمله وأين استُخدم.',
  'promotions.empty.title': 'لا توجد عروض بعد',
  'promotions.empty.body': 'الكوبون هو عرض له رمز يكتبه الشخص؛ والعرض التلقائي يُطبق عندما تتحقق شروطه. كلاهما يستخدم الشروط نفسها.',
  'quote.lapsed': 'منتهي الصلاحية',
  'quote.lapsed.body': 'كان هذا العرض ساريًا حتى {date}. غيّر التاريخ قبل إرساله.',
  'guardrail.no-discount': 'لا تقدّم خصمًا أبدًا',
  'guardrail.max-spend': 'حد الإنفاق لكل محادثة',
  'guardrail.mask-pii': 'احذف بيانات الاتصال من الإجابات',
  'guardrail.require-citation': 'لا تجب إلا من سجلات قرأها',
  'guardrail.refuse-topic': 'رفض موضوع',
  'guardrail.escalate-on': 'التحويل إلى شخص عندما',
  'guardrail.enforced': 'يتحقق منه المحرك',
  'guardrail.instructed': 'يُطلب من النموذج',
  'guardrail.enforced.help': 'يرفض المحرك عند مخالفة هذا.',
  'guardrail.instructed.help': 'مذكور في التعليمات. لا شيء هنا يمكنه التحقق منه.',
  'setup.category.people': 'الأشخاص',
  'setup.people.body': 'من هنا، ومن يتبع من، وفي أي فرق هم.',
  'people.roles': 'التسلسل الإداري',
  'people.teams': 'الفرق',
  'people.members': '{count} أشخاص',
  'people.noMembers': 'لا أحد بعد',
  'people.role.none': 'بلا دور',
  'people.role.change': 'دور {name}',
  'people.admin': 'مسؤول',
  'people.seesBelow': 'يرى كل ما يملكه من هم أدناه',
  'people.teams.independent': 'الفريق ليس جزءًا من التسلسل الإداري. يمكن لأي شخص أن يكون فيه.',
  'people.reportsTo': '{role} يتبع إلى',
  'people.reportsTo.none': 'لا أحد (أعلى الخط)',
  'people.inspect': 'ماذا يستطيع {name} أن يرى؟',
  'people.reach.title': 'ما يستطيع {name} رؤيته',
  'people.reach.own': 'يملك',
  'people.reach.visible': 'يستطيع رؤية',
  'people.reach.hierarchy': 'منها، بسبب من يتبعونه فقط',
  'people.reach.nobody': 'لا أحد يتبع {name}، لذا لا يضيف خط التبعية شيئًا.',
  'people.reach.everything': '{name} مسؤول عن مساحة العمل ويرى كل السجلات أصلًا، لذا لا يضيف خط التبعية شيئًا.',
  'people.reach.reports': 'يتبعون {name}',
  'people.reach.close': 'إغلاق',
  'fls.title': 'الحقول',
  'fls.open': 'قواعد الحقول',
  'fls.close': 'تم',
  'fls.affected': 'يغيّر ما يراه {count} أشخاص',
  'fls.affected.none': 'لا أحد يحمل هذه المجموعة بعد',
  'fls.access.edit': 'تحرير',
  'fls.access.read': 'قراءة فقط',
  'fls.access.hidden': 'مخفي',
  'fls.object': 'الكائن',
  'fls.field': 'الحقل',
  'fls.empty': 'كل الحقول قابلة للتحرير لهذه المجموعة. اختر حقلاً لتقييده.',
  'sharing.title': 'المشاركة',
  'sharing.body': 'من يمكنه رؤية كل نوع من السجلات. المالك ومن يرفع إليهم دائمًا يستطيعون.',
  'sharing.mode.private': 'المالك فقط',
  'sharing.mode.private.help': 'من يملك السجل ومن يرفع إليهم.',
  'sharing.mode.team': 'فريقه',
  'sharing.mode.team.help': 'كل من في الفريق الذي ينتمي إليه السجل.',
  'sharing.mode.everyone': 'الجميع',
  'sharing.mode.everyone.help': 'كل شخص في مساحة العمل هذه.',
  'sharing.mode.criteria': 'السجلات المطابقة لقاعدة',
  'sharing.mode.criteria.help': 'سجلات مختارة، تُشارك مع فريق واحد أو دور واحد.',
  'sharing.access': 'إلى أي مدى',
  'sharing.access.read': 'قراءة',
  'sharing.access.read-write': 'قراءة وتحرير',
  'sharing.criteria.team': 'المشاركة مع فريق',
  'sharing.criteria.role': 'المشاركة مع دور',
  'sharing.noOwner': 'لا مالك لهذا، لذا لا يمكن أن يكون للمالك فقط.',
  'customObject.title': 'كائناتك',
  'customObject.body': 'أشياء تتابعها مساحة العمل هذه ولا تأتي مع المنتج.',
  'customObject.new': 'كائن جديد',
  'customObject.step1': 'ما اسمه',
  'customObject.step2': 'اسمه البرمجي',
  'customObject.step3': 'من يمكنه رؤيته',
  'customObject.label': 'الواحد منه',
  'customObject.plural': 'الجمع منه',
  'customObject.key': 'الاسم البرمجي',
  'customObject.key.help': 'يُستخدم في الروابط وواجهة البرمجة. لا يمكن تغييره لاحقًا.',
  'customObject.icon': 'الأيقونة',
  'customObject.create': 'إنشاء كائن',
  'customObject.records': '{count} سجلات',
  'customObject.none': 'لا سجلات بعد',
  'customObject.empty': 'لا شيء بعد. الكائن نوع من السجلات، مثل شحنة أو عقد.',
  'formula.title': 'حقول الصيغ والتجميع',
  'formula.body': 'الصيغة تحسب قيمتها عند القراءة. التجميع يعدّ أو يجمع السجلات المرتبطة.',
  'formula.new': 'حقل جديد',
  'formula.name': 'اسم الحقل',
  'formula.label': 'التسمية',
  'formula.kind': 'النوع',
  'formula.kind.formula': 'صيغة',
  'formula.kind.rollup': 'تجميع',
  'formula.expression': 'الصيغة',
  'formula.functions': 'الدوال',
  'formula.fields': 'الحقول',
  'formula.preview': 'على أحدث سجل لديك',
  'formula.preview.none': 'لا توجد سجلات لتجربتها عليها بعد.',
  'formula.save': 'إضافة حقل',
  'formula.rollup.object': 'عدّ السجلات من',
  'formula.rollup.key': 'المرتبطة عبر',
  'formula.rollup.fn': 'ما يُحتسب',
  'formula.rollup.field': 'لأي حقل',
  'formula.empty': 'لا توجد حقول صيغ أو تجميع بعد.',
  'error.brand.tooPale': 'اللون {colour} فاتح جدًا ولا يُرى على الصفحة. أقرب درجة منه تعمل هي {suggestion}.',
  'dup.title': 'قد يكون موجودًا بالفعل',
  'dup.reason.email': 'البريد الإلكتروني نفسه',
  'dup.reason.phone': 'رقم الهاتف نفسه',
  'dup.reason.name-and-domain': 'اسم مشابه في الشركة نفسها',
  'dup.reason.name': 'اسم مشابه',
  'dup.confidence.certain': 'شبه مؤكد أنه نفسه',
  'dup.confidence.likely': 'على الأرجح نفسه',
  'dup.confidence.possible': 'قد يكون نفسه',
  'dup.view': 'افتحه',
  'dup.useInstead': 'استخدم هذا بدلًا منه',
  'dup.saveAnyway': 'احفظ على أي حال',
  'dup.notDuplicate': 'ليس تكرارًا',
  'dup.queue': 'تكرارات محتملة',
  'dup.queue.body': 'أزواج تبدو الشخص أو الشركة نفسها. لم يُغيَّر شيء هنا.',
  'dup.queue.none': 'لا يبدو أن هناك تكرارًا.',
  'dup.merge': 'دمج',
  'dup.merge.preview': 'ما سيحتويه السجل بعد الدمج',
  'dup.merge.keepWinner': 'أبقِ',
  'dup.merge.takeLoser': 'خذ',
  'dup.merge.confirm': 'ادمج الاثنين',
  'dup.merge.moving': 'ينتقل {count} صفًا مرتبطًا إلى السجل الباقي',
  'dup.merge.redundant': 'يوجد {count} صفًا متطابقًا في الاثنين وسيُحذف',
  'dup.merge.done': 'تم الدمج. السجل الآخر في سلة المحذوفات.',
  'contact.new': 'جهة اتصال جديدة',
  'contact.new.first': 'الاسم الأول',
  'contact.new.last': 'اسم العائلة',
  'contact.new.email': 'البريد الإلكتروني',
  'contact.new.phone': 'الهاتف',
  'contact.new.save': 'إنشاء جهة الاتصال',
  'audit.title': 'سجل التدقيق',
  'audit.body': 'من غيّر ماذا ومتى. لا يمكن تعديل هذا السجل أو حذفه.',
  'audit.history': 'السجل',
  'audit.history.none': 'لم تُسجَّل تغييرات بعد.',
  'audit.none': 'لا شيء مسجل بعد.',
  'audit.actor.user': 'شخص',
  'audit.actor.agent': 'وكيل',
  'audit.actor.system': 'النظام',
  'audit.filter.actorType': 'غيّره',
  'audit.filter.object': 'الكائن',
  'audit.filter.since': 'من',
  'audit.filter.all': 'أي شخص',
  'audit.changed': '{field}: {before} ← {after}',
  'audit.wasEmpty': 'فارغ',
  'search.recents': 'فُتحت مؤخرًا',
  'search.results': 'النتائج',
  'search.none': 'لا شيء يطابق {term}.',
  'search.lookedIn': 'تم البحث في {count} من أنواع السجلات: {objects}.',
  'search.seeAll': 'عرض كل النتائج',
  'search.hint': 'اكتب للبحث، أو اختر شيئًا كان مفتوحًا لديك.',
  'search.title': 'نتائج البحث',
  'view.all': 'كل السجلات',
  'view.switch': 'العرض',
  'view.save': 'حفظ',
  'view.saveAs': 'حفظ كعرض جديد',
  'view.discard': 'تجاهل التغييرات',
  'view.unsaved': 'لهذا العرض تغييرات غير محفوظة',
  'view.name': 'اسم العرض',
  'view.visibility': 'من يمكنه رؤيته',
  'view.visibility.private': 'أنا والمسؤولون فقط',
  'view.visibility.team': 'فريقي',
  'view.visibility.everyone': 'الجميع',
  'view.duplicate': 'نسخ',
  'view.delete': 'حذف العرض',
  'view.pin': 'تثبيت',
  'view.unpin': 'إلغاء التثبيت',
  'view.readOnly': 'هذا العرض يخص شخصًا آخر. انسخه لإجراء تغييرات.',
  'view.created': 'تم حفظ العرض.',
  'view.columns': 'الأعمدة',
  'view.columns.chosen': '{shown} من {total} أعمدة',
  'view.groupBy': 'تجميع حسب',
  'view.groupBy.none': 'بدون تجميع',
  'view.group.empty': 'بلا قيمة',
  'notify.title': 'الإشعارات',
  'notify.none': 'لا جديد.',
  'notify.today': 'اليوم',
  'notify.earlier': 'سابقًا',
  'notify.markAll': 'تعليم الكل كمقروء',
  'notify.mute': 'أوقف إشعاري بهذا',
  'notify.muted': 'تم الكتم. لن تصلك إشعارات عن هذا السجل.',
  'notify.unread': '{count} غير مقروء',
  'notify.preferences': 'تفضيلات الإشعارات',
  'notify.preferences.body': 'أوقف ما لا ترغب في سماعه. كل ما هنا يظهر على الجرس.',
  'notify.channel.in-app': 'على الجرس',
  'notify.onlyInApp': 'لا تُرسل إشعارات البريد أو الدفع بعد، لذا لا تُعرض هنا.',
  'env.title': 'البيئات',
  'env.body': 'انقل الإعدادات بين بيئة التجربة والإنتاج. الكائنات والحقول والتخطيطات ومجموعات الأذونات والمشاركة — لا السجلات.',
  'env.export': 'تصدير مساحة العمل',
  'env.paste': 'ألصق حزمة',
  'env.review': 'مراجعة التغييرات',
  'env.new': 'جديد',
  'env.changed': 'تغيّر',
  'env.identical': 'مطابق بالفعل',
  'env.onlyHere': 'موجود هنا فقط ({count}). لا يُحذف شيء منها.',
  'env.apply': 'تطبيق {count} محددًا',
  'env.applied': 'طُبِّق {count}. تُخطّي {skipped}.',
  'env.nothingSelected': 'لم يُحدد شيء. أشّر على العناصر المراد نقلها.',
  'env.selectAllChanged': 'تحديد كل جديد أو متغيّر',
  'plan.target': 'الانتقال إلى خطة',
  'plan.change': 'تغيير الخطة',
  'plan.moveTo': 'الانتقال إلى {plan}',
  'plan.review': 'اطّلع على ما سيتغيّر',
  'plan.upgrade.gains': 'ستحصل على {count} ميزة.',
  'plan.downgrade.warns': 'ستفقد الوصول إلى {count} ميزة.',
  'plan.downgrade.records': 'سيتعذّر الوصول إلى {count} سجلًا',
  'plan.downgrade.noRecords': 'لا سجلات خلف هذه الميزة',
  'plan.downgrade.kept': 'لا يُحذف شيء. تبقى السجلات، ويتوقف الوصول إليها حتى ترقّي الخطة مجددًا.',
  'plan.confirm': 'تأكيد التغيير',
  'plan.same': 'هذه هي خطتك الحالية.',
  'plan.changed': 'أنت الآن على {plan}.',
  'usage.title': 'الاستخدام',
  'usage.of': '{used} من {limit}',
  'usage.unlimited': '{used} · بلا حد في هذه الخطة',
  'usage.period': 'هذا الشهر',
  'usage.warning': 'أنت قريب من أحد الحدود',
  'usage.critical': 'أوشكت على بلوغ أحد الحدود',
  'usage.exceeded': 'تم بلوغ أحد الحدود',
  'usage.seeePlans': 'عرض الخطط',
  'empty.action.openAgents': 'افتح الوكلاء',
  'empty.action.addWidget': 'أضف عنصرًا',
  'empty.action.newPromotion': 'أنشئ عرضًا ترويجيًا',
  'empty.action.addVariant': 'أضف نسخة',
  'empty.action.chatSettings': 'افتح إعدادات الدردشة',
  'empty.action.addContact': 'أضف جهة اتصال',
  'empty.action.importContacts': 'استورد من جدول بيانات',
  'empty.action.newDeal': 'أنشئ صفقة',
  'empty.action.addLine': 'أضف بندًا',
  'empty.action.addStep': 'أضف الخطوة الأولى',
  'empty.action.runIt': 'شغّلها',
  'empty.action.addRule': 'أضف قاعدة',
  'empty.action.newCampaign': 'أنشئ حملة',
  'empty.action.addLead': 'أضف عميلًا محتملًا',
  'empty.action.openDeals': 'افتح الصفقات',
  'empty.action.newCase': 'سجّل تذكرة',
  'empty.action.chooseType': 'اختر نوع الحقل',
  'empty.action.chooseFile': 'اختر ملفًا',
  'empty.action.addSection': 'أضف قسمًا',
  'empty.action.openModules': 'افتح الوحدات',
  'empty.action.seePlans': 'اطّلع على الباقات',
  'empty.action.clearSearch': 'امسح البحث',
  'empty.action.search': 'ابحث في كل شيء',
  'empty.action.openSetup': 'افتح الإعداد',
  'bulk.title': 'المهام المجمّعة',
  'bulk.body': 'عمل بحجم كبير، خارج المسار التفاعلي. يُحسب التقدّم من السجلات، لا يُخمَّن من الساعة.',
  'bulk.empty': 'لا توجد مهام مجمّعة. أرسل واحدة عبر الواجهة البرمجية عندما تكون السجلات أكثر مما تحتمله الشاشة.',
  'bulk.rowLimit': 'حتى {limit} سجل في المهمة الواحدة على باقتك، محسوبة بمعزل عن حد الواجهة في الدقيقة.',
  'bulk.noLimit': 'لا حد للسجلات على باقتك. محسوبة بمعزل عن حد الواجهة في الدقيقة.',
  'bulk.progress': '{done} من {total} سجل',
  'bulk.failed': '{count} فشلت',
  'bulk.stalled': 'لم يلمس أحد هذه المهمة مؤخرًا.',
  'bulk.resume': 'استأنف',
  'bulk.cancel': 'أوقف',
  'bulk.errors': 'ملف الأخطاء',
  'bulk.status.queued': 'في الانتظار',
  'bulk.status.running': 'قيد التشغيل',
  'bulk.status.completed': 'انتهت',
  'bulk.status.failed': 'فشلت',
  'bulk.status.cancelled': 'أُوقفت',
  'mcp.title': 'وصول الوكلاء (MCP)',
  'mcp.body': 'ما يصل إليه وكيل ذكاء اصطناعي خارجي برمز ما. هذه رؤية الوكيل نفسه، مرسومة بصلاحيات الشخص الذي يعمل الرمز نيابةً عنه — لا بصلاحياتك.',
  'mcp.connection': 'الاتصال',
  'mcp.choose': 'اعرض ما يكشفه هذا الرمز',
  'mcp.actsAs': 'يعمل نيابةً عن {name}',
  'mcp.resources': 'الموارد التي يمكنه قراءتها',
  'mcp.tools': 'الأدوات التي يمكنه استدعاؤها',
  'mcp.withheld': 'ما حُجب، ولماذا',
  'mcp.readOnly': 'للقراءة فقط',
  'mcp.nothing': 'هذا الرمز لا يكشف شيئًا على الإطلاق.',
  'mcp.pick': 'اختر رمزًا',
  'sdk.title': 'مكتبات العميل',
  'sdk.body': 'كلتاهما مُولَّدتان من العقد أعلاه عند كل تغيير في الواجهة، فلا يمكن لأيٍّ منهما أن تنحرف عنه.',
  'sdk.js': 'جافاسكربت / تايبسكربت',
  'sdk.py': 'بايثون',
  'sdk.notPublished': 'مُولَّدة ومحفوظة في هذا المستودع. النشر إلى npm وPyPI خطوة إصدار غير مُعدّة بعد.',
  'embed.title': 'التضمينات',
  'embed.body': 'اعرض عنصرًا داخل موقع آخر. يُصدر رمز عند كل تحميل صفحة، محصور بعنصر واحد، ولنطاق تُدرجه هنا فقط.',
  'embed.name': 'ما الغرض من هذا التضمين؟',
  'embed.widget': 'أي عنصر؟',
  'embed.object': 'أي كائن؟',
  'embed.origins': 'أي المواقع يُسمح لها بعرضه؟',
  'embed.originsHelp': 'واحد في كل سطر، مثل https://partner.example. التضمين بلا نطاقات لا يسمح لأحد.',
  'embed.create': 'أنشئ التضمين',
  'embed.empty': 'لا توجد تضمينات. أنشئ واحدًا عندما يحتاج موقع آخر إلى عرض شيء من هنا.',
  'embed.revoke': 'ألغِ',
  'embed.revoked': 'ملغى',
  'embed.snippet': 'الصق هذا في صفحة المضيف',
  'embed.preview': 'معاينة',
  'embed.previewEmpty': 'لا شيء لعرضه بعد — لا توجد سجلات لهذا العنصر في مساحة العمل.',
  'embed.previewFailed': 'تعذّر تحميل المعاينة. ما يظهر خطأً هنا هو ما سيراه الموقع المضيف.',
  'embed.tokenLife': 'تعيش الرموز {seconds} ثانية.',
  'embed.noOrigins': 'لا توجد نطاقات بعد، فلا يمكن لأحد عرض هذا.',
  'apps.title': 'التطبيقات',
  'apps.body': 'شيفرة خارجية تعمل هنا. كل واحدة تعمل في عزلة خاصة بها بحد زمني وحد للذاكرة، ولا تستطيع إلا ما تمنحه لها.',
  'apps.paste': 'ألصق بيان الإضافة',
  'apps.review': 'راجع ما تطلبه',
  'apps.asking': '{name} من {publisher} تطلب أن:',
  'apps.unknown': 'تطلب أيضًا {scopes}، وهي غير متاحة في هذا المنتج. لن تحصل عليها.',
  'apps.nothingAsked': 'لا تطلب شيئًا على الإطلاق.',
  'apps.install': 'ثبّت بما حدّدته',
  'apps.cancel': 'لا تثبّت',
  'apps.installedOff': 'تم التثبيت وهي متوقفة. شغّلها عندما تكون مستعدًا.',
  'apps.empty': 'لا توجد تطبيقات مثبّتة. ألصق بيانًا أعلاه لترى ما ستطلبه.',
  'apps.enable': 'شغّل',
  'apps.disable': 'أوقف',
  'apps.refused': 'رفضت: {scopes}',
  'apps.granted': 'ممنوح: {scopes}',
  'apps.noneGranted': 'لم تُمنح أي صلاحيات',
  'apps.limits': '{ms} مللي ثانية، {mb} ميجابايت',
  'apps.runs': 'عمليات التشغيل الأخيرة',
  'apps.noRuns': 'لم تعمل بعد.',
  'apps.test': 'شغّلها مرة',
  'apps.outcome.ok': 'نجحت',
  'apps.outcome.failed': 'فشلت',
  'apps.outcome.timed-out': 'استغرقت وقتًا طويلًا',
  'apps.outcome.refused': 'متوقفة',
  'tags.title': 'الوسوم',
  'tags.body': 'تسميات حرة تعمل عبر كل الكائنات. التكرارات المتقاربة هي ما يجعل قائمة الوسوم عديمة الفائدة، لذا تُفحص هذه القائمة بحثًا عنها.',
  'tags.add': 'أضف وسمًا',
  'tags.placeholder': 'اكتب وسمًا',
  'tags.uses': '{count} سجل',
  'tags.governed': 'المسؤولون وحدهم يمكنهم إنشاء وسم جديد',
  'tags.ungoverned': 'يمكن لأي شخص إنشاء وسم',
  'tags.similar': 'هذه تبدو متشابهة',
  'tags.merge': 'ادمج في {label}',
  'tags.merged': 'نُقل {count} سجل إلى {label}.',
  'tags.rename': 'أعد التسمية',
  'tags.empty': 'لا توجد وسوم بعد. أضف واحدًا على سجل، أو أنشئ الأول هنا.',
  'tags.noSimilar': 'لا توجد تكرارات متقاربة.',
  'files.title': 'الملفات',
  'files.drop': 'أفلت ملفًا في أي مكان على هذه الصفحة',
  'files.choose': 'اختر ملفًا',
  'files.empty': 'لا توجد ملفات بعد. أفلت واحدًا في أي مكان على هذه الصفحة.',
  'files.version': 'الإصدار {n}',
  'files.notScanned': 'لم يُفحص',
  'files.infected': 'عُثر على برمجية خبيثة',
  'files.remove': 'أزل',
  'files.max': 'حتى {size}',
  'files.tooBig': 'حجم الملف {size}. الحد الأقصى {max}.',
  'files.uploading': 'جارٍ رفع {name}…',
  'work.priority.high': 'عالية',
  'work.priority.low': 'منخفضة',
  'work.reminded': 'أُرسل {count} تذكير.',
  'graphql.title': 'ساحة الاستعلام',
  'graphql.body': 'طلب واحد عبر كائنات مترابطة. العمق محدود بـ {depth} والتكلفة بنحو {cost} استعلام قاعدة بيانات، ويسمّي الرفض الحقل الذي تجاوز عنده.',
  'graphql.run': 'شغّل',
  'graphql.result': 'النتيجة',
  'graphql.readOnly': 'للقراءة فقط. لا توجد عمليات تعديل: الكتابة تمر عبر نقاط REST.',
  'developer.title': 'المطوّر',
  'developer.body': 'تتيح الرموز لبرنامج استدعاء مساحة العمل هذه. يعمل الرمز نيابةً عن الشخص الذي أنشأه ويرى ما يراه تمامًا.',
  'developer.name': 'ما الغرض من هذا الرمز؟',
  'developer.scopes': 'ما الذي يُسمح له بفعله؟',
  'developer.create': 'أصدر رمزًا',
  'developer.secretTitle': 'انسخه الآن. لن يُعرض مرة أخرى.',
  'developer.secretBody': 'يُخزَّن كتجزئة، فلا يستطيع أحد قراءته لاحقًا — ولا نحن. إن فقدته، أصدر رمزًا جديدًا.',
  'developer.done': 'نسختُه',
  'developer.empty': 'لا توجد رموز بعد. أصدر رمزًا عندما يحتاج برنامج إلى استدعاء مساحة العمل بنفسه.',
  'developer.revoke': 'إلغاء',
  'developer.revoked': 'ملغى',
  'developer.neverUsed': 'لم يُستخدم قط',
  'developer.lastUsed': 'آخر استخدام {when}',
  'developer.actsAs': 'يعمل نيابةً عن {name}',
  'developer.contract': 'اقرأ عقد الواجهة البرمجية',
  'developer.rateLimit': '{limit} استدعاء في الدقيقة على باقتك، محسوبة لكل رمز.',
  'developer.confirmRevoke': 'إلغاء {name}؟',
  'developer.confirmRevokeBody': 'سيتوقف أي برنامج يستخدم هذا الرمز عند استدعائه التالي. لا يمكن التراجع؛ أصدر رمزًا جديدًا بدلًا من ذلك.',
  'health.title': 'الحالة',
  'health.body': 'ما الذي يتعطّل الآن، محسوبًا خلال آخر {hours} ساعة. كل رقم هنا يحصي شيئًا قام به شخص أو مهمة فعلًا.',
  'health.status.ok': 'يعمل',
  'health.status.warn': 'متدهور',
  'health.status.down': 'متعطل',
  'health.status.not-measured': 'غير مقيس',
  'health.look': 'ألقِ نظرة',
  'health.notMeasured': 'غير مقيس هنا',
  'privacy.title': 'طلبات أصحاب البيانات',
  'privacy.body': 'شخص يطلب بياناته، أو يطلب محوها. يُسجَّل الرد، لأن السجل هو الدليل على أنك رددت.',
  'privacy.subjectEmail': 'بريده الإلكتروني',
  'privacy.kind.access': 'أرسل له بياناته',
  'privacy.kind.erasure': 'امحُ بياناته',
  'privacy.open': 'سجّل الطلب',
  'privacy.queue': 'الطلبات',
  'privacy.empty': 'لا توجد طلبات. عندما يطلب أحدهم نسخة من بياناته أو يطلب محوها، سجّله هنا ليكون الرد موثقًا.',
  'privacy.status.open': 'مفتوح',
  'privacy.status.completed': 'تمت الإجابة',
  'privacy.status.refused': 'مرفوض',
  'privacy.review': 'راجع ما سيُمحى',
  'privacy.export': 'أرسل النسخة',
  'privacy.refuse': 'ارفض',
  'privacy.refuseReason': 'لماذا يُرفض هذا الطلب؟',
  'privacy.confirmTitle': 'سيؤدي هذا إلى محو {email} من {count} مخازن.',
  'privacy.confirmBody': 'اقرأ القائمة. المحو لا يمكن التراجع عنه، وكل مخزن يُعامل بطريقة مختلفة.',
  'privacy.action.delete': 'سيُفرَّغ',
  'privacy.action.anonymise': 'يبقى، مع إزالة هوية الشخص',
  'privacy.retainedTitle': 'هذه تحتفظ بشيء عنه',
  'privacy.erase': 'امحُ من {count} مخازن',
  'privacy.nothing': 'لا يوجد شيء عن هذا العنوان هنا. لا شيء لمحوه.',
  'privacy.already': 'سبق محو هذا العنوان.',
  'privacy.erased': 'تم المحو من {count} مخازن.',
  'privacy.exported': 'أُرسلت النسخة. عدد السجلات: {count}.',
  'residency.title': 'أين تُحفظ بياناتك',
  'residency.region': 'المنطقة',
  'residency.fixed': 'لا يمكن تغيير هذا. نقل مساحة العمل يعني نقل سجلاتها وملفاتها ونسخها الاحتياطية، وهو ترحيل لا إعداد.',
  'residency.choose': 'أين تريد حفظ بياناتك؟',
  'residency.chooseHelp': 'اختر بعناية: لا يمكن تغيير ذلك لاحقًا.',
  'residency.onlyHere': 'ستُحفظ بياناتك في {region}.',
  'brand.title': 'الهوية البصرية',
  'brand.body': 'اختر لونًا واحدًا. تُشتق منه كل الدرجات التي تحتاجها الواجهة، فلا يمكن أن ينتهي اختيارك إلى نص غير مقروء.',
  'brand.enable': 'استخدام الهوية البصرية لمساحة العمل',
  'brand.colour': 'لون العلامة',
  'brand.logo': 'رابط الشعار',
  'brand.favicon': 'رابط أيقونة الموقع',
  'brand.loginBackground': 'رابط خلفية تسجيل الدخول',
  'brand.sendingDomain': 'نطاق الإرسال',
  'brand.sendingDomain.help': 'النطاق الذي تُرسل منه رسائل التسويق.',
  'brand.save': 'حفظ الهوية',
  'brand.preview': 'معاينة',
  'brand.preview.nav': 'التنقّل',
  'brand.preview.record': 'سجل',
  'brand.preview.email': 'رسالة',
  'brand.preview.button': 'إجراء رئيسي',
  'brand.preview.link': 'رابط داخل النص',
  'brand.useSuggested': 'استخدم {colour} بدلًا منه',
  'brand.derived': 'مشتقة من لونك',
  'setup.showAdvanced': 'إظهار {count} أقسام متقدمة إضافية',
  'setup.advancedHidden': 'قواعد المشاركة والحقول المحسوبة والكائنات المخصصة والدخول الموحّد في الوضع المتقدم.',
  'sso.title': 'الدخول الموحّد',
  'sso.body': 'أرسل المستخدمين إلى موفّر الهوية بدلًا من طلب كلمة مرور. سجّل الدخول مرة بنفسك قبل فرضه.',
  'sso.protocol': 'البروتوكول',
  'sso.issuer': 'الجهة المُصدِرة',
  'sso.clientId': 'معرّف العميل',
  'sso.clientSecret': 'سر العميل',
  'sso.secretKept': 'يوجد سر محفوظ. اترك الحقل فارغًا للإبقاء عليه.',
  'sso.authorizeUrl': 'رابط التفويض',
  'sso.tokenUrl': 'رابط الرمز',
  'sso.jwksUrl': 'رابط المفاتيح (JWKS)',
  'sso.saml.metadata': 'ما تعطيه لمزوّد الهوية',
  'sso.saml.entityId': 'معرّف الكيان (الجمهور)',
  'sso.saml.acs': 'عنوان الرد (خدمة استقبال التأكيد)',
  'sso.saml.signing': 'وقّع التأكيد نفسه، لا الاستجابة فقط. الاستجابة الموقّعة حول تأكيد غير موقّع مرفوضة.',
  'sso.saml.copy': 'انسخ البيانات الوصفية',
  'sso.saml.copied': 'تم النسخ',
  'sso.entityId': 'معرّف الكيان',
  'sso.ssoUrl': 'رابط تسجيل الدخول',
  'sso.certificate': 'شهادة التوقيع',
  'sso.domains': 'النطاقات',
  'sso.domains.help': 'تُرسَل العناوين على هذه النطاقات إلى موفّرك. نطاق واحد لمساحة عمل واحدة.',
  'sso.save': 'حفظ الاتصال',
  'sso.verified': 'جُرِّب {when}',
  'sso.notVerified': 'لم يُجرَّب بعد. سجّل الدخول عبر هذا الموفّر مرة قبل فرضه.',
  'sso.enforce': 'اشتراط الدخول الموحّد',
  'sso.enforced': 'مطلوب. يسجّل الجميع الدخول عبر الموفّر عدا مسؤول الطوارئ.',
  'sso.notEnforced': 'اختياري. لا يزال بإمكان المستخدمين الدخول بكلمة مرور.',
  'sso.jit': 'إنشاء الحسابات عند تسجيل الدخول',
  'sso.jit.help': 'من لا حساب له هنا يحصل على حساب عند أول دخول. اختر ما يمكنه رؤيته.',
  'sso.jit.role': 'دور الحسابات الجديدة',
  'sso.breakGlass': 'مسؤول الطوارئ',
  'sso.breakGlass.help': 'حساب واحد يستطيع الدخول بكلمة مرور دائمًا، حتى عند تعذّر الوصول إلى الموفّر.',
  'sso.scim.title': 'التزويد من الدليل',
  'sso.scim.body': 'امنح موفّرك رمزًا لإنشاء الأشخاص وتحديثهم وتعطيلهم هنا.',
  'sso.scim.create': 'إصدار رمز',
  'sso.scim.label': 'التسمية',
  'sso.scim.once': 'انسخه الآن. لن يُعرض مرة أخرى.',
  'sso.scim.expiry': 'تنتهي صلاحيته بعد',
  'sso.scim.expiry.never': 'لا تنتهي صلاحيته',
  'sso.scim.expiry.days': '{count} يومًا',
  'sso.scim.expiresOn': 'ينتهي في {when}',
  'sso.scim.expired': 'منتهي الصلاحية — لم يعد هذا الرمز يعمل',
  'sso.scim.revoke': 'إبطال',
  'sso.scim.none': 'لا توجد رموز. لا يستطيع موفّرك تزويد أحد قبل وجود رمز.',
  'sso.scim.lastUsed': 'آخر استخدام {when}',
  'sso.scim.neverUsed': 'لم يُستخدم قط',
  'fx.title': 'العملات وأسعار الصرف',
  'week.title': 'أسبوع العمل',
  'bin.title': 'سلة المحذوفات',
  'bin.body': 'تبقى السجلات المحذوفة هنا {days} يوماً في هذه الخطة، ثم تُحذف نهائياً.',
  'bin.empty': 'لم يُحذف أي شيء.',
  'bin.all': 'كل شيء',
  'bin.filter': 'نوع السجل',
  'bin.record': 'السجل',
  'bin.deletedBy': 'حذفه',
  'bin.deletedAt': 'تاريخ الحذف',
  'bin.expires': 'يُحذف نهائياً',
  'bin.expired': 'تجاوز المهلة',
  'bin.restore': 'استعادة',
  'bin.purge': 'حذف نهائي',
  'bin.purgeExpired': 'حذف كل ما تجاوز مهلته',
  'bin.purgeExpired.none': 'لا شيء تجاوز مهلته.',
  'bin.purged': 'تم حذف {count} نهائياً.',
  'bin.confirm.title': 'حذف {name} نهائياً؟',
  'bin.confirm.body': 'لا يمكن التراجع عن هذا. يحتفظ سجل التدقيق بأن ذلك حدث ومن قام به.',
  'bin.confirm.yes': 'حذف نهائي',
  'bin.noScheduler': 'لا يوجد مجدول، لذا تبقى السجلات التي تجاوزت مهلتها حتى يحذفها أحد.',
  'week.body': 'ما تحتسبه مهلة اتفاقية الخدمة عندما تنص السياسة على توقفها خارج ساعات العمل.',
  'week.days': 'أيام العمل',
  'week.start': 'يفتح',
  'week.end': 'يغلق',
  'week.save': 'حفظ أسبوع العمل',
  'week.saved': 'تم الحفظ.',
  'week.day.0': 'الأحد',
  'week.day.1': 'الإثنين',
  'week.day.2': 'الثلاثاء',
  'week.day.3': 'الأربعاء',
  'week.day.4': 'الخميس',
  'week.day.5': 'الجمعة',
  'week.day.6': 'السبت',
  'week.zone': 'الأوقات بتوقيت {zone}، المنطقة الزمنية لمساحة العمل.',
  'fx.body': 'قيمة العملة اعتبارًا من تاريخ. يُحوَّل السجل بالسعر الساري في تاريخه.',
  'fx.reporting': 'تُعد تقارير مساحة العمل بعملة {code}',
  'fx.add': 'إضافة عملة',
  'fx.code': 'العملة',
  'fx.remove': 'إزالة',
  'fx.rate.from': 'من',
  'fx.rate.to': 'إلى',
  'fx.rate.on': 'اعتبارًا من',
  'fx.rate.value': 'السعر',
  'fx.rate.save': 'حفظ السعر',
  'fx.rate.none': 'لا أسعار بعد. لا يمكن تحويل المبالغ بعملات أخرى حتى يوجد سعر.',
  'report.converted': 'كل العملات، بعملة {code}',
  'report.converted.gap': 'تعذّر تحويل {count} مبلغًا: لا يوجد سعر لـ {from}.',
  'refusal.sso.noProvider': 'لا يوجد مزوّد هوية لمساحة العمل تلك.',
  'refusal.sso.refused': 'رفض مزوّد الهوية تسجيل الدخول.',
  'refusal.sso.requestSpent': 'تم استخدام تسجيل الدخول هذا من قبل أو انتهت صلاحيته. ابدأ من جديد.',
  'refusal.sso.usedAlready': 'تم استخدام تسجيل الدخول هذا من قبل. ابدأ من جديد.',
  'refusal.sso.unverified': 'تعذّر التحقق من رد مزوّد الهوية.',
  'refusal.sso.noEmail': 'لم يرسل مزوّد الهوية عنوان بريد إلكتروني، فلا يوجد من يمكن تسجيل دخوله.',
  'refusal.sso.deactivated': 'تم تعطيل هذا الحساب في مساحة العمل هذه.',
  'refusal.sso.addressTaken': 'يقول مزوّدك إن عنوانك أصبح {email}، لكن حسابًا آخر في مساحة العمل هذه يستخدمه بالفعل. على أحد المسؤولين معالجة ذلك قبل أن تتمكن من تسجيل الدخول.',
  'refusal.sso.noAccount': 'ليس لديك حساب في مساحة العمل هذه، وهي لا تنشئ حسابات عند تسجيل الدخول. اطلب من أحد المسؤولين دعوتك.',
  'refusal.sso.ambiguousWorkspace': 'أكثر من مساحة عمل هنا تثق بمزوّد الهوية ذاك، وتسجيل دخول بدأه المزوّد بنفسه لا يحدد أيها المقصود. ابدأ من صفحة تسجيل الدخول بدلًا من ذلك.',
  'refusal.session.alreadyEnded': 'لم تعد جلسة تسجيل الدخول تلك نشطة، فلا يوجد ما يمكن إلغاؤه.',
  'refusal.stage.unknownField': 'لا يوجد في الصفقة حقل باسم {fields}، لذا لا يمكن مغادرة مرحلة تشترطه أبدًا.',
  'refusal.stage.exitCriteria': 'تحتاج {stage} إلى {fields} قبل أن تنتقل الصفقة.',
  'refusal.reason.needsName': 'يحتاج السبب إلى اسم.',
  'refusal.reason.needsOutcome': 'حدّد ما إذا كان هذا السبب يفسّر فوزًا أم خسارة.',
  'refusal.reason.duplicate': '{label} سبب مسجّل بالفعل لتلك النتيجة.',
  'refusal.reason.wrongEnding': 'هذا السبب يفسّر نتيجة مختلفة عن التي تُغلق بها هذه الصفقة.',
  'refusal.competitor.needsName': 'يحتاج المنافس إلى اسم.',
  'refusal.competitor.duplicate': '{name} مدرج بالفعل في قائمة المنافسين.',
  'refusal.score.badCondition': 'هذا الشرط لا يصلح للعميل المحتمل: {problems}',
  'refusal.score.needsName': 'تحتاج قاعدة النقاط إلى اسم يفهمه المندوب.',
  'refusal.score.needsCondition': 'تحتاج قاعدة النقاط إلى شرط.',
  'refusal.score.needsPoints': 'تحتاج قاعدة النقاط إلى عدد صحيح من النقاط. يُسمح بالسالب.',
  'refusal.score.duplicate': '{label} قاعدة نقاط موجودة بالفعل.',
  'refusal.score.badDecay': 'الانخفاض عدد صحيح من النقاط أسبوعيًا، بين 0 و100.',
  'refusal.notFound': '{nounKey} غير موجود. ربما حُذف، أو ربما تتبع رابطًا قديمًا.',
  'noun.tenant': 'مساحة العمل هذه',
  'noun.stage': 'تلك المرحلة',
  'noun.deal': 'تلك الصفقة',
  'noun.pricelist': 'قائمة الأسعار تلك',
  'noun.account': 'تلك الشركة',
  'refusal.deal.contactRole': 'يجب أن يكون دور جهة الاتصال في الصفقة أحد الأدوار التي يمكن للمنتج إعداد تقارير عنها.',
  'refusal.deal.teamRole': 'يجب أن يكون دور عضو الفريق أحد الأدوار التي يمكن للمنتج إعداد تقارير عنها.',
  'refusal.deal.splitTotal': 'مجموع الحصص {total}%، والصفقة تُقسَّم مئة بالمئة أو لا تُقسَّم إطلاقًا.',
  'noun.contact': 'جهة الاتصال تلك',
  'noun.person': 'ذلك الشخص',
  'noun.product': 'ذلك المنتج',
  'noun.reason': 'ذلك السبب',
  'noun.competitor': 'ذلك المنافس',
  'noun.scoreRule': 'قاعدة النقاط تلك',
  'refusal.lastAdmin': 'لن يبقى أحد قادر على إدارة الأشخاص. امنح شخصًا آخر مجموعة المسؤول أولاً.',
  'refusal.currencySelf': 'العملة تساوي دائمًا واحدًا من نفسها.',
  'refusal.currencyCode': 'العملة رمز من ثلاثة أحرف، مثل AED أو USD.',
  'refusal.currencyOwn': '{code} هي عملة مساحة العمل نفسها، لذا لا يمكن إزالتها. غيّر عملة مساحة العمل أولاً.',
  'refusal.rateShape': 'السعر رقم موجب، مثل 3.6725.',
  'refusal.objectNoOwner': 'لا مالك لـ{plural}، لذا لا يمكن أن تكون خاصة. اختر من يمكنه رؤيتها بدلاً من ذلك.',
  'refusal.sharingAccess': 'اختر مقدار الوصول: قراءة، أو قراءة وتحرير.',
  'refusal.criteriaGrantee': 'اختر فريقًا واحدًا أو دورًا واحدًا فقط لتشارك معه القاعدة.',
  'refusal.formulaCycle': 'هذه الصيغة تعتمد على نفسها: {cycle}.',
  'refusal.apiName': 'الاسم البرمجي يبدأ بحرف، ويستخدم أحرفًا صغيرة وأرقامًا وشرطات سفلية، وطوله بين ثلاثة وأربعين حرفًا.',
  'permissions.title': 'الصلاحيات',
  'permissions.body': 'ما يُسمح للشخص بفعله. ست مجموعات تأتي مع مساحة العمل وتغطي معظم الحالات.',
  'permissions.preset': 'تأتي مع مساحة العمل',
  'permissions.custom': 'مخصصة',
  'permissions.holders': 'يحملها {count} أشخاص',
  'permissions.holders.none': 'لا أحد يحملها',
  'permissions.who': 'من يحمل ماذا',
  'permissions.none': 'لا توجد مجموعات صلاحيات',
  'permissions.edit': 'تغيير مجموعات {name}',
  'permissions.change': 'تغيير',
  'permissions.editSet': 'تغيير ما يسمح به',
  'permissions.save': 'حفظ',
  'permissions.cancel': 'إلغاء',
  'permissions.diff.title': 'ما الذي سيتغير',
  'permissions.diff.gains': 'يكتسب',
  'permissions.diff.losses': 'يفقد',
  'permissions.diff.unchanged': 'لا شيء يتغير. هذه المجموعات تمنح ما تمنحه المجموعات الحالية.',
  'permissions.diff.checking': 'جارٍ حساب ما سيتغير',
  'permissions.create': 'إنشاء مجموعة مخصصة',
  'permissions.deactivate': 'إلغاء التفعيل',
  'permissions.reactivate': 'إعادة التفعيل',
  'permissions.status.deactivated': 'مُلغى التفعيل',
  'account.region': 'المنطقة',
  'account.locale': 'التواريخ والأرقام',
  'account.timezone': 'المنطقة الزمنية',
  'account.numerals': 'الأرقام',
  'account.numerals.latn': '1234',
  'account.numerals.arab': '١٢٣٤',
  'account.region.example': 'يظهر اليوم بصيغة {date}، والمبلغ بصيغة {amount}.',
  'account.density': 'الواجهة',
  'account.density.simple': 'مبسّطة',
  'account.density.advanced': 'متقدمة',
  'account.trial': 'تنتهي الفترة التجريبية {when}',

  'sandbox.banner': 'الوضع التجريبي: تُسجَّل الرسائل هنا بدلاً من إرسالها.',
  'sandbox.view': 'اطّلع على ما كان سيُرسل',

  // --- home, records and fields -------------------------------------------
  'home.title': 'الرئيسية',
  'home.lede': 'ما هو قيد التنفيذ، وما يجب عمله، وما تغيّر منذ آخر زيارة.',
  'home.kpi.weighted': 'مرجّح',
  'home.kpi.weighted.basis': 'حسب احتمالية المرحلة',
  'home.kpi.openPipeline': 'خط الأنابيب المفتوح',
  'home.kpi.dealCount': '{count} صفقة مفتوحة',
  'home.kpi.noDeals': 'لا توجد صفقات مفتوحة بعد',
  'home.myDay.open': 'فتح',
  'home.myDay.openWork': 'فتح مهامي',
  'home.pipeline.covers': 'تغطي الإجماليات {counted} من {count} صفقة، بعملة {currency}.',
  'home.myDay': 'يومي',
  'home.myDay.empty': 'لا يوجد شيء مجدول اليوم',
  'home.myDay.all': 'عرض كل أعمالي',
  'home.pipeline': 'خط الأنابيب',
  'home.pipeline.open': '{count} مفتوحة بقيمة {amount}',
  'home.pipeline.empty': 'لم يتم إعداد خط أنابيب بعد',
  'home.pipeline.board': 'فتح اللوحة',
  'home.recent': 'المحدَّثة مؤخرًا',
  'home.recent.empty': 'لم يتم تحديث أي شيء بعد',
  'home.checklist': 'البدء',
  'home.checklist.done': '{done} من {total} مكتملة',
  'home.checklist.complete': 'كل شيء هنا تم إعداده.',
  'home.checklist.hide': 'أخفِ هذا',
  'home.checklist.show': 'أظهر قائمة البدء',
  'home.checklist.collapse': 'اطوِ',
  'home.checklist.expand': 'وسّع',
  'home.checklist.hidden': 'تم إخفاؤها. يمكنك إعادتها متى شئت.',
  'home.lists.new': 'جديد',
  'home.widget.failed': 'تعذر تحميل هذا الجزء',
  'home.widget.retry': 'إعادة المحاولة',
  'record.mode.simple': 'مبسط',
  'record.mode.advanced': 'عرض كل الحقول',
  'record.showAll': 'عرض {count} إضافية',
  'record.omitted.fieldPermission': '{count} مخفية بسبب الأذونات',
  'record.omitted.advancedOnly': '{count} تظهر في الوضع المتقدم',
  'record.omitted.overBudget': '{count} حقول إضافية',
  'record.omitted.noSuchField': '{count} حقول لم تعد موجودة',
  'record.edit': 'تعديل',
  'record.conflict.title': 'قام شخص آخر بتعديل هذا.',
  'record.conflict.body': 'تم حفظ كل ما غيّرته عدا ما يلي. اختر القيمة التي تريد الاحتفاظ بها.',
  'record.conflict.keepYours': 'الاحتفاظ بقيمتي',
  'record.conflict.keepTheirs': 'استخدام قيمتهم',
  'record.conflict.resolved': 'تم الحل',
  'record.saved.count': '{count} حقول محفوظة',
  'field.yes': 'نعم',
  'field.no': 'لا',
  'field.download': 'تنزيل',
  'field.more': '+{count} إضافية',
  'field.choose': 'اختيار',
  'field.clear': 'مسح',
  'deal.competitors': 'من غيرنا في الصفقة',
  'deal.competitors.none': 'لم يُذكر أحد بعد',
  'deal.competitors.add': 'أضف منافسًا',
  'deal.competitors.newName': 'منافس جديد',
  'deal.competitors.outcomeFor': 'ما الذي حدث أمام {name}',
  'deal.competitors.unknown': 'ما زالت مفتوحة',
  'deal.competitors.won-against': 'فزنا أمامهم',
  'deal.competitors.lost-to': 'خسرنا أمامهم',
  'deal.competitors.withdrew': 'انسحبوا',
  'deal.stage.checklist': 'قبل أن تغادر الصفقة {stage}',
  'deal.stage.met': 'مكتمل',
  'deal.stage.needed': 'ما زال مطلوبًا',
  'deal.stage.refused': 'تم رفض هذا النقل',
  'deal.stage': 'المرحلة',
  'deal.amount': 'المبلغ',
  'deal.closeDate': 'تاريخ الإغلاق',

  'metric.trend.up': 'ارتفاع {change}',
  'metric.trend.down': 'انخفاض {change}',
  'metric.trend.flat': 'لا تغيير',


  // --- record pages -------------------------------------------------------
  'record.timeline': 'الجدول الزمني',
  'record.timeline.all': 'الكل',
  'record.timeline.empty': 'لا يوجد نشاط بعد',
  'record.timeline.emptyAction': 'سجّل أول نشاط',
  'record.timeline.byAgent': 'نفّذه وكيل',
  'record.timeline.bySystem': 'نُفّذ تلقائيًا',
  'record.timeline.why': 'لماذا حدث هذا؟',
  'record.timeline.expand': 'عرض المزيد',
  'record.timeline.collapse': 'عرض أقل',
  'record.timeline.loadMore': 'تحميل الأقدم',
  'record.timeline.showing': '{shown} من {total}',
  'record.details': 'التفاصيل',
  'record.related': 'ذات صلة',
  'record.follow': 'متابعة',
  'record.following': 'تتابع',
  'record.followers': '{count} يتابعون',
  'record.action.call': 'اتصال',
  'record.action.email': 'بريد إلكتروني',
  'record.action.whatsapp': 'واتساب',
  'record.action.note': 'تسجيل ملاحظة',
  'record.note.subject': 'ماذا حدث',
  'record.note.body': 'أي شيء يستحق التذكر',
  'record.note.save': 'حفظ الملاحظة',
  'record.note.cancel': 'إلغاء',
  'record.action.schedule': 'جدولة خطوة',
  'record.more': 'المزيد',
  'record.notFound': 'تعذر العثور على هذا السجل',
  'record.notFound.body': 'قد يكون محذوفًا، أو يخص زميلاً لا يمكنك الاطلاع على سجلاته.',
  'consent.optedIn': 'موافق',
  'consent.optedOut': 'غير موافق',
  'consent.unknown': 'لم يُسأل',
  'consent.channel.email': 'البريد الإلكتروني',
  'consent.channel.sms': 'رسائل نصية',
  'consent.channel.whatsapp': 'واتساب',
  'consent.channel.call': 'المكالمات',
  'consent.channel.post': 'البريد',
  'activity.type.note': 'ملاحظة',
  'activity.type.call': 'مكالمة',
  'activity.type.email': 'بريد إلكتروني',
  'activity.type.meeting': 'اجتماع',
  'activity.type.whatsapp': 'واتساب',
  'activity.type.sms': 'رسالة نصية',
  'activity.type.next-step': 'الخطوة التالية',


  // --- leads, conversion and companies ------------------------------------
  'lead.score': 'النقاط',
  'lead.scoring.title': 'كيف تُحتسب نقاط العملاء المحتملين',
  'lead.scoring.body': 'النقاط التي يكسبها العميل المحتمل ويخسرها. المندوب الذي لا يستطيع مناقشة الرقم سيتجاهله، لذا تظهر كل قاعدة في التفصيل على السجل.',
  'lead.scoring.label': 'ماذا نسميها',
  'lead.scoring.when': 'عندما يكون لهذا الحقل قيمة',
  'lead.scoring.points': 'النقاط (يُسمح بالسالب)',
  'lead.scoring.decay': 'عميل محتمل لا يتابعه أحد',
  'lead.scoring.decay.none': 'لا تنخفض نقاطه',
  'lead.scoring.decay.perWeek': 'يخسر {points} أسبوعيًا',
  'lead.scoring.rescore': 'طبّق على كل العملاء المحتملين',
  'lead.scoring.rescored': 'أُعيد احتساب {count} عميلًا محتملًا',
  'lead.status': 'الحالة',
  'lead.source': 'المصدر',
  'lead.created': 'أُضيف',
  'lead.empty.title': 'لا توجد عملاء محتملون بعد',
  'lead.empty.body': 'العميل المحتمل هو من أبدى اهتمامًا ولم يصبح عميلاً بعد. يصلون عبر النماذج والاستيراد والحملات.',
  'lead.scoreBreakdown': 'سبب هذه النقاط',
  'lead.convert': 'تحويل',
  'lead.converted': 'تم التحويل',
  'lead.converted.body': 'أصبح هذا العميل المحتمل شركة وشخصًا وصفقة.',
  'lead.undo': 'التراجع عن التحويل',
  'lead.undo.until': 'يمكنك التراجع حتى {when}',
  'lead.undo.expired': 'انتهت مهلة التراجع',
  'convert.title': 'تحويل هذا العميل المحتمل',
  'convert.body': 'هذا ما سيتم إنشاؤه بالضبط. لن يُنشأ شيء حتى تؤكد.',
  'convert.company': 'الشركة',
  'convert.person': 'الشخص',
  'convert.deal': 'الصفقة',
  'convert.willCreate': 'سيتم إنشاؤه',
  'convert.willLink': 'موجود بالفعل - سيتم استخدامه',
  'convert.noDeal': 'لن يتم إنشاء صفقة',
  'convert.createDeal': 'إنشاء صفقة',
  'convert.dealName': 'اسم الصفقة',
  'convert.dealAmount': 'المبلغ',
  'convert.confirm': 'إنشاء الثلاثة',
  'convert.confirmTwo': 'إنشاء الاثنين',
  'convert.reversible': 'يمكنك التراجع خلال 24 ساعة.',
  'convert.failed': 'لم يتم إنشاء أي شيء',
  'account.openPipeline': 'خط الأنابيب المفتوح',
  'account.wonRevenue': 'الإيرادات المُغلقة',
  'account.people': 'الأشخاص',
  'record.delete': 'حذف',
  'record.deleted': 'تم حذف {name}',
  'record.deleted.body': 'إنه في سلة المحذوفات. لا يمكن لأحد غيرك رؤيته.',
  'record.undo': 'تراجع',
  'record.restored': 'تمت استعادة {name}',
  'record.delete.confirm': 'هل تريد حذف {name}؟ سينتقل إلى سلة المحذوفات ويمكن استعادته.',
  'record.alreadyDeleted': 'قام شخص آخر بحذفه قبلك.',
  'account.thisCompany': 'هذه الشركة',
  'account.group': 'إجمالي المجموعة',
  'account.group.context': 'عبر {count} شركة يمكنك رؤيتها',
  'account.pricelist': 'قائمة الأسعار',
  'account.pricelist.standard': 'التسعير القياسي',
  'account.pricelist.help': 'تُسعّر عروض هذه الشركة من هذه القائمة. التسعير القياسي يستخدم الكتالوج وأي قواعد تنطبق على الجميع.',
  'account.hierarchy': 'هيكل المجموعة',
  'account.openCases': 'الحالات المفتوحة',
  'account.noSubsidiaries': 'لا توجد شركات تابعة لهذه الشركة.',
  'account.hierarchy.scope': 'الشركات التابعة التي لا يُسمح لك بفتحها غير محتسبة هنا.',
  'account.subsidiaries': 'الشركات التابعة',
  'account.empty.title': 'لا توجد شركات بعد',
  'account.empty.body': 'الشركات تجمع الأشخاص الذين تتعامل معهم والصفقات التي تديرها معهم.',


  // --- deals and quotes ---------------------------------------------------
  'deal.board': 'اللوحة',
  'deal.list': 'القائمة',
  'deal.people.theirs': 'من نبيع له',
  'deal.people.ours': 'من يعمل عليها',
  'deal.people.noContacts': 'لم يُذكر أحد من جانبهم بعد',
  'deal.people.noTeam': 'المالك وحده',
  'deal.people.total': 'مجموع الحصص',
  'deal.people.roleFor': 'ما هو {name} بالنسبة لهذه الصفقة',
  'deal.people.decision-maker': 'صاحب القرار',
  'deal.people.champion': 'الداعم',
  'deal.people.blocker': 'المعارض',
  'deal.people.influencer': 'المؤثر',
  'deal.people.user': 'المستخدم النهائي',
  'deal.people.technical': 'تقني',
  'deal.people.owner': 'المالك',
  'deal.people.contributor': 'مساهم',
  'deal.people.specialist': 'أخصائي',
  'deal.people.manager': 'مدير',
  'deal.people.introducer': 'من عرّف بالفرصة',
  'deal.pipeline': 'خط الأنابيب',
  'deal.emptyColumn': 'اسحب صفقة إلى هنا عندما تصل إلى {column}.',
  'deal.moveCard': 'النقل إلى مرحلة أخرى',
  'deal.moveTo': 'نقل {card} من {column}',
  'deal.moving': 'جارٍ النقل',
  'deal.reverted': 'أُعيد: {reason}',
  'deal.empty.title': 'لا توجد صفقات بعد',
  'deal.empty.body': 'الصفقة هي إيراد تعمل على تحقيقه، بمبلغ وتاريخ.',
  'deal.health.healthy': 'على المسار',
  'deal.health.watch': 'انتبه',
  'deal.health.atRisk': 'في خطر',
  'deal.health.won': 'رُبحت',
  'deal.health.lost': 'خُسرت',
  'deal.health.stalled': 'متوقفة',
  'deal.health.why': 'سبب هذه الدرجة',
  'deal.health.factors': 'ما وراءها',
  'deal.closeReason': 'لماذا أُغلقت بهذا الشكل؟',
  'deal.closeReason.needed': 'إغلاق الصفقة يحتاج سببًا.',
  'quote.title': 'عرض السعر',
  'quote.new': 'عرض سعر جديد',
  'quote.lines': 'البنود',
  'quote.description': 'الوصف',
  'quote.quantity': 'الكمية',
  'quote.unitPrice': 'سعر الوحدة',
  'quote.discount': 'خصم %',
  'quote.margin': 'هامش الربح',
  'quote.margin.unknown': 'لم تُدخل التكلفة، لذا الهامش غير معروف',
  'quote.margin.hidden': 'هامش الربح غير معروض لك',
  'quote.lineTotal': 'الإجمالي',
  'quote.optional': 'اختياري',
  'quote.addLine': 'إضافة بند',
  'quote.removeLine': 'إزالة {description}',
  'quote.keyboardHint': 'Tab للتنقل، Enter لإضافة صف، Ctrl+Backspace لحذف صف.',
  'quote.subtotal': 'المجموع الفرعي',
  'quote.optionalTotal': 'البنود الاختيارية، إن اختيرت',
  'quote.discountLabel': 'الخصم',
  'quote.total': 'الإجمالي',
  'quote.readOnly': 'تم إرسال هذا العرض، لذا لا يمكن تغيير بنوده.',
  'quote.send': 'إرسال العرض',
  'quote.sent': 'أُرسل',
  'quote.preview': 'معاينة',
  'quote.validUntil': 'صالح حتى',
  'quote.empty.title': 'لا توجد عروض على هذه الصفقة',
  'quote.empty.body': 'عرض السعر يوضح ما تقدمه وبأي سعر.',

  'deal.openOnly': 'الصفقات المفتوحة فقط',

  'quote.rowActions': 'إجراءات الصف',

  'deal.noTotal': '—',

  'home.kpi.someCurrencies': '{counted} من {count} صفقة مفتوحة، بعملة مساحة العمل',


  // --- setup ---------------------------------------------------------------
  'setup.title': 'الإعدادات',
  'setup.search': 'البحث في الإعدادات',
  'setup.category.objects': 'الكائنات والحقول',
  'setup.category.modules': 'الوحدات',
  'setup.category.data': 'البيانات',
  'setup.category.plan': 'الخطة والاستخدام',
  'setup.modules.title': 'الوحدات',
  'setup.modules.body': 'تشغيل أجزاء المنتج أو إيقافها لمساحة العمل هذه.',
  'setup.impact.title': 'قبل إيقاف هذا',
  'setup.impact.titleOn': 'قبل تشغيل هذا',
  'setup.impact.screens': '{count} شاشات ستختفي',
  'setup.impact.operations': '{count} عملية في الواجهة ستتوقف',
  'setup.impact.tools': '{count} أدوات وكيل ستخرج من السجل',
  'setup.impact.features': '{count} ميزات ستذهب معها',
  'setup.impact.dependents': 'تعتمد عليها أيضًا: {modules}',
  'setup.impact.retained': 'لن يُحذف شيء. تبقى هذه السجلات كما هي:',
  'setup.impact.confirmOff': 'إيقاف {module}',
  'setup.impact.confirmOn': 'تشغيل {module}',
  'setup.impact.core': 'هذه وحدة أساسية ولا يمكن إيقافها.',
  'setup.impact.locked': 'غير مشمولة في هذه الخطة.',
  'setup.layout.title': 'التخطيط',
  'setup.layout.available': 'الحقول المتاحة',
  'setup.layout.canvas': 'على الصفحة',
  'setup.layout.counter': '{count} حقول على الصفحة',
  'setup.layout.overBudget': 'الوضع المبسط يعرض أول {budget}. ستظهر {over} في الوضع المتقدم فقط.',
  'setup.layout.addField': 'إضافة {field}',
  'setup.layout.removeField': 'إزالة {field}',
  'setup.layout.empty': 'لا شيء على الصفحة بعد',
  'setup.field.new': 'حقل جديد',
  'setup.field.type': 'ما نوع الحقل؟',
  'setup.field.name': 'الاسم',
  'setup.field.key': 'اسم الواجهة',
  'setup.field.preview': 'هكذا سيبدو',
  'setup.field.create': 'إنشاء الحقل',
  'setup.import.title': 'الاستيراد',
  'setup.import.step.upload': 'اختر ملفًا',
  'setup.import.step.map': 'طابق الأعمدة',
  'setup.import.step.check': 'التحقق قبل الكتابة',
  'setup.import.step.done': 'تم',
  'setup.import.dryRun': 'فحص الملف',
  'setup.import.apply': 'استيراد {count} صفًا',
  'setup.import.valid': '{count} من {total} صفًا جاهزة',
  'setup.import.errors': '{count} صفوف بها مشكلة',
  'setup.import.download': 'تنزيل الأخطاء',
  'setup.import.undo': 'التراجع عن هذا الاستيراد',
  'setup.import.undoUntil': 'يمكنك التراجع حتى {when}',
  'setup.import.undone': 'أُزيل {removed} سجلًا، وتُرك {kept} لأن أحدًا عدّلها',
  'setup.import.history': 'عمليات الاستيراد الأخيرة',
  'setup.import.nothing': 'لا توجد عمليات استيراد بعد',
  'setup.plan.title': 'الخطة والاستخدام',
  'setup.plan.current': 'خطتك',
  'setup.plan.locked': 'تحتاج {plan}',
  'setup.plan.included': 'مشمولة',
  'setup.plan.usage': 'الاستخدام',
  'setup.plan.upgrade': 'تغيير الخطة',

  'action.on': 'مُفعّل',
  'action.off': 'مُعطّل',

  'setup.plan.lockedCount': 'غير مشمولة في هذه الخطة',


  // --- rules, pricing and variants ---------------------------------------
  'rule.all': 'كل من',
  'rule.any': 'أي من',
  'rule.none': 'لا شيء من',
  'rule.addCondition': 'إضافة شرط',
  'rule.addGroup': 'إضافة مجموعة',
  'rule.remove': 'إزالة',
  'rule.field': 'الحقل',
  'rule.operator': 'العملية',
  'rule.value': 'القيمة',
  'rule.and': 'و',
  'rule.or': 'أو',
  'rule.summary': '{count} شروط تنتج {outcome}',
  'rule.matching': '{count} مطابقة',
  'rule.matchingUnknown': 'لم يُحتسب بعد',
  'rule.sampleRows': 'على سبيل المثال',
  'rule.tooDeep': 'تتداخل القواعد {max} مستويات',
  'rule.op.eq': 'يساوي',
  'rule.op.ne': 'لا يساوي',
  'rule.op.lt': 'أقل من',
  'rule.op.lte': 'على الأكثر',
  'rule.op.gt': 'أكثر من',
  'rule.op.gte': 'على الأقل',
  'rule.op.between': 'بين',
  'rule.op.contains': 'يحتوي على',
  'rule.op.startsWith': 'يبدأ بـ',
  'rule.op.in': 'واحد من',
  'rule.op.notIn': 'ليس من',
  'rule.op.isSet': 'له قيمة',
  'rule.op.isNotSet': 'ليس له قيمة',
  'pricelist.title': 'قواعد التسعير',
  'pricelist.rules': 'القواعد، بترتيب الأولوية',
  'pricelist.preview': 'ما سيدفعه العميل',
  'pricelist.product': 'المنتج',
  'pricelist.customer': 'العميل',
  'pricelist.quantity': 'الكمية',
  'pricelist.winning': 'القاعدة الفائزة',
  'pricelist.listPrice': 'سعر القائمة',
  'pricelist.overlap': '{name} تنطبق أيضًا، لكن {winner} لها أولوية أقل رقمًا فتفوز.',
  'pricelist.empty.title': 'لا توجد قواعد تسعير بعد',
  'pricelist.empty.body': 'بدون قاعدة، يُباع كل منتج بسعر القائمة.',
  'pricelist.priority': 'الأولوية',
  'pricelist.newRule': 'إضافة قاعدة',
  'variant.title': 'المتغيرات',
  'variant.attributes': 'السمات',
  'variant.generated': '{count} متغيرًا',
  'variant.willGenerate': 'سينتج عن هذا {count} متغيرًا.',
  'variant.warn': 'هذا أكثر من {threshold}. راجع السمات قبل التوليد.',
  'variant.generate': 'توليدها',
  'variant.empty.title': 'لا توجد سمات بعد',
  'variant.empty.body': 'سمات مثل المقاس واللون تولّد متغيرًا لكل تركيبة.',
  'variant.sku': 'رمز المنتج',
  'variant.adjustment': 'تعديل السعر',
  'variant.active': 'نشط',


  // --- flows and segments -------------------------------------------------
  'flow.palette': 'الخطوات',
  'flow.canvas': 'المسار',
  'flow.addStep': 'إضافة {label}',
  'flow.removeStep': 'إزالة {label}',
  'flow.step': 'الخطوة {sequence}',
  'flow.tookBranch': 'سلك {branch}',
  'flow.notReached': 'لم يُصل إليها',
  'flow.input': 'رأى',
  'flow.output': 'قرّر',
  'flow.atThisStep': '{count} هنا',
  'flow.problems': 'قبل تشغيل هذا',
  'flow.activate': 'تشغيل',
  'flow.deactivate': 'إيقاف مؤقت',
  'flow.draft': 'مسودة',
  'flow.active': 'يعمل',
  'flow.paused': 'موقوف',
  'flow.test': 'اختبار على سجل',
  'flow.testRun': 'تشغيل الاختبار',
  'flow.runs': 'التشغيلات',
  'flow.run.empty': 'لم يعمل بعد',
  'flow.run.replay': 'إعادة التشغيل',
  'flow.run.outcome': 'النتيجة',
  'flow.run.steps': '{count} خطوات',
  'flow.run.waitingUntil': 'في الانتظار حتى {when}',
  'flow.run.left': 'غادر الرحلة: {reason}',
  'flow.empty.title': 'لا شيء هنا بعد',
  'flow.empty.body': 'يبدأ المسار بمُشغّل ويستمر حتى يوقفه شيء.',
  'segment.title': 'الجمهور',
  'segment.list.title': 'الجماهير',
  'segment.list.new': 'جمهور جديد',
  'segment.list.name': 'الجمهور',
  'segment.list.count': 'الأشخاص',
  'segment.list.countedAt': 'تم العد',
  'segment.list.neverCounted': 'لم يتم العد بعد',
  'segment.list.recount': 'عد الآن',
  'segment.list.empty.title': 'لا توجد جماهير محفوظة بعد',
  'segment.list.empty.body': 'الجمهور هو كل من يطابق مجموعة من الشروط. الحملات والرحلات ترسل إلى واحد.',
  'segment.members': '{count} شخصًا',
  'segment.sample': 'على سبيل المثال',
  'segment.empty.title': 'لا يوجد جمهور بعد',
  'segment.empty.body': 'الجمهور هو كل من تنطبق عليه مجموعة الشروط.',
  'chatbot.title': 'نص المحادثة',
  'chatbot.publish': 'نشر',
  'chatbot.blocked': 'هذه ستمنع عمله:',
  'chatbot.test': 'جرّبه',

  'segment.membersLabel': 'الأشخاص في هذا الجمهور',


  'revenue.title': 'الإيرادات المتكررة',
  'revenue.subscriptions': 'الاشتراكات',
  'revenue.mrr': 'الإيراد الشهري المتكرر',
  'revenue.arr': 'الإيراد السنوي المتكرر',
  'revenue.asAt': 'حتى تاريخ',
  'revenue.period': 'الفترة',
  'revenue.from': 'من',
  'revenue.to': 'إلى',
  'revenue.activeCount': '{count} مشترك يدفع',
  'revenue.statusLabel': 'الحالة',
  'revenue.movement': 'الحركة',
  'revenue.chart': 'كيف تحركت الإيرادات المتكررة خلال الفترة',
  'revenue.opening': 'الرصيد الافتتاحي',
  'revenue.closing': 'الرصيد الختامي',
  'revenue.new': 'جديد',
  'revenue.expansion': 'توسّع',
  'revenue.reactivation': 'عادوا',
  'revenue.contraction': 'انكماش',
  'revenue.churn': 'مغادرة',
  'revenue.increase': 'يضيف إلى الإجمالي',
  'revenue.decrease': 'يخصم من الإجمالي',
  'revenue.drillThrough': 'اعرض الاشتراكات خلف {step}',
  'revenue.nothingMoved': 'لم تتغير أي قيمة في هذه الفترة.',
  'revenue.doesNotBalance': 'هذه الأرقام لا تتطابق: تنقص الحركات عن الإجمالي الختامي بمقدار {difference}. لم يُرسم المخطط، لأن مخططاً غير متوازن يبدو تماماً كالمخطط المتوازن.',
  'revenue.empty.title': 'لا توجد إيرادات متكررة بعد',
  'revenue.empty.body': 'تظهر الأرقام هنا بمجرد وجود اشتراك. لا شيء يُقدَّر تقديراً.',
  'revenue.movements.empty': 'لم يتغير شيء بين هذين التاريخين.',
  'revenue.subscription': 'اشتراك',
  'revenue.plan': 'الخطة',
  'revenue.quantity': 'الكمية',
  'revenue.unitPrice': 'سعر الوحدة',
  'revenue.startedOn': 'بدأ في',
  'revenue.account': 'الشركة',
  'revenue.status.active': 'نشط',
  'revenue.status.paused': 'موقوف مؤقتاً',
  'revenue.status.cancelled': 'ملغى',
  'revenue.status.not-started': 'لم يبدأ',
  'revenue.history': 'سجل الخطة',
  'revenue.historyNote': 'كل تغيير قيد مستقل بتاريخ سريانه. لا يُعدَّل أي شيء هنا.',
  'revenue.effectiveOn': 'يسري في',
  'revenue.recordedAt': 'سُجِّل في',
  'revenue.reason': 'السبب',
  'revenue.superseded': 'ألغاه قيد تصحيحي',
  'revenue.corrects': 'يصحّح القيد {id}',
  'revenue.recordChange': 'سجّل تغييراً',
  'revenue.changeKind': 'ماذا حدث',
  'revenue.newMrr': 'القيمة الشهرية الجديدة',
  'revenue.save': 'سجّله',
  'revenue.cancel': 'إلغاء',
  'revenue.reasonRequired': 'اذكر سبب الإنهاء.',
  'revenue.effectiveHint': 'تاريخ السريان، وليس بالضرورة اليوم.',
  'revenue.kind.started': 'بدأ',
  'revenue.kind.upgraded': 'ترقية',
  'revenue.kind.downgraded': 'تخفيض',
  'revenue.kind.quantity-changed': 'تغيّرت الكمية',
  'revenue.kind.price-changed': 'تغيّر السعر',
  'revenue.kind.paused': 'إيقاف مؤقت',
  'revenue.kind.resumed': 'استئناف',
  'revenue.kind.cancelled': 'إلغاء',
  'revenue.kind.reactivated': 'إعادة تفعيل',
  'revenue.kind.corrected': 'تصحيح',
  'revenue.subscription.empty.title': 'لا توجد اشتراكات بعد',
  'revenue.subscription.empty.body': 'يسجّل الاشتراك ما يدفعه العميل، ومن ثم كل تغيير يطرأ عليه.',


  'report.title': 'تقرير',
  'report.builder': 'منشئ التقارير',
  'report.source': 'موضوع التقرير',
  'report.view': 'عبر عرض محفوظ',
  'report.view.none': 'بدون عرض',
  'report.fields': 'الحقول',
  'report.groupBy': 'تجميع الصفوف حسب',
  'report.summarise': 'التلخيص',
  'report.filters': 'عوامل التصفية',
  'report.chart': 'طريقة العرض',
  'report.preview': 'معاينة',
  'report.livePreview': 'يتحدّث هذا مع كل تغيير. لا توجد خطوة تشغيل.',
  'report.name': 'الاسم',
  'report.save': 'احفظ التقرير',
  'report.saved': 'تم الحفظ',
  'report.visibility': 'من يمكنه فتحه',
  'report.visibility.private': 'أنا فقط',
  'report.visibility.workspace': 'الجميع في مساحة العمل',
  'report.records': 'السجلات',
  'report.group': 'المجموعة',
  'report.value': 'القيمة',
  'report.rows': 'السجلات وراء ذلك',
  'report.rowsTruncated': 'تُعرض الصفحة الأولى فقط. الأرقام أعلاه تشمل الجميع.',
  'report.empty': 'لا شيء يطابق عوامل التصفية هذه.',
  'report.drillThrough': 'اعرض السجلات في {segment}',
  'report.noDrillThrough': 'لا توجد قائمة سجلات لهذه المجموعة.',
  'report.widerThanRows': 'تشمل هذه الأرقام {withheld} سجلاً لا يمكنك فتحه. تعرض القائمة أدناه ما يمكنك الاطلاع عليه فقط.',
  'report.problems': 'لا يمكن تشغيل هذا بعد:',
  'report.lastValid': 'تُعرض آخر نسخة صالحة.',
  'report.add': 'أضف',
  'report.remove': 'أزل',
  'report.fn.count': 'العدد',
  'report.fn.sum': 'الإجمالي',
  'report.fn.avg': 'المتوسط',
  'report.fn.min': 'الأدنى',
  'report.fn.max': 'الأعلى',
  'report.chart.table': 'جدول',
  'report.chart.bar': 'أشرطة',
  'report.chart.column': 'أعمدة',
  'report.chart.line': 'خط',
  'report.chart.donut': 'الحصص',
  'report.chart.funnel': 'قمع',
  'dashboard.title': 'لوحة المعلومات',
  'dashboard.empty.title': 'لا يوجد شيء في هذه اللوحة بعد',
  'dashboard.empty.body': 'لوحة المعلومات مجموعة من الأسئلة المحفوظة. أضف أولها، أو ابدأ من قالب جاهز لدورك.',
  'dashboard.widgetFailed': 'تعذّر تحميل هذه الأداة.',
  'dashboard.openReport': 'افتح التقرير كاملاً',
  'dashboard.pick': 'لوحة المعلومات',


  'campaign.title': 'الحملات',
  'campaign.new': 'حملة جديدة',
  'campaign.name': 'الاسم',
  'campaign.channel': 'القناة',
  'campaign.status': 'الحالة',
  'campaign.audience': 'الجمهور',
  'campaign.content': 'المحتوى',
  'campaign.owner': 'المسؤول',
  'campaign.sentAt': 'أُرسلت في',
  'campaign.members': 'الأشخاص',
  'campaign.sent': 'وصلت إلى',
  'campaign.refused': 'لم تُرسل',
  'campaign.opened': 'فُتحت',
  'campaign.clicked': 'نُقرت',
  'campaign.bounced': 'ارتدّت',
  'campaign.performance': 'النتائج',
  'campaign.tab.members': 'الأشخاص',
  'campaign.tab.content': 'المحتوى',
  'campaign.tab.attribution': 'ما نتج عنها',
  'campaign.send': 'أرسلها',
  'campaign.sending': 'جارٍ الإرسال',
  'campaign.reachPreview': 'يمكن الوصول إلى {reachable} من {total}',
  'campaign.whyNot': 'لماذا لن يستلمها الباقون',
  'campaign.refusal.suppressed': 'طلبوا الإيقاف، أو ارتدّ العنوان',
  'campaign.refusal.optedOut': 'رفضوا هذه القناة',
  'campaign.refusal.noConsent': 'لم يُسألوا قط',
  'campaign.refusal.noAddress': 'لا يوجد عنوان مسجّل',
  'campaign.refusalCount': '{count} شخصاً',
  'campaign.confirmSend': 'أرسل إلى {count} شخصاً',
  'campaign.confirmSendBody': 'لا يمكن التراجع بعد الإرسال.',
  'campaign.needsAudience': 'اختر جمهوراً أولاً.',
  'campaign.needsContent': 'اختر ما سترسله أولاً.',
  'campaign.empty.title': 'لا توجد حملات بعد',
  'campaign.empty.body': 'ترسل الحملة محتوى واحداً إلى جمهور واحد، وتسجّل ما حدث مع كل شخص.',
  'campaign.noResults': 'لم يُرسل شيء بعد.',
  'content.title': 'المحتوى',
  'content.email': 'بريد إلكتروني',
  'content.landingPage': 'صفحة هبوط',
  'content.article': 'مقالة',
  'content.subject': 'سطر الموضوع',
  'content.preheader': 'نص المعاينة',
  'content.save': 'احفظ',
  'content.saved': 'تم الحفظ',
  'content.unsaved': 'لم يُحفظ',
  'content.retry': 'أعد محاولة الحفظ',
  'content.blocks': 'الكتل',
  'content.canvas': 'المحتوى',
  'content.settings': 'إعدادات الكتلة',
  'content.preview': 'معاينة',
  'content.preview.desktop': 'سطح المكتب',
  'content.preview.mobile': 'الهاتف',
  'content.preview.dark': 'داكن',
  'content.testSend': 'أرسل لي نسخة تجريبية',
  'content.missingTokens': 'بعض هذه لا قيمة لها لهذا الشخص.',
  'content.missingTokensHelp': 'أعطِ كل واحد قيمة بديلة، حتى لا يُحيّا أحد باسم غير موجود.',
  'content.empty': 'لا يوجد شيء بعد. أضف كتلة للبدء.',
  'content.problems': 'قبل أن يُرسل هذا:',
  'content.selectBlock': 'اختر كتلة لتحريرها',
  'content.moveUp': 'حرّك لأعلى',
  'content.moveDown': 'حرّك لأسفل',
  'content.remove': 'احذف',
  'content.words': 'النص',
  'content.file': 'الملف',
  'content.description': 'الوصف',
  'content.link': 'الرابط',
  'content.align': 'المحاذاة',
  'content.align.start': 'يمين',
  'content.align.center': 'وسط',
  'content.align.end': 'يسار',
  'content.block.text': 'نص',
  'content.block.heading': 'عنوان',
  'content.block.image': 'صورة',
  'content.block.button': 'زر',
  'content.block.divider': 'فاصل',
  'content.block.spacer': 'مسافة',
  'content.block.columns': 'أعمدة',
  'content.block.quote': 'اقتباس',
  'content.block.video': 'فيديو',
  'content.block.unsubscribe': 'تذييل إلغاء الاشتراك',


  'chat.title': 'الدردشة المباشرة',
  'chat.queue.waiting': 'في الانتظار',
  'chat.queue.active': 'نشطة',
  'chat.queue.mine': 'لديّ',
  'chat.waitingFor': 'ينتظر منذ {duration}',
  'chat.noConversations': 'لا توجد محادثات في الانتظار.',
  'chat.widgetStatus': 'أداة الدردشة مفعّلة.',
  'chat.settings': 'إعدادات الدردشة',
  'chat.pick': 'اختر محادثة',
  'chat.reply': 'الرد',
  'chat.send': 'أرسل',
  'chat.claim': 'خذ هذه المحادثة',
  'chat.end': 'أغلق المحادثة',
  'chat.ended': 'أُغلقت هذه المحادثة.',
  'chat.abandoned': 'أغلق الزائر نافذته.',
  'chat.abandonedHelp': 'لن يصلهم شيء بعد الآن هنا. تم الاحتفاظ بمسودتك.',
  'chat.draftKept': 'تم الاحتفاظ بمسودتك.',
  'chat.author.visitor': 'الزائر',
  'chat.author.operator': 'أنت',
  'chat.author.bot': 'روبوت الدردشة',
  'chat.author.system': 'النظام',
  'chat.context': 'عن هذا الزائر',
  'chat.context.location': 'أين هم',
  'chat.context.unknownLocation': 'غير معروف',
  'chat.context.firstSeen': 'أول ظهور',
  'chat.context.match': 'السجل المطابق',
  'chat.context.noMatch': 'لم يُطابق بأحد بعد',
  'chat.context.convert': 'أنشئ عميلاً محتملاً من هذا الزائر',
  'chat.context.pages': 'الصفحات التي شاهدوها',
  'chat.context.noPages': 'لم تُسجَّل أي صفحات.',
  'chat.canned': 'ردود جاهزة',
  'chat.cannedInsert': 'أدرج {shortcut}',


  'portal.poweredBy': 'مُرسل من {workspace}',
  'portal.content': 'هذه الصفحة',
  'portal.requestNewLink': 'أرسل لي رابطاً جديداً',
  'portal.refusal.invalid.title': 'هذا الرابط لا يعمل',
  'portal.refusal.invalid.body': 'تأكد من نسخ العنوان كاملاً من الرسالة التي وصلتك.',
  'portal.refusal.expired.title': 'انتهت صلاحية هذا الرابط',
  'portal.refusal.expired.body': 'تتوقف الروابط عن العمل بعد فترة. يمكننا إرسال رابط جديد لك.',
  'portal.refusal.revoked.title': 'تم سحب هذا الرابط',
  'portal.refusal.revoked.body': 'قام أحدهم في الشركة المُرسِلة بسحبه. تواصل معهم إذا كنت لا تزال بحاجة إلى الوصول.',
  'portal.refusal.wrong-surface.title': 'هذا الرابط لصفحة أخرى',
  'portal.refusal.wrong-surface.body': 'افتحه من الرسالة التي وصلتك بدلاً من تعديل العنوان.',
  'portal.newLinkSent': 'إذا كان هذا العنوان مسجلاً لدينا، فالرابط الجديد في طريقه إليك.',
  'portal.field.full_name': 'الاسم',
  'portal.field.email': 'البريد الإلكتروني',
  'portal.field.title': 'المسمى الوظيفي',
  'portal.field.number': 'المرجع',
  'portal.field.status': 'الحالة',
  'portal.field.total': 'الإجمالي',
  'portal.field.currency': 'العملة',
  'portal.field.valid_until': 'صالح حتى',
  'preferences.title': 'تفضيلاتك',
  'preferences.intro': 'اختر ما تودّ أن تسمع عنه، وبأي وسيلة.',
  'preferences.channel.email': 'البريد الإلكتروني',
  'preferences.channel.sms': 'رسالة نصية',
  'preferences.channel.whatsapp': 'واتساب',
  'preferences.channel.call': 'مكالمة هاتفية',
  'preferences.neverAsked': 'لم يُسأل من قبل',
  'preferences.stopAll': 'أوقف كل شيء',
  'preferences.stopAllHelp': 'نقرة واحدة. سنتوقف عن التواصل معك عبر جميع القنوات.',
  'preferences.stopped': 'لقد توقفنا. لن يُرسل إليك شيء بعد الآن.',
  'preferences.saved': 'تم الحفظ',
  'portal.quotes': 'عروض الأسعار الخاصة بك',
  'portal.noQuotes': 'لا يوجد شيء هنا بعد.',
  'portal.yourDetails': 'بياناتك',
  'booking.title': 'احجز موعداً',
  'booking.intro': 'اختر وقتاً يناسبك.',
  'booking.noSlots': 'لا توجد أوقات متاحة حالياً.',


  'booking.note': 'عمّ تودّ أن نتحدث؟',
  'booking.taken': 'حجز أحدهم ذلك الوقت أثناء اختيارك. اختر وقتاً آخر — ما كتبته لا يزال موجوداً.',
  'booking.confirmed': 'تم الحجز. ستصلك رسالة تأكيد بالبريد الإلكتروني.',


  'agent.title': 'الوكلاء',
  'agent.purpose': 'الغرض منه',
  'agent.new.title': 'وكيل جديد',
  'agent.new.body': 'يحتاج الوكيل إلى اسم وغرض ومفتاح تشير إليه به الأشياء الأخرى.',
  'agent.new.name': 'الاسم',
  'agent.new.key': 'المفتاح',
  'agent.new.keyHelp': 'أحرف صغيرة بلا مسافات. يُستخدم في الأتمتة ولا يمكن تغييره لاحقاً.',
  'agent.new.create': 'إنشاء وكيل',
  'agent.instructions': 'التعليمات',
  'agent.topics': 'المواضيع',
  'agent.actions': 'ما يمكنه فعله',
  'agent.sources': 'ما يمكنه قراءته',
  'comments.title': 'النقاش',
  'record.action.clone': 'ابدأ نسخة',
  'record.clone.name': 'اسم الصفقة الجديدة',
  'record.clone.explain': 'ينتقل الأشخاص وفريق العمل والمنافسون. أما المرحلة وتاريخ الإغلاق والسجل فلا.',

  'qualification.title': 'التأهيل',
  'qualification.answer': 'أجب',
  'qualification.evidence': 'على أي أساس؟',
  'qualification.state.met': 'نعم',
  'qualification.state.not-met': 'لا',
  'qualification.state.refused': 'رفض الإفصاح',
  'qualification.state.unknown': 'لم يُسأل',

  'callQueue.title': 'قائمة الاتصال',
  'callQueue.lists': 'قوائمك',
  'callQueue.progress': '{done} من {total}',
  'callQueue.pick': 'اختر قائمة للعمل عليها.',
  'callQueue.finished': 'انتهت هذه القائمة. لم يبقَ أحد للاتصال به.',
  'callQueue.subject': 'ما يُكتب على الخط الزمني',
  'callQueue.notes': 'أي شيء يستحق الحفظ',
  'callQueue.skip': 'تخطٍّ',
  'callQueue.skipped': 'مُتخطّى',
  'callQueue.called': 'تم الاتصال',
  'callQueue.noNumber': 'لا يوجد رقم هاتف على هذا السجل.',
  'callQueue.empty.title': 'لا توجد قوائم اتصال بعد',
  'callQueue.empty.body': 'ابنِ واحدة من قائمة أشخاص مُرشّحة، وستحتفظ بموضعك أثناء العمل عليها.',

  'record.note.kind': 'نوع القيد',
  'record.note.kind.note': 'ملاحظة',
  'record.note.kind.call': 'مكالمة',
  'record.call.outcome': 'كيف انتهت',
  'record.call.outcome.connected': 'تحدثنا إليهم',
  'record.call.outcome.no-answer': 'لا يوجد رد',
  'record.call.outcome.voicemail': 'تركنا رسالة صوتية',
  'record.call.outcome.wrong-number': 'رقم خاطئ',
  'record.call.outcome.busy': 'مشغول',
  'record.call.outcome.callback-requested': 'طلبوا معاودة الاتصال',
  'record.call.direction': 'الاتجاه',
  'record.call.outbound': 'نحن اتصلنا',
  'record.call.inbound': 'هم اتصلوا',
  'record.call.minutes': 'الدقائق',

  'comments.body': 'بين الزملاء. هذا لا يظهر على الخط الزمني للعميل.',
  'comments.write': 'اطرح سؤالاً، أو استخدم @ لإشراك أحدهم',
  'comments.post': 'نشر',
  'comments.reply': 'رد',
  'comments.resolve': 'تم الرد',
  'comments.reopen': 'إعادة الفتح',
  'comments.edited': 'مُعدّل',
  'comments.somebody': 'أحدهم',
  'comments.none': 'لم يُناقش شيء بعد بخصوص هذا.',

  'agent.prebuilt.title': 'وكلاء جاهزون',
  'agent.prebuilt.body': 'يصل كل واحد منهم متوقفاً، وكل إجراء مضبوط على الاقتراح، وعليه اجتياز اختباراته قبل تشغيله. اقرأ ما يقوله قبل ذلك.',
  'agent.prebuilt.install': 'أضِف إلى مساحة العمل',
  'agent.prebuilt.installed': 'موجود بالفعل',

  'routing.model.title': 'أي نموذج يجيب',
  'routing.model.body': 'تُجرَّب النماذج بالترتيب حتى يجيب أحدها. حساب هذا التثبيت يأتي أخيراً دائماً، فحتى لو فشلت السلسلة كلها يبقى هناك رد.',
  'routing.model.label': 'ما تسمّي به هذا الحساب',
  'routing.model.kind': 'المزوّد',
  'routing.model.baseUrl': 'عنوان النقطة الطرفية (للمخصّص فقط)',
  'routing.model.key': 'مفتاح الواجهة',
  'routing.model.keySet': 'المفتاح محفوظ',
  'routing.model.keyMissing': 'لا يوجد مفتاح',
  'routing.model.add': 'أضف حساباً',
  'routing.model.remove': 'إزالة',
  'routing.model.ours': 'هذا التثبيت',
  'routing.model.default': 'لم يُضبط شيء، لذا يجيب نموذج هذا التثبيت.',
  'routing.model.defaultModel': 'نموذجه الافتراضي',
  'routing.model.addStep': 'أضف إلى السلسلة',
  'routing.model.task.agent': 'الوكلاء',
  'routing.model.task.draft': 'صياغة النصوص',
  'routing.model.task.summarise': 'التلخيص',

  'agent.guardrails': 'الضوابط',
  'agent.tests': 'الاختبارات',
  'agent.source.knowledge': 'مقالات المعرفة المنشورة',
  'agent.source.documents': 'المستندات النصية والمقتطفات في المكتبة',
  'agent.source.record.account': 'الشركات، مع ملاحظاتها',
  'agent.source.record.contact': 'الأشخاص، مع ملاحظاتهم',
  'agent.source.record.deal': 'الصفقات، مع ملاحظاتها',
  'agent.source.record.lead': 'العملاء المحتملون، مع ملاحظاتهم',
  'agent.reindex': 'فهرسة ما تغيّر',
  'agent.index.meaning': 'تمت فهرسة {indexed} وتخطّي {skipped}. المطابقة على المعنى.',
  'agent.index.words': 'تمت فهرسة {indexed} وتخطّي {skipped}. لا يوجد مفتاح تضمينات، لذا المطابقة على الكلمات.',
  'agent.index.none': 'لا شيء مفهرس: لم يتم إعداد مزوّد تضمينات.',
  'agent.preview': 'أظهر ما سيقرأه',
  'agent.preview.ask': 'سؤال قد يطرحه أحدهم عليه',
  'agent.preview.nothing': 'لا شيء في هذه المصادر قريب بما يكفي من هذا السؤال ليستحق الاستشهاد به.',

  'agent.testChat': 'جرّبه',
  'agent.ask': 'اسأل شيئاً',
  'agent.send': 'اسأل',
  'agent.publish': 'شغّله',
  'agent.pause': 'أوقفه مؤقتاً',
  'agent.blocked': 'لا يمكن تشغيله بعد:',
  'agent.runTests': 'شغّل الاختبارات',
  'agent.autonomy': 'مستوى الاستقلالية',
  'agent.autonomy.propose': 'يقترح وينتظر',
  'agent.autonomy.auto': 'يتصرف دون سؤال',
  'agent.autonomy.read-only': 'يقرأ فقط',
  'agent.locked': 'يتطلب {entitlement}',
  'agent.cost': 'كلّف هذا {amount}',
  'agent.tokens': '{input} داخل، {output} خارج',
  'agent.trace': 'كيف وصل إلى ذلك',
  'agent.rawPayload': 'أظهر البيانات الخام',
  'agent.step.thought': 'تفكير',
  'agent.step.retrieval': 'بحث',
  'agent.step.tool-call': 'أراد التصرف',
  'agent.step.answer': 'أجاب',
  'agent.step.refusal': 'رفض',
  'agent.empty.title': 'لا يوجد وكلاء بعد',
  'agent.empty.body': 'يجيب الوكيل عن الأسئلة ويقترح تغييرات. لا يتصرف دون إذن، إلا إذا سمحت له بذلك.',
  'agent.activity': 'نشاط الوكلاء',
  'agent.activity.empty': 'تظهر التشغيلات هنا بمجرد تشغيل وكيل.',
  'agent.spend': 'المصروف حتى الآن',
  'agent.run': 'تشغيل',
  'agent.invokedBy': 'طلبه',
  'agent.outcome': 'النتيجة',
  'proposal.title': 'بانتظارك',
  'proposal.empty.title': 'لا شيء يحتاج مراجعتك',
  'proposal.empty.body': 'يقترح الوكلاء التغييرات هنا عندما تكون مضبوطة على السؤال أولاً.',
  'proposal.settings': 'إعدادات الوكلاء',
  'proposal.why': 'لماذا',
  'proposal.evidence': 'ما اطّلع عليه',
  'proposal.accept': 'قبول',
  'proposal.edit': 'عدّله أولاً',
  'proposal.dismiss': 'تجاهل',
  'proposal.dismissReason': 'لماذا لا؟',
  'proposal.dismissHelp': 'هكذا يتحسّن الوكيل. كلمة «لا» وحدها لا تعلّمه شيئاً.',
  'proposal.save': 'احفظ التعديل',
  'proposal.accepted': 'تم القبول',
  'proposal.edited': 'عُدّل وقُبل',
  'proposal.dismissed': 'تم التجاهل',
  'proposal.failed': 'تعذّر تنفيذ ذلك: {reason}',
  'proposal.stillHere': 'لا يزال موجوداً، فلم يُفقد شيء.',


  'case.title': 'الحالات',
  'case.queue.unassigned': 'غير مُسندة',
  'case.queue.mine': 'لديّ',
  'case.queue.all': 'الكل',
  'case.number': 'المرجع',
  'case.subject': 'الموضوع',
  'case.contact': 'من سأل',
  'case.priority': 'الأولوية',
  'case.status': 'الحالة',
  'case.assignee': 'من يعمل عليها',
  'case.unassigned': 'لا أحد بعد',
  'case.escalated': 'تجاوزت {cases} حالة موعدها النهائي. تم إبلاغ {people} أشخاص.',
  'case.escalated.one': 'تجاوزت حالة واحدة موعدها النهائي. تم إبلاغ {people} أشخاص.',
  'case.escalated.nobody': 'تجاوزت {cases} حالة موعدها النهائي، ولا يوجد من يمكن إبلاغه.',
  'case.sla': 'الوقت المتبقي',
  'case.priority.low': 'منخفضة',
  'case.priority.normal': 'عادية',
  'case.priority.high': 'عالية',
  'case.priority.urgent': 'عاجلة',
  'case.status.new': 'جديدة',
  'case.status.open': 'مفتوحة',
  'case.status.waiting-on-customer': 'بانتظار العميل',
  'case.status.resolved': 'تم الحل',
  'case.status.closed': 'مغلقة',
  'case.sla.breached': 'متأخرة بـ {duration}',
  'case.sla.dueSoon': 'مستحقة في {duration}',
  'case.sla.onTrack': 'بقي {duration}',
  'case.sla.met': 'خلال الموعد',
  'case.sla.none': 'لا موعد محدد',
  'case.sla.firstResponse': 'للرد',
  'case.sla.resolution': 'للحل',
  'case.reply.public': 'رُد على العميل',
  'case.reply.internal': 'أضف ملاحظة داخلية',
  'case.reply.publicHelp': 'سيستلمون هذا.',
  'case.reply.internalHelp': 'فريقك فقط يرى هذا.',
  'case.reply.draft': 'ما تريد قوله',
  'case.reply.sent': 'أُرسل إلى العميل',
  'case.reply.noted': 'سُجّل لفريقك',
  'case.reply.failed': 'لم يُرسل: {reason}',
  'case.reply.draftKept': 'مسودتك لا تزال موجودة.',
  'case.message.public': 'العميل يرى هذا',
  'case.message.internal': 'داخلي فقط',
  'case.author.customer': 'العميل',
  'case.author.agent': 'فريقك',
  'case.author.system': 'النظام',
  'case.articles': 'قد يجيب على هذا',
  'case.articles.internal': 'داخلي',
  'case.articles.none': 'لا شيء يطابق هذا الموضوع.',
  'case.details': 'عن هذه الحالة',
  'case.empty.title': 'لا شيء في قائمة الانتظار',
  'case.empty.body': 'تصل الحالات هنا من البريد والبوابة والدردشة. أعدّ تحويل البريد إلى حالات من الإعدادات للبدء.',
  'case.take': 'خُذها',
  'case.resolve': 'علّمها كمحلولة',


  // What a device kept while it had no network (F-139).
  'offline.title': 'في انتظار الإرسال',
  'offline.indicator.offline': 'غير متصل',
  'offline.indicator.waiting': '{count} في انتظار الإرسال',
  'offline.indicator.needsYou': '{count} بحاجة إليك',
  'offline.indicator.sending': 'جارٍ الإرسال',
  'offline.indicator.allSent': 'تم إرسال كل شيء',
  'offline.open': 'عرض ما هو في الانتظار',
  'offline.empty.title': 'لا شيء في الانتظار',
  'offline.empty.body': 'كل ما غيّرته موجود في مساحة العمل. أي عمل تقوم به دون اتصال سينتظر هنا حتى يتوفر الاتصال.',
  'offline.kept': 'محفوظ على هذا الجهاز',
  'offline.keptBody': 'لا يوجد اتصال. هذا على جهازك ولا يمكن لأحد غيرك رؤيته بعد.',
  'offline.state.waiting': 'في انتظار الاتصال',
  'offline.state.needsYou': 'بحاجة إليك',
  'offline.state.refused': 'تعذّر الإرسال',
  'offline.queuedAt': 'حُفظ {when}',
  'offline.sendNow': 'حاول الآن',
  'offline.discard': 'تجاهل هذا',
  'offline.discard.confirm': 'هل تريد تجاهل ما كتبته؟ لم يُرسل ولا يمكن استرجاعه.',
  'offline.conflict.title': 'تغيّر {field} أثناء الانتظار',
  'offline.conflict.body': 'كتبت أنت شيئًا وكتب شخص آخر شيئًا آخر. اختر ما تحتفظ به مساحة العمل.',
  'offline.conflict.yours': 'ما كتبته',
  'offline.conflict.theirs': 'ما كتبه غيرك',
  'offline.conflict.keepYours': 'احتفظ بما كتبته',
  'offline.conflict.keepTheirs': 'احتفظ بما كتبه غيرك',
  'offline.onRecord': 'على {record}',
  'offline.refusedBecause': 'رفضته مساحة العمل: {reason}',

  'time.days': '{count} ي',
  'time.hours': '{count} س',
  'time.minutes': '{count} د',

  // --- targets and attainment (F-053) ---------------------------------------
  'target.title': 'الأهداف',
  'target.body': 'ما يُقاس به كل شخص، وما أغلقه فعلاً. الصفقة المقسومة تُحتسب بحسب حصصها، والصفقة غير المقسومة تُحتسب كاملة لمالكها.',
  'target.year': 'السنة المالية',
  'target.by': 'القياس حسب',
  'target.subject.user': 'الأشخاص',
  'target.subject.team': 'الفرق',
  'target.period.month': 'الشهر {index}',
  'target.period.quarter': 'الربع {index}',
  'target.period.year': 'السنة كاملة',
  'target.period.custom': 'من {start} إلى {end}',
  'target.column.target': 'الهدف',
  'target.column.attained': 'المُغلق',
  'target.attainment': '{percent}% من الهدف',
  'target.noTarget': 'لا يوجد هدف متفق عليه',
  'target.deals': '{count} صفقة',
  'target.set': 'حفظ',
  'target.remove': 'إزالة الهدف',
  'target.fiscalStart': 'تبدأ السنة في',
  'target.fiscalStart.body': 'تُحتسب الأرباع من هذا الشهر. السنة التي تبدأ في أبريل تجعل أبريل إلى يونيو هو الربع الأول.',
  'target.paste.title': 'لصق من جدول بيانات',
  'target.paste.body': 'صف لكل شخص: الاسم ثم مبلغ لكل عمود، مفصولة بعلامات جدولة. لا يُحفظ شيء حتى يطابق كل صف شخصاً.',
  'target.paste.apply': 'تطبيق اللصق',
  'target.paste.applied': 'تم ضبط {count} هدف',
  'target.unattributed': '{amount} أغلقها من ليس في هذا الجدول: {count} صفقة بلا مالك وبلا تقسيم. غير مُدرجة في أي صف أعلاه.',
  'target.unconvertible': 'المبالغ بعملة {currencies} ليست ضمن هذه الأرقام. لا يوجد سعر صرف يغطيها.',
  'target.overlapping': '{count} أشخاص ينتمون إلى أكثر من فريق من هذه الفرق، لذا تزيد صفوف الفرق عن إجمالي ما أغلقته المنشأة.',
  'target.strays': 'أهداف متفق عليها لفترات لم تعد ضمن هذه السنة المالية',
  'target.scope': 'الأرقام تغطي الصفقات التي يمكنك رؤيتها.',
  'target.empty.title': 'لا توجد أهداف بعد',
  'target.empty.body': 'الهدف رقم يُقاس به شخص خلال فترة. اضبط هدفاً ليظهر الإنجاز بجانبه.',
  'refusal.period.startMonth': 'تبدأ السنة المالية في أحد الأشهر الاثني عشر، و{month} ليس منها.',
  'refusal.period.date': 'هذا ليس تاريخاً.',
  'refusal.target.periodKind': 'يغطي الهدف شهراً أو ربعاً أو سنة.',
  'refusal.target.periodOrder': 'تنتهي هذه الفترة قبل أن تبدأ.',
  'refusal.target.negative': 'الهدف مبلغ يُراد بلوغه، و{amount} أقل من الصفر.',
  'noun.target': 'هذا الهدف',
  'noun.team': 'هذا الفريق',
  'target.periodKind': 'الفترات',
  'target.periodKind.month': 'شهرية',
  'target.periodKind.quarter': 'ربع سنوية',
  'target.periodKind.year': 'سنوية',
  'target.measure': 'القياس بـ',
  'target.measure.amount': 'المال',
  'target.measure.count': 'الصفقات المُغلقة',
  'target.dealsClosed': 'أُغلقت {count}',
  'target.targetDeals': '{count} صفقة',
  'target.sharedDeals': '{count} صفقة وصلت إلى أكثر من صف، لأن الصفقة المقسومة تُحتسب صفقة كاملة لكل من عمل عليها. لا تُجمع أعداد الصفقات رأسياً.',
  'refusal.target.measure': 'يُقاس الهدف بالمال أو بعدد الصفقات.',
  'refusal.target.currencyMissing': 'الهدف بالمال يحتاج إلى عملة.',
  'refusal.target.wholeCount': 'الهدف بعدد الصفقات رقم صحيح، و{amount} ليس كذلك.',
  // --- the forecast (F-052) --------------------------------------------------
  'forecast.title': 'التوقعات',
  'forecast.body': 'ما يُتوقع إغلاقه، بجانب ما يشير إليه خط الأنابيب وما أُغلق فعلاً. الرقمان لا يُجمعان أبداً: أحدهما حساب، والآخر وعد قطعه شخص.',
  'forecast.category.commit': 'ملتزم به',
  'forecast.category.best-case': 'أفضل الحالات',
  'forecast.category.pipeline': 'خط الأنابيب',
  'forecast.category.omitted': 'مستبعد',
  'forecast.commitAndBestCase': 'الملتزم به وأفضل الحالات',
  'forecast.weighted': 'المرجّح',
  'forecast.weighted.hint': 'كل صفقة مفتوحة مضروبة في احتمال مرحلتها. الصفقات المستبعدة خارج الحساب.',
  'forecast.closed': 'المُغلق',
  'forecast.target': 'الهدف',
  'forecast.variance.over': '{amount} فوق الهدف',
  'forecast.variance.under': '{amount} دون الهدف',
  'forecast.variance.none': 'لا يوجد هدف متفق عليه',
  'forecast.open': 'إظهار الصفقات',
  'forecast.close': 'إخفاء الصفقات',
  'forecast.deal.split': '{percent}% من {amount}',
  'forecast.deal.whole': 'الصفقة كاملة',
  'forecast.undated': '{amount} من الصفقات المفتوحة بلا تاريخ إغلاق، فهي خارج كل فترة وخارج هذه الأرقام. {count} صفقة.',
  'forecast.unattributed': '{amount} من الصفقات المفتوحة بلا مالك وبلا تقسيم، فهي ليست في أي صف هنا. {count} صفقة.',
  'forecast.unconvertible': 'الصفقات المفتوحة بعملة {currencies} ليست ضمن هذه الأرقام. لا يوجد سعر صرف يغطيها.',
  'forecast.drilldown.short': '{count} صفقة في هذه الخانة تعذّر تحويلها وليست مدرجة.',
  'forecast.noDeals': 'لا توجد صفقات مفتوحة في هذه الفترة.',
  'forecast.empty.title': 'لا شيء للتوقع بعد',
  'forecast.empty.body': 'تقرأ التوقعات الصفقات المفتوحة مقابل الأهداف المتفق عليها. اضبط الأهداف أولاً، وخط الأنابيب يكمل الباقي.',
  'forecast.setTargets': 'ضبط الأهداف',
  'forecast.noTargets': 'لا أحد في هذا الجدول لديه هدف متفق عليه لهذه السنة، فلا يوجد ما تُقرأ التوقعات مقابله.',
  'forecast.scope': 'الأرقام تغطي الصفقات التي يمكنك رؤيتها.',
  'refusal.forecast.category': 'فئة التوقع هي ملتزم به أو أفضل الحالات أو خط الأنابيب أو مستبعد.',
  'refusal.forecast.closed': 'أُغلقت هذه الصفقة، والصفقة المغلقة نتيجة وليست توقعاً.',
  'forecast.submit.title': 'رقمك لهذه الفترة',
  'forecast.submit.body': 'ما تخبر مديرك بأنه سيُغلق. يُحفظ منفصلاً عما يعدّله إليه، ليتسنى مقارنة الرقمين لاحقاً.',
  'forecast.submit.amount': 'المبلغ',
  'forecast.submit.note': 'ما ينبغي أن يعرفوه',
  'forecast.submit.action': 'إرسال',
  'forecast.submit.resubmit': 'تحديث',
  'forecast.submitted': 'أُرسل {when}',
  'forecast.submit.none': 'لم يُرسل بعد',
  'forecast.adjusted.by': 'عُدّل إلى {amount} بواسطة {name}',
  'forecast.adjust.title': 'تعديل',
  'forecast.adjust.note': 'السبب (مطلوب)',
  'forecast.adjust.action': 'تعديل',
  'forecast.adjust.clear': 'إزالة التعديل',
  'forecast.rollup.title': 'توقعات فريقك',
  'forecast.rollup.total': 'الإجمالي المجمّع',
  'forecast.rollup.awaiting': '{count} أشخاص لم يرسلوا، لذا لا يغطيهم هذا الإجمالي.',
  'forecast.rollup.complete': 'أرسل الجميع.',
  'forecast.rollup.notSubmitted': 'لم يُرسل',
  'refusal.forecast.noActor': 'التوقع يرسله شخص.',
  'refusal.forecast.negative': 'التوقع مبلغ يُتوقع إغلاقه، و{amount} أقل من الصفر.',
  'refusal.forecast.adjustSelf': 'تعديل توقعك هو إرساله من جديد. غيّر الرقم بدلاً من ذلك.',
  'refusal.forecast.adjustReason': 'التعديل يحتاج إلى سبب. سيُبلَّغ شخص بأن رقمه قد تغيّر.',
  'refusal.forecast.nothingToAdjust': 'لم يرسل هذا الشخص توقعاً لهذه الفترة.',
  'refusal.forecast.noAdjustment': 'لا يوجد تعديل على هذا التوقع لإزالته.',
  'refusal.forecast.notMine': '{name} لا يتبعك في التسلسل الإداري.',
  'accuracy.title': 'التوقع مقابل الفعلي',
  'accuracy.body': 'ما تم توقعه كل أسبوع، بجانب ما أُغلق فعلاً في الفترة. السجل يبدأ حين يبدأ: الأسبوع الذي لم يُلتقط ليس أسبوع أصفار.',
  'accuracy.week': 'أسبوع {date}',
  'accuracy.forecast': 'التوقع',
  'accuracy.actual': 'الفعلي',
  'accuracy.error.over': 'زيادة بمقدار {amount}',
  'accuracy.error.under': 'نقص بمقدار {amount}',
  'accuracy.error.none': 'لم يُتوقع ذلك الأسبوع',
  'accuracy.partial': 'لم تنتهِ هذه الفترة بعد، لذا تُقارن كل التوقعات بما أُغلق حتى الآن. من المتوقع أن تبدو جميعها متفائلة.',
  'accuracy.gaps': '{count} أسابيع في هذه الفترة لم تُلتقط قط. هي مفقودة من السجل، وليست أصفاراً.',
  'accuracy.empty': 'لم يُلتقط شيء لهذه الفترة بعد. يبدأ السجل أول مرة يفتح فيها أحدهم هذه الصفحة.',
  'accuracy.pipelineSaid': 'خط الأنابيب قال {amount}',
  // --- the funnel (F-100) ----------------------------------------------------
  'funnel.title': 'أين يتسرب خط الأنابيب',
  'funnel.body': 'من الصفقات التي دخلت كل مرحلة، كم منها تقدّم. ليس كم يجلس فيها الآن: المرحلة التي يعبرها الجميع في يوم تبدو فارغة، ومن ثم تبدو معطلة.',
  'funnel.entered': 'دخلت {count}',
  'funnel.advanced': 'تقدّمت {count}',
  'funnel.conversion': 'تقدّم {percent}%',
  'funnel.conversion.none': 'لم يدخل أحد هذه المرحلة',
  'funnel.conversion.terminal': 'لا يوجد ما يليها',
  'funnel.days': '{days} يوماً في المتوسط',
  'funnel.days.none': 'لم تغادر أي صفقة هذه المرحلة بعد',
  'funnel.stillHere': '{count} ما زالت هنا، وليست ضمن المتوسط',
  'funnel.since': 'يبدأ السجل في {date}.',
  'funnel.beforeHistory': 'تمتد هذه النافذة إلى ما قبل أي شيء مسجّل. لا يمكن احتساب المراحل إلا من {date}، فما يظهر أدناه ليس الفترة كاملة.',
  'funnel.noHistory': 'لم يُسجّل شيء بعد. يبدأ السجل أول مرة تُنشأ فيها صفقة أو تُنقل.',
  'funnel.window.90': 'آخر 90 يوماً',
  'funnel.window.365': 'آخر سنة',
  'funnel.window.all': 'كل ما هو مسجّل',
  'noun.pipeline': 'خط الأنابيب هذا',
  // --- pipeline and stage editor (F-042) -------------------------------------
  'pipelines.title': 'خطوط الأنابيب والمراحل',
  'pipelines.body': 'ما تمر به الصفقة في طريقها إلى الفوز، وقيمة كل خطوة. كل خط أنابيب يحتفظ بطريقة للفوز وطريقة للخسارة.',
  'pipelines.new': 'خط أنابيب جديد',
  'pipelines.name': 'الاسم',
  'pipelines.makeDefault': 'افتح هذا افتراضياً',
  'pipelines.default': 'افتراضي',
  'pipelines.remove': 'إزالة خط الأنابيب',
  'pipelines.stage.new': 'إضافة مرحلة',
  'pipelines.stage.name': 'اسم المرحلة',
  'pipelines.stage.probability': 'الاحتمال',
  'pipelines.stage.outcome': 'الوصول إليها يعني',
  'pipelines.outcome.open': 'ما زالت مفتوحة',
  'pipelines.outcome.won': 'مكسوبة',
  'pipelines.outcome.lost': 'مخسورة',
  'pipelines.stage.deals': '{count} صفقة هنا',
  'pipelines.stage.empty': 'لا شيء هنا',
  'pipelines.stage.up': 'تقديم',
  'pipelines.stage.down': 'تأخير',
  'pipelines.stage.remove': 'إزالة المرحلة',
  'pipelines.save': 'حفظ',
  'refusal.pipeline.noName': 'هذا يحتاج إلى اسم.',
  'refusal.pipeline.last': 'هذا هو خط الأنابيب الوحيد. المنشأة بلا خط أنابيب لا يمكنها الاحتفاظ بصفقة.',
  'refusal.pipeline.hasDeals': '{count} صفقة في خط الأنابيب هذا. انقلها أولاً.',
  'refusal.stage.probability': 'الاحتمال نسبة صحيحة من 0 إلى 100، و{value} ليست كذلك.',
  'refusal.stage.outcome': 'الوصول إلى مرحلة يعني أن الصفقة مفتوحة أو مكسوبة أو مخسورة.',
  'refusal.stage.lastWon': 'هذه هي المرحلة الوحيدة التي تكسب الصفقة.',
  'refusal.stage.lastLost': 'هذه هي المرحلة الوحيدة التي تخسر الصفقة.',
  'refusal.stage.hasDeals': '{count} صفقة في هذه المرحلة. انقلها أولاً.',
  'refusal.stage.partialOrder': 'اذكر كل المراحل الـ{count}، مرة واحدة لكل منها.',
  // --- capture forms (F-036) -------------------------------------------------
  'form.fields': 'ما يجمعه هذا النموذج',
  'form.fields.body': 'يحتاج النموذج إلى حقل بريد أو هاتف، وإلا تعذّرت متابعة من يملؤه.',
  'form.field.add': 'إضافة حقل',
  'form.field.remove': 'إزالة',
  'form.field.required': 'مطلوب',
  'form.consent': 'اطلب الموافقة',
  'form.consent.text': 'ما يوافقون عليه',
  'form.save': 'حفظ النموذج',
  'form.saved': 'تم الحفظ',
  'form.received': 'ما استلمه هذا النموذج',
  'form.received.created': '{count} أصبحت عملاء محتملين',
  'form.received.spam': '{count} رُفضت كرسائل آلية',
  'form.received.noConsent': '{count} لم توافق',
  'form.received.invalid': '{count} كانت ناقصة',
  'form.received.none': 'لا شيء بعد.',
  'form.notConfigured': 'لا توجد حقول في هذا النموذج بعد، لذا لا يجمع شيئاً.',
  'noun.form': 'هذا النموذج',
  'refusal.form.notAForm': 'هذا التضمين ليس نموذج التقاط.',
  'refusal.form.noFields': 'يحتاج النموذج إلى حقل واحد على الأقل.',
  'refusal.form.unknownField': 'لا يمكن لنموذج الالتقاط جمع {field}.',
  'refusal.form.noReach': 'يحتاج نموذج الالتقاط إلى حقل بريد أو هاتف.',
  'refusal.form.noConsentText': 'تحتاج الموافقة إلى الجملة التي يوافق عليها الشخص.',
  'refusal.form.consent': 'يرجى الموافقة قبل الإرسال.',
  'refusal.form.required': '{field} مطلوب.',
  'refusal.form.badToken': 'رابط النموذج هذا غير صالح أو انتهت صلاحيته.',
  'refusal.form.wrongWidget': 'لم يُنشأ هذا الرابط لنموذج التقاط.',
  // --- lead routing (F-034) --------------------------------------------------
  'routing.title': 'إلى من يذهب العميل المحتمل الجديد',
  'routing.body': 'تُجرَّب القواعد بالترتيب وتفوز أول قاعدة مطابقة. العميل الذي لا يطابق شيئاً يبقى غير مُسند بدلاً من أن يذهب لأول من يصادف.',
  'routing.rule.name': 'اسم القاعدة',
  'routing.rule.add': 'إضافة قاعدة',
  'routing.strategy': 'أسندها إلى',
  'routing.strategy.round-robin': 'كل بدوره',
  'routing.strategy.load-balanced': 'من لديه أقل انتظاراً',
  'routing.strategy.named': 'شخص محدد',
  'routing.team': 'من الفريق',
  'routing.team.any': 'أي شخص في المنشأة',
  'routing.person': 'الشخص',
  'routing.pool': '{count} أشخاص يمكنها الوصول إليهم',
  'routing.pool.empty': 'هذه القاعدة لا تصل إلى أحد. ضع شخصاً في الفريق، وإلا ستطابق ولا تُسند شيئاً.',
  'routing.up': 'جرّبها أولاً',
  'routing.down': 'جرّبها لاحقاً',
  'routing.remove': 'إزالة القاعدة',
  'routing.off': 'موقوفة',
  'routing.empty': 'لا توجد قواعد بعد، لذا يصل كل عميل محتمل غير مُسند.',
  'routing.simulate': 'اختبر بعميل محتمل',
  'routing.simulate.matched': '{rule} ستعطيها إلى {owner}.',
  'routing.simulate.noRule': 'لا توجد قاعدة تطابق هذا العميل، لذا سيبقى غير مُسند.',
  'routing.simulate.poolEmpty': '{rule} تطابق ولا تصل إلى أحد. سيبقى غير مُسند.',
  'routing.noTerritory': 'التوجيه حسب المنطقة غير مبني بعد، لذا لا يُعرض هنا.',
  'noun.routingRule': 'هذه القاعدة',
  'refusal.routing.noName': 'قاعدة التوجيه تحتاج إلى اسم.',
  'refusal.routing.strategy': 'ليست هذه طريقة يوجّه بها هذا المنتج عميلاً محتملاً.',
  'refusal.routing.noPerson': 'القاعدة التي تسمّي شخصاً عليها أن تسمّيه.',
  'refusal.routing.badCondition': 'هذا الشرط لا يعمل على عميل محتمل: {problems}',
  'refusal.routing.partialOrder': 'اذكر كل القواعد الـ{count}، مرة واحدة لكل منها.',
  'routing.respond': 'الرد خلال (دقائق)',
  'routing.respond.none': 'بلا مؤقّت',
  'routing.respond.set': '{count} دقيقة عمل للرد',
  'notify.lead.unworked': 'عميل محتمل أُسند إليّ ولم يُرد عليه',
  'note.mentioned': 'أُبلغ {names}',
  'note.mentioned.none': 'لم يُذكر أحد.',
  // --- discount approval (F-049) ---------------------------------------------
  'approval.title': 'اعتماد الخصم',
  'approval.pending': 'بانتظار اعتماد خصم {percent}%، وهو فوق حد {cap}%.',
  'approval.pending.by': 'طلبه {name}.',
  'approval.approved': 'اعتمد {name} خصماً بنسبة {percent}%.',
  'approval.rejected': 'لم يعتمد {name}: {comment}',
  'approval.approve': 'اعتماد',
  'approval.reject': 'رفض',
  'approval.reason': 'السبب',
  'approval.own': 'أنت من طلب هذا، فعلى شخص آخر الموافقة عليه.',
  'approval.cap': 'الخصومات فوق',
  'approval.cap.none': 'بلا حد: لا يحتاج أي خصم إلى اعتماد',
  'approval.cap.set': 'فوق {percent}%، يجب أن يوافق شخص ما',
  'approval.cap.save': 'حفظ',
  'noun.approval': 'هذا الطلب',
  'refusal.approval.cap': 'حد الخصم نسبة من 0 إلى 100، و{value} ليست كذلك.',
  'refusal.approval.noActor': 'القرار يتخذه شخص.',
  'refusal.approval.own': 'أنت من طلب هذا الخصم، فعلى شخص آخر الموافقة عليه.',
  'refusal.approval.notYours': 'اتخاذ قرار في الخصم ليس من مهام هذا الدور.',
  'refusal.approval.rejectReason': 'اذكر السبب ليعرف من طلب ما ينبغي تغييره.',
  'refusal.approval.decided': '{name} سبق أن {status} هذا الطلب.',
  'refusal.approval.decidedRace': 'قرر شخص آخر هذا الطلب قبلك.',
  'refusal.quote.awaitingApproval': '{number} بانتظار اعتماد خصم {percent}%.',
  // --- terms and renewals (F-051) --------------------------------------------
  'term.title': 'المدة',
  'term.openEnded': 'مفتوحة — تستمر حتى يوقفها أحد.',
  'term.from': 'من {start}',
  'term.until': 'حتى {end}',
  'term.countdown': 'تتجدد خلال {days} يوماً',
  'term.countdown.ends': 'تنتهي خلال {days} يوماً',
  'term.overdue': 'مرّ على تاريخ الانتهاء {days} يوماً.',
  'term.ended': 'انتهت.',
  'term.autoRenews': 'تتجدد تلقائياً',
  'term.manual': 'لا تتجدد من تلقاء نفسها',
  'term.history': 'المدد حتى الآن',
  'term.history.first': 'المدة الأولى',
  'term.history.renewal': 'تجديد',
  'term.set': 'ضبط المدة',
  'term.endsOn': 'تنتهي في',
  'term.months': 'المدة التالية (أشهر)',
  'term.notice': 'التنبيه قبل هذا العدد من الأيام',
  'term.save': 'حفظ',
  'noun.subscription': 'هذا الاشتراك',
  'refusal.term.openEnded': 'الاتفاقية المفتوحة ليس فيها ما يُجدَّد.',
  'refusal.term.noLength': 'التجديد يحتاج أن يعرف مدة الفترة التالية.',
  // --- playbooks and call scripts (F-056) ------------------------------------
  'playbook.title': 'دليل مرحلة {stage}',
  'playbook.questions': 'اسأل',
  'playbook.objections': 'إذا قالوا',
  'playbook.response': 'قل',
  'playbook.question.add': 'إضافة سؤال',
  'playbook.objection.add': 'إضافة اعتراض',
  'playbook.remove': 'إزالة',
  'playbook.save': 'حفظ الدليل',
  'playbook.empty': 'لم يُكتب شيء لهذه المرحلة بعد.',
  'playbook.edit': 'الدليل',
  'playbook.guidance': 'ماذا تعني هذه المرحلة',
  'playbook.exit': 'قبل أن تغادر الصفقة',
  'playbook.exit.add': 'إضافة شرط',
  'playbook.ramp': 'يُفتح تلقائياً لمن هم في أول',
  'playbook.ramp.days': 'يوم',
  'playbook.ramp.off': 'لا يُفتح تلقائياً أبداً.',
  'playbook.show': 'إظهار الدليل',
  'playbook.hide': 'إخفاء الدليل',
  'refusal.playbook.noResponse': '"{objection}" ليس له ما يُقال رداً عليه.',
  'refusal.playbook.noObjection': 'الإجابة تحتاج إلى الاعتراض الذي تجيب عنه.',
  'refusal.playbook.rampDays': 'مدة التهيئة عدد صحيح من الأيام، من 0 إلى 3650.',
  // --- support access and the visit banner (F-144) ---------------------------
  'visit.who': '{staff} من الدعم مسجّل الدخول باسمك.',
  'visit.reason': 'السبب المذكور: {reason}',
  'visit.end': 'إنهاء هذه الزيارة',
  'support.title': 'وصول الدعم',
  'support.body': 'لا يمكن لأحد من الدعم فتح مساحة العمل هذه ما لم تسمح له، وحتى الوقت الذي تحدده فقط.',
  'support.state.never': 'لم يُسمح للدعم بالدخول قط.',
  'support.state.open': 'يمكن للدعم الدخول حتى {when}.',
  'support.state.withdrawn': 'سُحب وصول الدعم في {when}.',
  'support.state.lapsed': 'انتهى وصول الدعم في {when}.',
  'support.hours': 'ساعات',
  'support.let': 'السماح للدعم بالدخول',
  'support.withdraw': 'سحب الوصول',
  'support.visits': 'الزيارات',
  'support.visits.none': 'لم يدخل أحد من الدعم.',
  'support.visit.ongoing': 'جارية',
  'refusal.platform.capability': 'حساب وحدة التحكم لديك لا يملك {capability}.',
  'refusal.support.hours': 'اسمح للدعم بالدخول لعدد صحيح من الساعات، من 1 إلى 720.',
  'refusal.support.reason': 'اذكر سبب دخولك في جملة يمكن لغيرك قراءتها لاحقاً.',
  'refusal.support.neverAsked': 'لم يسمح أحد في {workspace} للدعم بالدخول.',
  'refusal.support.withdrawn': 'سحبت {workspace} وصول الدعم.',
  'refusal.support.lapsed': 'انتهى وصول الدعم إلى {workspace}. اطلب منهم تمديده.',
  'refusal.support.notActive': '{name} ليس حساباً نشطاً.',
  'noun.supportGrant': 'تصريح دعم مفتوح',
  'refusal.platform.noSession': 'تعذّر إنشاء جلسة الموظف.',
  'refusal.platform.noWorkspace': 'لا توجد مساحة عمل بهذا المعرّف.',
  'refusal.platform.staffEmail': 'حساب الموظف يحتاج إلى بريد إلكتروني.',
  'refusal.platform.staffPassword': 'كلمة مرور الموظف 12 حرفاً على الأقل.',
  'refusal.platform.strayCapability': 'حساب الموظف لا يملك إلا صلاحيات المنصة. {stray} ليست منها.',
  'refusal.platform.staffNotCreated': 'لم يُنشأ حساب الموظف.',
  'refusal.platform.noStaff': 'لا يوجد حساب موظف نشط بهذا العنوان.',
  'refusal.support.openTreatedAsRefusal': 'عُومل تصريح مفتوح على أنه رفض.',
  'noun.workspace': 'تلك مساحة العمل',
  'refusal.platform.signIn': 'سجّل الدخول إلى وحدة التحكم للمتابعة.',
  'refusal.platform.badRoute': 'يجب أن يحمل مسار وحدة التحكم إذن منصة.',
  'refusal.platform.wrongPath': 'وصل مسار وحدة تحكم إلى مسار مساحة العمل.',
  // --- backups and restore (F-145) -------------------------------------------
  'backup.title': 'النسخ الاحتياطي والاستعادة',
  'backup.last': 'آخر نسخة ناجحة: {when}.',
  'backup.never': 'لم تُؤخذ أي نسخة احتياطية لمساحة العمل هذه.',
  'backup.failed': 'فشلت المحاولة في {when}: {why}',
  'backup.storedIn': 'تُحفظ الأرشيفات في {where}.',
  'backup.run': 'انسخ الآن',
  'backup.retention': 'احتفظ بالنسخ لمدة',
  'backup.days': 'يوم',
  'backup.list': 'النسخ الاحتياطية',
  'backup.none': 'لا شيء هنا بعد.',
  'backup.rows': 'صف',
  'backup.gone': 'الأرشيف غير موجود',
  'backup.verify': 'تحقق من إمكانية قراءتها',
  'backup.restore': 'استعد من هذه',
  'backup.scope': 'استعادة',
  'backup.scope.record': 'سجل واحد',
  'backup.scope.object': 'جدول واحد',
  'backup.scope.workspace': 'مساحة العمل كاملة',
  'backup.table': 'الجدول',
  'backup.record': 'معرّف السجل',
  'backup.preview': 'استعادة {covers} ستعيد {missing} وتستبدل {changed}.',
  'backup.preview.note': 'كل ما أُنشئ بعد النسخة يبقى كما هو.',
  'backup.commit': 'نفّذ',
  'backup.cancel': 'اتركها',
  'refusal.backup.alreadyRunning': 'هناك نسخة احتياطية لمساحة العمل هذه قيد التشغيل بالفعل.',
  'refusal.backup.retention': 'احتفظ بالنسخ لعدد صحيح من الأيام، من 1 إلى 3650.',
  'refusal.backup.archiveMissing': 'النسخة مسجّلة لكن أرشيفها غير موجود.',
  'refusal.restore.unknownTable': '{table} ليس جدولاً تنسخه مساحة العمل هذه.',
  'refusal.restore.compositeRecord': 'ليس لصف {table} معرّف سجل واحد. استعد الجدول كاملاً بدلاً من ذلك.',
  'refusal.restore.notInBackup': 'هذا السجل ليس في هذه النسخة.',
  'refusal.restore.cancelled': 'أُلغيت هذه الاستعادة.',
  'refusal.restore.alreadyCommitted': 'نُفّذت هذه الاستعادة بالفعل.',
  'refusal.restore.moved': 'تغيّرت مساحة العمل منذ المعاينة. راجعها مرة أخرى.',
  'noun.backup': 'تلك النسخة',
  'noun.restore': 'تلك الاستعادة',
  'refusal.restore.noTable': 'حدّد الجدول الذي تستعيد منه.',
  'refusal.restore.noRecord': 'حدّد السجل الذي تستعيده.',
  // --- location from an address (F-152) --------------------------------------
  'geo.estimate': 'تقدير من العنوان بواسطة {source}',
  'geo.sandbox': 'تقدير تجريبي، وليس بحثاً حقيقياً',
  'geo.unrecorded': 'سُجّل قبل أن يُذكر مصدره',
  'geo.private': 'جاء من داخل شبكة، فلا مكان يُحدَّد',
  'geo.title': 'الموقع من العنوان',
  'geo.body': 'تُقدَّر الدولة والمنطقة والمدينة من عنوان الزائر عند الالتقاط. دقة المدينة ضعيفة.',
  'geo.retention': 'احتفظ بالعنوان الخام لمدة',
  'geo.days': 'يوم',
  'geo.dropNow': 'احذف العناوين المنتهية الآن',
  'geo.provider.sandbox': 'لا يوجد مزوّد مُهيّأ. المواقع تقديرات تجريبية وتقول ذلك.',
  'geo.provider.live': 'يُحلّ بواسطة {source}.',
  'geo.provider.none': 'لا شيء مُهيّأ لتحليل العناوين.',
  'refusal.geo.noAddress': 'لا يوجد عنوان لتحليله.',
  'refusal.geo.notLocated': 'هذا لا يحمل موقعاً.',
  'refusal.geo.retention': 'احتفظ بالعناوين لعدد صحيح من الأيام، من 0 إلى 3650.',
  'refusal.funnel.noWindow': 'حدّد إلى أي مدى تنظر للخلف: إما بالأيام أو بتاريخ بداية.',
  // --- provider modes (part 3) -----------------------------------------------
  'provider.allSandbox': '{count} مزوّد في الوضع التجريبي: تُنشأ الرسائل وطلبات النموذج وتُحفظ هنا بدل إرسالها.',
  'provider.someMissing': '{sandbox} مزوّد في الوضع التجريبي، و{missing} غير مُهيّأ إطلاقاً.',
  'provider.sandbox': 'تجريبي — يُنشأ ويُحفظ هنا، ولا يُرسل',
  'provider.notConfigured': 'غير مُهيّأ — لا يُرسل شيء، ولا يُدّعى غير ذلك',
  'provider.show': 'أيّها؟',
  'provider.hide': 'إخفاء',
  'provider.title': 'المزوّدون',
  'provider.body': 'ما تستطيع هذه النسخة الوصول إليه فعلاً. الوضع التجريبي وضع عامل: تُنشأ الرسائل وتُتحقّق وتُحفظ هنا، وكل شاشة تعرض واحدة تقول ذلك.',
  'provider.mode.live': 'مباشر',
  // --- prospecting and enrichment (F-038, F-154) ------------------------------
  'mining.title': 'ابحث عن شركات جديدة',
  'mining.body': 'ابحث لدى مزوّد بيانات عن شركات لا يعرفها أحد هنا بعد. البحث مجاني؛ تدفع عندما تصبح الشركة عميلاً محتملاً.',
  'mining.balance': 'بقي {balance} رصيد.',
  'mining.industry': 'القطاع',
  'mining.geography': 'المكان',
  'mining.role': 'الدور',
  'mining.look': 'ابحث (مجاناً)',
  'mining.found': 'وُجد {found}، منها {known} موجودة هنا بالفعل.',
  'mining.already': 'موجودة هنا كـ {label}',
  'mining.cost': 'استيراد هذه يكلّف {count} من رصيدك البالغ {balance}.',
  'mining.import': 'استورد المحدّدة',
  'enrich.title': 'أكمل المعروف',
  'enrich.applied': 'أُكمل {fields}.',
  'enrich.differs': '{field}: هنا "{ours}"، والمزوّد يقول "{theirs}".',
  'enrich.nothing': 'لا جديد لإكماله.',
  'enrich.cached': 'من بحث مدفوع مسبقاً.',
  'refusal.credits.none': 'لم يبق رصيد إثراء في مساحة العمل هذه.',
  'refusal.credits.notEnough': 'هذا يكلّف {cost} رصيداً والمتبقي {balance}. اختر عدداً أقل أو أضف رصيداً.',
  'refusal.mining.noCriteria': 'حدّد ما تبحث عنه: قطاع أو مكان أو دور.',
  'refusal.mining.nothingSelected': 'اختر شركة واحدة على الأقل للاستيراد.',
  'refusal.enrich.noDomain': 'لا يوجد نطاق موقع لـ {name} للبحث عنه.',
  'refusal.enrich.notConfigured': 'لا يوجد مزوّد بيانات شركات مُهيّأ، فلم يُبحث عن شيء.',
  'refusal.credits.wholeNumber': 'يُحسب الرصيد بأعداد صحيحة، واحد فأكثر.',
  'content.block.lineItems': 'البنود',
  // --- document templates (F-050) ---------------------------------------------
  'doc.title': 'قوالب المستندات',
  'doc.body': 'عروض أسعار وصفقات تُطبع من قالب. تُملأ الحقول من السجل عند إنشاء المستند.',
  'doc.new': 'قالب جديد',
  'doc.none': 'لا توجد قوالب بعد. أنشئ قالباً لطباعة عروض الأسعار والصفقات.',
  'doc.edit': 'تعديل',
  'doc.name': 'الاسم',
  'doc.object': 'يُطبع من',
  'doc.object.quote': 'عرض سعر',
  'doc.object.deal': 'صفقة',
  'doc.format': 'الصيغة',
  'doc.formatNote': 'لا يستطيع ملف PDF طباعة الحروف العربية أو رمز الروبية. اختر DOCX لأي مستند قد يحتويها.',
  'doc.fields': 'الحقول',
  'doc.save': 'حفظ القالب',
  'doc.cancel': 'إلغاء',
  'doc.generateTitle': 'الطباعة من قالب',
  'doc.noTemplates': 'لا توجد قوالب عروض أسعار بعد. يمكن للمسؤول إنشاء واحد في الإعدادات.',
  'doc.template': 'القالب',
  'doc.choose': 'اختر قالباً',
  'doc.unknownFields': 'يستخدم القالب {fields}، وهو ليس حقلاً في عرض السعر. يجب إصلاح القالب.',
  'doc.emptyFields': 'لا قيمة في عرض السعر هذا لـ {fields}. املأها قبل الطباعة.',
  'doc.unsettable': 'لا يستطيع ملف PDF طباعة {characters}. استخدم قالب DOCX لعرض السعر هذا.',
  'doc.ready': 'لكل حقل قيمة. لن يُطبع شيء كعنصر نائب.',
  'doc.generate': 'إنشاء وإرفاق',
  'doc.field.accountName': 'اسم الشركة',
  'doc.field.ownerName': 'المالك',
  'doc.field.workspaceName': 'شركتك',
  'doc.field.today': 'اليوم',
  'doc.field.quoteNumber': 'رقم عرض السعر',
  'doc.field.quoteName': 'اسم عرض السعر',
  'doc.field.quoteValidUntil': 'صالح حتى',
  'doc.field.quoteTerms': 'الشروط',
  'doc.field.quoteSubtotal': 'المجموع الفرعي',
  'doc.field.quoteDiscount': 'الخصم',
  'doc.field.quoteTotal': 'الإجمالي',
  'doc.field.dealName': 'اسم الصفقة',
  'doc.field.dealAmount': 'مبلغ الصفقة',
  'doc.field.dealStage': 'المرحلة',
  'doc.field.dealCloseDate': 'تاريخ الإغلاق',
  'refusal.document.noName': 'أعط القالب اسماً.',
  'refusal.document.object': 'يمكن إنشاء المستندات من عروض الأسعار والصفقات، وليس من {object}.',
  'refusal.document.format': 'المستند إما PDF أو DOCX.',
  'refusal.document.blocks': 'لا يمكن حفظ هذا القالب: {problem}',
  'refusal.document.dealLines': 'لا توجد بنود في الصفقة لطباعتها. البنود تخص قالب عرض السعر.',
  'refusal.document.unknownFields': 'يستخدم القالب {fields}، وهو ليس حقلاً في {object}.',
  'refusal.document.nameTaken': 'يوجد بالفعل قالب باسم "{name}".',
  'refusal.document.emptyFields': 'لا قيمة في {object} هذا لـ {fields}، ولا يوفّر القالب بديلاً. املأها أولاً.',
  'refusal.document.unsettable': 'لا يستطيع ملف PDF طباعة {characters}. اجعل هذا قالب DOCX، فهو يستطيع.',
  'noun.documentTemplate': 'ذلك القالب',
  'noun.quote': 'ذلك العرض',
  'partner.title': "الشركاء",
  'partner.body': "الشركات التي تبيع نيابة عنك، والصفقات التي تسجّلها، والروابط المشتركة معها. يرى الشريك الصفقات التي سجّلها فقط، ولا يرى صفقات شريك آخر أبداً.",
  'partner.notPermitted': "تتطلب إدارة الشركاء صلاحية إدارة الشركاء. اطلبها من المسؤول.",
  'partner.registrations': "التسجيلات",
  'partner.registrations.none': "لم يسجّل أي شريك صفقة بعد.",
  'partner.partners': "الشركات الشريكة",
  'partner.partners.none': "لا توجد شركة شريكة بعد.",
  'partner.partners.help': "اجعل الشركة شريكة من صفحتها، وأصدر الروابط لموظفيها من هناك.",
  'partner.people': "الأشخاص: {count}",
  'partner.protectionDays': "محمية لمدة {days} يوماً",
  'partner.protectionLabel': "مدة الحماية بالأيام",
  'partner.protectedUntil': "محمية حتى {date}",
  'partner.registeredBy': "سجّلها {person} من {partner} بتاريخ {date}",
  'partner.someone': "شخص لم يعد مسجلاً",
  'partner.status.pending': "قيد المراجعة",
  'partner.status.accepted': "مقبولة",
  'partner.status.rejected': "مرفوضة",
  'partner.conflict.partner': "تتعارض مع «{deal}» التي سجّلها {partner}. لم يُبلَّغ الشريك بذلك.",
  'partner.conflict.direct': "تتعارض مع «{deal}» التي تُباع مباشرة. لم يُبلَّغ الشريك بذلك.",
  'partner.accept': "قبول",
  'partner.reject': "رفض",
  'partner.cancel': "إلغاء",
  'partner.rejectReason': "السبب، بكلمات سيقرؤها الشريك",
  'partner.collateral': "مشترك مع الشركاء",
  'partner.collateral.none': "لم تتم مشاركة أي شيء مع الشركاء بعد.",
  'partner.collateral.title': "العنوان",
  'partner.collateral.url': "الرابط (https://)",
  'partner.collateral.share': "مشاركة",
  'partner.collateral.remove': "إيقاف المشاركة",
  'partner.dealPanel': "سجّلها شريك",
  'partner.toldPartner': "أُبلغ الشريك: {note}",
  'partner.accountPanel': "شريك",
  'partner.since': "شريك منذ {date}",
  'partner.notPartner': "هذه الشركة ليست شريكة.",
  'partner.make': "اجعلها شريكة",
  'partner.saveDays': "حفظ مدة الحماية",
  'partner.end': "إنهاء الشراكة",
  'partner.noPeople': "لا يوجد أحد مسجل في هذه الشركة، فلا أحد يمكن إرسال رابط الشريك إليه.",
  'partner.issueLink': "إصدار رابط الشريك",
  'partner.linkIssued': "أرسل هذا الرابط إليهم. يعمل حتى {date} ولن يُعرض مرة أخرى.",
  'partner.portal.register': "تسجيل صفقة",
  'partner.portal.registered': "تم التسجيل. الصفقة قيد المراجعة ويمكنك متابعتها أدناه.",
  'partner.portal.company': "شركة العميل",
  'partner.portal.website': "موقع العميل (اختياري)",
  'partner.portal.dealName': "اسم الصفقة",
  'partner.portal.closeDate': "تاريخ الإغلاق المتوقع",
  'partner.portal.amount': "المبلغ المتوقع",
  'partner.portal.currency': "العملة",
  'partner.portal.submit': "تسجيل",
  'partner.portal.deals': "تسجيلاتك",
  'partner.portal.noDeals': "لم تسجّل أي صفقة بعد.",
  'partner.portal.status.under-review': "قيد المراجعة",
  'partner.portal.status.accepted': "مقبولة",
  'partner.portal.status.not-accepted': "غير مقبولة",
  'partner.portal.collateral': "مشترك معك",
  'partner.portal.noCollateral': "لم تتم مشاركة أي شيء بعد.",
  'portal.field.name': "الصفقة",
  'portal.field.amount': "المبلغ",
  'portal.field.close_date': "الإغلاق المتوقع",
  'portal.field.protected_until': "محمية حتى",
  'noun.link': "ذلك الرابط",
  'refusal.partner.protectionDays': "مدة الحماية عدد صحيح من الأيام، من 1 إلى 730.",
  'refusal.partner.notAtPartner': "{name} لا يعمل لدى شريك، لذا لا يمكن منحه رابط شريك.",
  'refusal.partner.rejectReason': "اذكر السبب بكلمات سيقرؤها الشريك.",
  'refusal.partner.notRegistered': "لم يسجّل شريك هذه الصفقة.",
  'refusal.partner.protected': "{partner} يحتفظ بهذا العميل بالفعل، ولم تنتهِ مدة حمايته.",
  'refusal.partner.alreadyDecided': "تم البتّ في هذا التسجيل بالفعل ({status}).",
  'refusal.partner.collateralTitle': "أعطِ الرابط عنواناً.",
  'refusal.partner.collateralUrl': "يجب أن يبدأ الرابط المشترك بـ https://.",
  'refusal.partner.wrongSurface': "هذا الرابط ليس رابط شريك.",
  'refusal.partner.noLongerPartner': "لم تعد هذه الشركة شريكة.",
  'refusal.partner.closed': "بوابة الشركاء هذه مغلقة.",
  'refusal.partner.company': "اذكر الشركة التي تخصها هذه الصفقة.",
  'refusal.partner.dealName': "أعطِ الصفقة اسماً.",
  'refusal.partner.amount': "المبلغ رقم أكبر من الصفر، مثل 48000 أو 48000.50.",
  'refusal.partner.currency': "العملة رمز من ثلاثة أحرف، مثل AED.",
  'refusal.partner.closeDate': "تاريخ الإغلاق المتوقع تاريخ، مثل 2026-12-31.",
  'refusal.partner.closeDatePast': "تاريخ الإغلاق المتوقع في الماضي.",
  'refusal.partner.ownCompany': "لا يمكن للشريك تسجيل صفقة مع شركته.",
  'refusal.partner.alreadyRegistered': "لقد سجّلت «{deal}» من قبل.",

  'quote.termsTitle': "الشروط والصلاحية",
  'quote.terms': "الشروط",
  'quote.termsSave': "حفظ الشروط",
  'quote.noTerms': "لم تُحدَّد شروط لهذا العرض.",
  'quote.noValidity': "ليس لهذا العرض تاريخ صلاحية.",
  'quote.startedFrom': "بدأ من {template}، الإصدار {version}",
  'quoteTemplate.title': "قوالب عروض الأسعار",
  'quoteTemplate.body': "هياكل عروض أسعار يبدأ منها المندوب: البنود والشروط والصلاحية والهوية. العرض المُنشأ من قالب عرض عادي، وتعديل القالب لاحقاً لا يغيّره أبداً.",
  'quoteTemplate.new': "قالب عرض أسعار جديد",
  'quoteTemplate.none': "لا توجد قوالب عروض أسعار بعد.",
  'quoteTemplate.summary': "{currency} · {lines} بنود · الإصدار {version} · حُفظ {date}",
  'quoteTemplate.pickerTitle': "ابدأ من",
  'quoteTemplate.blank': "عرض أسعار فارغ",
  'quoteTemplate.noneInCurrency': "لا توجد قوالب عروض أسعار مسعّرة بعملة {currency}.",
  'quoteTemplate.choice': "{name} ({lines} بنود)",
  'quoteTemplate.version': "الإصدار",
  'quoteTemplate.versionOption': "الإصدار {version}",
  'quoteTemplate.save': "حفظ كإصدار جديد",
  'quoteTemplate.saved': "حُفظ كإصدار {version}.",
  'quoteTemplate.archive': "إيقاف عرض هذا القالب",
  'quoteTemplate.oldVersion': "هذا الإصدار {version}، محفوظ كسجل لما بدأت منه العروض. لا يمكن تعديله؛ اختر أحدث إصدار لإجراء تغييرات.",
  'quoteTemplate.notPermitted': "يمكنك قراءة هذا القالب. يتطلب تغييره صلاحية إنشاء قوالب عروض الأسعار وتغييرها.",
  'quoteTemplate.name': "الاسم",
  'quoteTemplate.currency': "العملة",
  'quoteTemplate.validityDays': "صالح لمدة (أيام)",
  'quoteTemplate.branding': "يُطبع باستخدام",
  'quoteTemplate.brandingNone': "بدون قالب مستند",
  'quoteTemplate.brandingChosen': "قالب المستند المختار مسبقاً",
  'quoteTemplate.searchProducts': "ابحث في الكتالوج",
  'quoteTemplate.addProduct': "أضف بند منتج",
  'quoteTemplate.chooseProduct': "اختر منتجاً",
  'quoteTemplate.productsCapped': "تُعرض أول 25 منتجاً. ابحث للعثور على غيرها.",
  'quoteTemplate.productPricing': "تُعرض فقط المنتجات المسعّرة بعملة {currency}. يُسعَّر بند المنتج من الكتالوج في يوم استخدام القالب، لذا قد يتغير السعر المعروض هنا.",
  'quoteTemplate.readOnly': "للقراءة فقط",
  'quoteTemplate.validFor': "صالح لمدة {days} يوماً من يوم استخدامه",
  'quoteTemplate.optionalSection': "اختياري - يختارها العميل",
  'quoteTemplate.emptyState': "قالب فارغ، مع بند فارغ جاهز للتعبئة.",
  'quoteTemplate.loadFailed': "تعذّر تحميل هذا القالب.",
  'noun.quoteTemplate': "ذلك القالب",
  'refusal.quote.termsLength': "الشروط 10,000 حرف كحد أقصى.",
  'refusal.quote.validUntil': "تاريخ الصلاحية تاريخ، مثل 2026-12-31.",
  'refusal.quote.validUntilPast': "هذا التاريخ قد مضى، فسيصبح العرض منتهي الصلاحية لحظة حفظه.",
  'refusal.quoteTemplate.name': "أعطِ القالب اسماً لا يتجاوز 200 حرف.",
  'refusal.quoteTemplate.currency': "العملة رمز من ثلاثة أحرف، مثل AED.",
  'refusal.quoteTemplate.validity': "العرض المُنشأ من هذا القالب صالح من يوم إلى 365 يوماً.",
  'refusal.quoteTemplate.noLines': "يحتاج القالب إلى بند واحد على الأقل.",
  'refusal.quoteTemplate.tooManyLines': "يحتوي القالب على 200 بند كحد أقصى.",
  'refusal.quoteTemplate.lineDescription': "البند {row} يحتاج إلى وصف.",
  'refusal.quoteTemplate.lineQuantity': "البند {row} يحتاج إلى كمية أكبر من الصفر.",
  'refusal.quoteTemplate.linePrice': "البند {row} يحتاج إلى سعر صفر أو أكثر.",
  'refusal.quoteTemplate.lineDiscount': "خصم البند {row} خارج النطاق من 0 إلى 100 بالمئة.",
  'refusal.quoteTemplate.lineProduct': "البند {row} لمنتج غير موجود في الكتالوج.",
  'refusal.quoteTemplate.document': "اختر قالب مستند مخصصاً لعروض الأسعار.",
  'refusal.quoteTemplate.nameTaken': "يوجد بالفعل قالب عرض أسعار باسم \"{name}\".",
  'refusal.quoteTemplate.stale': "حفظ شخص آخر الإصدار {current} من هذا القالب بعد أن فتحت الإصدار {opened}. أعد فتحه لترى تغييراته.",
  'refusal.quoteTemplate.notDraft': "تم إرسال {quote}، لذا لا يمكن تطبيق قالب عليه.",
  'refusal.quoteTemplate.notEmpty': "يحتوي {quote} على بنود بالفعل. القالب يبدأ عرضاً ولا يستبدله.",
  'refusal.quoteTemplate.currencyMismatch': "\"{template}\" مسعّر بعملة {templateCurrency}، وهذا العرض بعملة {quoteCurrency}.",
  'refusal.quoteTemplate.productGone': "البند {row}، \"{line}\"، لمنتج لم يعد يُباع. عدّل القالب.",
  'refusal.quoteTemplate.lineCurrency': "البند {row}، \"{line}\"، مسعّر بعملة {lineCurrency}، وهذا العرض بعملة {quoteCurrency}.",
  'quoteTemplate.noDocuments': "لا توجد قوالب مستندات لعروض الأسعار بعد. أنشئ واحداً في الإعدادات ضمن قوالب المستندات لتختار طريقة طباعة العروض من هذا القالب.",
  'portal.quote.open': "فتح",
  'portal.quote.close': "إغلاق",
  'portal.quote.unavailable': "لا يمكن عرض هذا العرض.",
  'portal.quote.validUntil': "صالح حتى {date}",
  'portal.quote.lapsed': "كان هذا العرض صالحاً حتى {date}، وقد مضى هذا التاريخ. اطلب عرضاً جديداً.",
  'portal.quote.optionalTitle': "اختياري - اختر ما تود إضافته",
  'portal.quote.accept': "قبول مقابل {total}",
  'portal.quote.decline': "رفض",
  'portal.quote.back': "رجوع",
  'portal.quote.declineReason': "هل تخبرنا بالسبب؟ (اختياري)",
  'portal.quote.accepted': "قبلت هذا العرض في {date}.",
  'portal.quote.declined': "رفضت هذا العرض في {date}.",
  'quote.decision.accepted': "قبل {person} هذا العرض في {date} مقابل {total}، مع {lines} بنود اختيارية.",
  'quote.decision.declined': "رفض {person} هذا العرض في {date}. {reason}",
  'quote.decision.someone': "العميل",
  'refusal.quoteAccept.wrongLink': "هذا الرابط لا يفتح عروض الأسعار.",
  'refusal.quoteAccept.reasonLength': "اجعل السبب أقل من 2,000 حرف.",
  'refusal.quoteAccept.declineWithLines': "العرض المرفوض لا يتضمن بنوداً اختيارية.",
  'refusal.quoteAccept.notOptional': "أحد البنود المختارة ليس بنداً اختيارياً في هذا العرض.",
  'refusal.quoteAccept.alreadyDecided': "تم البتّ في {quote} بالفعل ({status}).",
  'refusal.quoteAccept.lapsed': "كان {quote} صالحاً حتى {date}، وقد مضى هذا التاريخ. اطلب عرضاً جديداً.",
  'refusal.quoteAccept.notOpen': "لا يمكن البتّ في {quote} الآن.",
  'refusal.quoteAccept.closed': "لم تعد عروض الأسعار متاحة في هذه الصفحة.",
  'quote.unit': "الوحدة",
  'quote.unitConverted': "يساوي {amount} {base}",
  'unit.title': "وحدات القياس",
  'unit.body': "الوحدات التي تُعدّ بها المنتجات وتُباع. الوحدة المكوّنة من وحدة أخرى تُخزَّن بما تساويه من الوحدة الأساسية، فكرتونة من 6 علب في كل منها 12 تساوي 72 قطعة.",
  'unit.none': "لا توجد وحدات بعد. ابدأ بوحدة أساسية مثل قطعة أو متر.",
  'unit.comesTo': "يساوي {factor} {base}",
  'unit.isBase': "وحدة أساسية",
  'unit.name': "الاسم",
  'unit.madeOf': "مكوّنة من",
  'unit.aBaseUnit': "لا شيء - وحدة أساسية",
  'unit.holdsSome': "عدد من {unit}",
  'unit.howMany': "كم عدد",
  'unit.add': "إضافة وحدة",
  'unit.countedIn': "تُعدّ بـ",
  'unit.noneYet': "لا توجد وحدات أساسية بعد. أضفها في الإعدادات ضمن وحدات القياس.",
  'unit.notCounted': "غير محدد",
  'unit.countedInHelp': "يمكن بعدها إدخال بنود عروض هذا المنتج بأي وحدة مكوّنة من هذه الوحدة، مع عرض ما تساويه.",
  'noun.unit': "تلك الوحدة",
  'refusal.unit.label': "أعطِ الوحدة اسماً لا يتجاوز 60 حرفاً.",
  'refusal.unit.taken': "توجد بالفعل وحدة باسم \"{label}\".",
  'refusal.unit.count': "حدّد عدد ما تحتويه، برقم أكبر من الصفر.",
  'refusal.unit.notBase': "\"{label}\" تُعدّ بوحدة أخرى، لذا لا يمكن أن تكون الوحدة الأساسية لمنتج.",
  'refusal.unit.wrongFamily': "البند {row}: لا يُباع {product} بوحدة {unit}.",
  'territory.title': "المناطق",
  'territory.body': "من يعمل على أي شركات. القائمة المسمّاة تتقدم على المعايير؛ وبين المعايير يفوز الرقم الأقل أولوية. حفظ المنطقة لا ينقل أحداً - النقل هو إعادة التوزيع أدناه، مع معاينة أولاً.",
  'territory.new': "منطقة جديدة",
  'territory.none': "لا توجد مناطق بعد.",
  'territory.inactive': "(غير نشطة)",
  'territory.counts': "{matching} مطابقة · {assigned} مسندة · الأولوية {priority} · {who}",
  'territory.edit': "تعديل",
  'territory.overlap': "{count} من هذه الشركات تطابق منطقة أخرى أيضاً. الرقم الأقل أولوية يأخذها؛ على أحد التحقق من أن هذا مقصود.",
  'territory.rebalance': "إعادة التوزيع",
  'territory.rebalanceBody': "انقل كل شركة إلى المنطقة التي تنتمي إليها الآن، مع إعطائها مالك تلك المنطقة. يُحفظ كل نقل على الشركة.",
  'territory.rebalanced': "نُقلت {count} شركة.",
  'territory.previewRebalance': "معاينة ما سيُنقل",
  'territory.nothingToMove': "كل شركة في مكانها الصحيح بالفعل.",
  'territory.wouldMove': "ستُنقل {total} شركة. تُعرض أول {shown}.",
  'territory.move': "{company}: {from} ← {to}",
  'territory.noTerritory': "بدون منطقة",
  'territory.applyRebalance': "نقل {total} شركة",
  'territory.cancel': "إلغاء",
  'territory.name': "الاسم",
  'territory.priority': "الأولوية (الأقل يفوز)",
  'territory.owner': "المالك",
  'territory.nobody': "لا أحد - يعمل عليها الفريق",
  'territory.team': "الفريق",
  'territory.noTeam': "بدون فريق",
  'territory.activeLabel': "نشطة - تطالب بالشركات",
  'territory.criteria': "الشركات المطابقة",
  'territory.named': "الشركات المسمّاة",
  'territory.namedHelp': "الشركة المسمّاة هنا تنتمي إلى هذه المنطقة مهما قالت المعايير. يمكن تسمية الشركة في منطقة واحدة فقط.",
  'territory.remove': "إزالة",
  'territory.findCompany': "ابحث عن شركة لتسميتها",
  'territory.addCompany': "إضافة {name}",
  'territory.save': "حفظ المنطقة",
  'territory.accountPanel': "المنطقة",
  'territory.reason.created': "{date}: وُضعت في {to} عند إنشائها. المالك {owner}.",
  'territory.reason.changed': "{date}: نُقلت من {from} إلى {to} عند تغيّر بياناتها. المالك {owner}.",
  'territory.reason.rebalanced': "{date}: نُقلت من {from} إلى {to} بإعادة توزيع. المالك {owner}.",
  'noun.territory': "تلك المنطقة",
  'refusal.territory.criteria': "هذه المعايير لا تنطبق على شركة: {problems}",
  'refusal.territory.name': "أعطِ المنطقة اسماً لا يتجاوز 120 حرفاً.",
  'refusal.territory.priority': "الأولوية رقم صحيح من 1 إلى 10,000؛ الأقل يفوز.",
  'refusal.territory.assignee': "حدّد من يعمل على هذه المنطقة: شخص أو فريق أو كلاهما.",
  'refusal.territory.nameTaken': "توجد بالفعل منطقة باسم \"{name}\".",
  'refusal.territory.namedTwice': "{company} مسمّاة بالفعل في {territory}.",
  'refusal.territory.off': "المناطق متوقفة في مساحة العمل هذه.",
  // --- automation lane: validation rules (F-091) ---------------------------
  'noun.object': "هذا الكائن",
  'noun.validationRule': "قاعدة التحقق هذه",
  'refusal.validation.noName': "تحتاج قاعدة التحقق إلى اسم، لأن الاسم هو ما يُعرض على الناس.",
  'refusal.validation.noMessage': "تحتاج قاعدة التحقق إلى رسالة توضّح ما يجب إصلاحه.",
  'refusal.validation.severity': "القاعدة إما تحذّر وإما ترفض الحفظ.",
  'refusal.validation.scope': "تنطبق القاعدة على الجميع أو على أدوار مختارة.",
  'refusal.validation.field': "«{field}» ليس حقلاً يمكن عرض رسالة هذه القاعدة عليه.",
  'refusal.validation.conditions': "لا يمكن تقييم هذه القاعدة بصيغتها الحالية.",
  'refusal.validation.noRoles': "تحتاج القاعدة الخاصة بأدوار مختارة إلى دور واحد على الأقل.",
  'refusal.validation.unknownRole': "أحد الأدوار المختارة غير موجود في مساحة العمل هذه.",
  'refusal.validation.duplicateName': "يوجد في هذا الكائن قاعدة باسم «{name}» بالفعل.",
  'refusal.validation.blocked': "القاعدة {rule}: {message}",
  'refusal.validation.blockedMany': "هذا الحفظ يخالف {count} من القواعد. كل منها معروض على حقله.",
  'refusal.validation.required': "الحقل {field} مطلوب.",
  'validation.required.rule': "حقل مطلوب",
  'validation.required.message': "الحقل {field} مطلوب.",
  'validation.ruleName': "القاعدة: {rule}",
  'validation.elsewhere': "عن حقول غير معروضة في هذه الصفحة",
  'validation.title': "قواعد التحقق",
  'validation.body': "ارفض الحفظ، أو احفظ مع تحذير، عندما يبدو السجل خاطئاً. يُخبَر من توقفه القاعدة باسمها دائماً.",
  'validation.object': "الكائن",
  'validation.new': "قاعدة جديدة",
  'validation.empty': "لا توجد قواعد تحقق على هذا الكائن بعد.",
  'validation.name': "اسم القاعدة",
  'validation.field': "اعرض الرسالة على الحقل",
  'validation.severity': "عندما يخالفها سجل",
  'validation.severity.block': "ارفض الحفظ",
  'validation.severity.warn': "احفظ مع تحذير",
  'validation.message': "الرسالة",
  'validation.scope': "تنطبق على",
  'validation.scope.everyone': "الجميع",
  'validation.scope.roles': "أدوار مختارة",
  'validation.scope.nobody': "لا أحد: حُذفت الأدوار التي كانت تسمّيها",
  'validation.conditions': "يخالف السجل هذه القاعدة عندما",
  'validation.makeRequired': "اجعل هذا الحقل مطلوباً",
  'validation.save': "احفظ القاعدة",
  'validation.cancel': "إلغاء",
  'validation.edit': "تعديل",
  'validation.delete': "إزالة",
  'validation.off': "متوقفة",
  'validation.chip.block': "ترفض",
  'validation.chip.warn': "تحذّر",

  'noun.lead': "هذا العميل المحتمل",
  'refusal.touch.noPerson': "تنتمي نقطة التواصل إلى عميل محتمل أو جهة اتصال.",
  'source.title': "المصدر",
  'source.first': "أول تواصل",
  'source.last': "آخر تواصل",
  'source.firstAndLast': "أول وآخر تواصل",
  'source.count': "نقاط التواصل المسجلة: {count}",
  'source.referrer': "الصفحة المُحيلة",
  'source.notSent': "لم يُرسل",
  'source.via.form': "عبر النموذج {form}",
  'source.via.formRemoved': "عبر نموذج التقاط حُذف منذ ذلك الحين",
  'source.via.record': "مخزّن على العميل المحتمل عند إنشائه",
  'source.none.person': "لم يُسجَّل أي وصول لهذا الشخص بعد. تُسجَّل نقاط التواصل عندما يرسل أحدهم نموذج التقاط.",
  'source.none.deal': "أُنشئت هذه الصفقة دون شخص مرتبط بها، لذا لا تحمل مصدراً.",
  'source.deal.from': "منقول من",
  'source.deal.hidden': "منقول من جهة اتصال لا يمكنك فتحها",
  'source.deal.noTouch': "لم يكن لدى ذلك الشخص أي تواصل مسجّل عند إنشاء هذه الصفقة.",
  'refusal.source.object': "يُحفظ المصدر للعملاء المحتملين وجهات الاتصال والصفقات.",
  'attribution.title': "الإسناد",
  'attribution.body': "إيرادات الصفقات الرابحة موزّعة على نقاط التواصل التي سبقت كل صفقة، بنموذجين معاً. الرقم الواحد وحده نموذج يُقدَّم كحقيقة.",
  'attribution.modelA': "النموذج",
  'attribution.modelB': "مقارنة مع",
  'attribution.model.first': "أول تواصل",
  'attribution.model.last': "آخر تواصل",
  'attribution.model.linear': "خطي",
  'attribution.model.u-shaped': "على شكل U (40/20/40)",
  'attribution.model.time-decay': "تناقص زمني (نصف عمر 7 أيام)",
  'attribution.dimension': "التجميع حسب",
  'attribution.from': "ربحت من",
  'attribution.to': "ربحت حتى",
  'attribution.deals': "الصفقات",
  'attribution.none': "لم تأتِ أي صفقة رابحة في هذه الفترة عبر نقطة تواصل متتبَّعة.",
  'attribution.noValue': "(لا شيء)",
  'attribution.covers': "يشمل {deals} صفقة رابحة بقيمة {revenue}. غير مُسنَد: {unattributed} صفقة رابحة بلا تواصل قبل إنشائها ({unattributedRevenue})، و{other} صفقة رابحة بعملة أخرى.",
  'attribution.campaignTitle': "الإسناد",
  'attribution.trackingCode': "مُتتبَّعة بـ utm_campaign",
  'attribution.saveCode': "حفظ الرمز",
  'attribution.trackingHelp': "يُقارن حرفياً كما يظهر في الرابط - \"Spring-Sale\" و\"spring-sale\" رمزان مختلفان.",
  'attribution.noCode': "أعطِ هذه الحملة قيمة utm_campaign التي تحملها روابطها لترى ما أُسند إليها.",
  'refusal.attribution.sameModel': "اختر نموذجين مختلفين للمقارنة.",
  'refusal.attribution.period': "اختر فترة تبدأ في يوم نهايتها أو قبله.",
  'refusal.attribution.codeLength': "رمز التتبع 200 حرف كحد أقصى.",
  'refusal.attribution.codeTaken': "{campaign} مُتتبَّعة بالفعل بـ \"{code}\".",
  'noun.campaign': "تلك الحملة",
  'notify.lead.mql': "عميل محتمل أملكه يصبح مؤهلاً تسويقياً",
  'mql.queue': "العملاء المؤهلون تسويقياً",
  'mql.off': "تسليم العملاء المؤهلين متوقف: لم يُحدَّد حد.",
  'mql.threshold': "يُسلَّم عند {points} نقطة - الحد الذي حدده التسويق.",
  'mql.onlyMine': "الخاصة بي فقط",
  'mql.empty': "لا شيء بانتظار الرد.",
  'mql.scored': "{handoff} نقطة عند التسليم، {now} الآن",
  'mql.due': "الرد قبل {when}",
  'mql.overdue': "متأخر منذ {when}",
  'mql.why': "سبب الإرجاع",
  'mql.chooseReason': "اختر سبباً",
  'mql.reject': "إرجاع",
  'mql.accept': "قبول",
  'mql.cancel': "إلغاء",
  'mql.title': "تسليم العملاء المؤهلين تسويقياً",
  'mql.body': "الدرجة التي يسلّم عندها التسويق العميل المحتمل للمبيعات، والمدة المتاحة للمبيعات للرد، والأسباب التي يمكن للمبيعات تقديمها للإرجاع - تعريف واحد يقرؤه الفريقان.",
  'mql.thresholdLabel': "الحد (نقاط)",
  'mql.hoursLabel': "الرد خلال (ساعات عمل)",
  'mql.save': "حفظ",
  'mql.thresholdHelp': "اترك الحد فارغاً لإيقاف التسليم. يُسلَّم العميل المحتمل مرة واحدة، عندما تصل درجته إليه أول مرة.",
  'mql.reasons': "الأسباب المتاحة للمبيعات",
  'mql.retire': "إيقاف",
  'mql.newReason': "سبب جديد",
  'mql.addReason': "إضافة سبب",
  'mql.reportTitle': "ما قالته المبيعات، آخر 90 يوماً",
  'mql.reportSummary': "تم الرد على {decided}: قُبل {accepted}، وأُرجع {rejected}.",
  'mql.band': "{from}-{to} نقطة: قُبل {accepted}، وأُرجع {rejected}",
  'refusal.mql.threshold': "الحد عدد صحيح من النقاط من 1 إلى 1,000.",
  'refusal.mql.slaHours': "لدى المبيعات من 1 إلى 720 ساعة عمل للرد.",
  'refusal.mql.reasonTaken': "\"{label}\" سبب موجود بالفعل.",
  'refusal.mql.noteLength': "اجعل الملاحظة أقل من 2,000 حرف.",
  'refusal.mql.reasonRequired': "اختر سبب الإرجاع.",
  'refusal.mql.reasonRetired': "هذا السبب لم يعد متاحاً. اختر غيره.",
  'refusal.mql.alreadyDecided': "تم البتّ في هذا العميل المحتمل بالفعل ({decision}).",
  'refusal.mql.notMql': "لم يُسلَّم هذا العميل المحتمل كمؤهل تسويقياً.",
  'refusal.mql.period': "الفترة من 1 إلى 730 يوماً.",
  'campaign.refusal.awaitingConfirmation': "طُلب منه التأكيد عبر البريد ولم يؤكد بعد",
  'campaign.refusal.erased': "مُحي بناءً على طلبه",
  'consent.pending': "بانتظار التأكيد",
  'consent.evidence': "إثبات الموافقة",
  'consent.evidenceBody': "كل موافقة أعطاها هذا الشخص أو سحبها، مع النص الذي عُرض عليه ومصدرها. القيمة المنطقية ليست إثباتاً.",
  'consent.none': "لم تُسجَّل أي موافقة لهذا الشخص.",
  'consent.wording': "عُرض عليه",
  'consent.noWording': "لم يُسجَّل النص المعروض (سُجّل قبل حفظ النصوص).",
  'consent.from': "من {source}",
  'consent.ip': "عنوان IP {ip}",
  'consent.expires': "يتوقف رابط التأكيد عن العمل {date}.",
  'consent.recordedBy': "سجّله {name}",
  'confirm.title': "أكّد اشتراكك",
  'confirm.confirmed': "شكراً - تم تأكيدك. لن نرسل إلا ما وافقت عليه.",
  'confirm.already': "لقد أكّدت من قبل. لا شيء آخر مطلوب.",
  'confirm.expired': "انتهت صلاحية رابط التأكيد هذا. سجّل مرة أخرى وسنرسل رابطاً جديداً.",
  'confirm.invalid': "هذا الرابط لا يعمل. تأكد من نسخ العنوان كاملاً من البريد.",
  'confirm.agreed': "ما وافقت عليه",
  'refusal.consent.doubleNeedsConsent': "التسجيل المزدوج يؤكد الموافقة، لذا يجب أن يطلب النموذج الموافقة أولاً.",
  'refusal.consent.object': "تُحفظ الموافقة لجهات الاتصال والعملاء المحتملين.",
  'form.doubleOptIn': "اطلب منهم التأكيد عبر البريد قبل احتسابها (تسجيل مزدوج)",
  'landing.pages': "الصفحات المقصودة",
  'landing.new': "صفحة مقصودة جديدة",
  'landing.none': "لا توجد صفحات مقصودة بعد.",
  'landing.liveCurrent': "منشورة على {path}",
  'landing.liveOlder': "منشورة على {path} - إصدار أقدم؛ آخر تعديل لم يُنشر",
  'landing.draftOnly': "مسودة - غير منشورة",
  'landing.missing': "لا توجد صفحة على هذا العنوان.",
  'landing.previewBanner': "معاينة - هذه المسودة، ولا يراها أحد غيرك حتى تُنشر.",
  'landing.welcomeBack': "مرحباً بعودتك - نحتاج فقط ما ليس لدينا.",
  'landing.previewNoSubmit': "لا يُرسَل النموذج من المعاينة.",
  'landing.submit': "إرسال",
  'landing.liveAt': "منشورة على {path} منذ {date}",
  'landing.saveDraft': "حفظ المسودة",
  'landing.makePreview': "إنشاء رابط معاينة",
  'landing.publish': "نشر هذا الإصدار",
  'landing.unpublish': "إيقاف النشر",
  'landing.previewFirst': "افتح معاينة هذا الإصدار قبل نشره.",
  'landing.previewReady': "رابط المعاينة جاهز.",
  'landing.openPreview': "فتح المعاينة",
  'landing.name': "الاسم",
  'landing.slug': "العنوان (بعد /p/<workspace>/)",
  'landing.title': "عنوان الصفحة (تعرضه محركات البحث)",
  'landing.form': "نموذج الالتقاط",
  'landing.noForm': "بدون نموذج",
  'landing.description': "الوصف (تعرضه محركات البحث)",
  'landing.content': "المحتوى",
  'landing.emptyBuilder': "صفحة فارغة جاهزة لأول كتلة.",
  'noun.landingPage': "تلك الصفحة المقصودة",
  'refusal.landing.name': "أعطِ الصفحة اسماً.",
  'refusal.landing.slug': "العنوان أحرف إنجليزية صغيرة وأرقام وشرطات مفردة، مثل spring-offer.",
  'refusal.landing.title': "عنوان الصفحة من 1 إلى 120 حرفاً.",
  'refusal.landing.description': "الوصف 300 حرف كحد أقصى.",
  'refusal.landing.blocks': "لا يمكن حفظ هذه الصفحة: {problem}",
  'refusal.landing.form': "اختر نموذج التقاط ما زال فعالاً.",
  'refusal.landing.slugTaken': "توجد صفحة أخرى على /{slug} بالفعل.",
  'refusal.landing.notPreviewed': "افتح معاينة هذا الإصدار قبل نشره.",
  'refusal.landing.changed': "تغيّرت الصفحة أثناء نشرها. عاينها مرة أخرى.",
  'refusal.form.email': "عنوان البريد الإلكتروني هذا غير مكتمل - يحتاج إلى @ ونطاق.",
  'assets.library': "مكتبة الأصول",
  'assets.fromLibrary': "من المكتبة",
  'assets.chooseImage': "اختر من المكتبة",
  'assets.search': "ابحث في الأسماء والوسوم",
  'assets.kind': "النوع",
  'assets.kindAll': "كل الأنواع",
  'assets.kind.image': "صورة",
  'assets.kind.document': "مستند",
  'assets.kind.snippet': "مقتطف",
  'assets.kind.template': "قالب",
  'assets.folders': "المجلدات",
  'assets.all': "الكل",
  'assets.folder': "المجلد",
  'assets.noFolder': "بلا مجلد",
  'assets.newFolder': "مجلد جديد",
  'assets.folderName': "اسم المجلد",
  'assets.editorRole': "من يمكنه تعديله",
  'assets.anyoneEdits': "كل من يحرر المحتوى",
  'assets.createFolder': "أضف المجلد",
  'assets.locked': "لا يمكن تعديل هذا المجلد إلا لدور {role} والأدوار الأعلى منه. يمكن للجميع استخدام محتواه.",
  'assets.none': "لا يوجد شيء في المكتبة هنا بعد.",
  'assets.noneBody': "أضف صورة أو مستندًا أو مقتطفًا أو قالبًا لاستخدامه في كل رسالة وصفحة.",
  'assets.usedIn': "مستخدم في {count}",
  'assets.unused': "غير مستخدم في أي مكان",
  'assets.whereUsed': "أين يُستخدم",
  'assets.use.email': "رسالة بريد",
  'assets.use.landing-page': "صفحة مقصودة",
  'assets.use.article': "مقالة",
  'assets.add': "أضف إلى المكتبة",
  'assets.name': "الاسم",
  'assets.tags': "الوسوم، مفصولة بفواصل",
  'assets.file': "الملف",
  'assets.snippetText': "النص",
  'assets.save': "احفظ",
  'assets.delete': "أزل من المكتبة",
  'assets.deleteBlocked': "إنه مستخدم، لذا لا يمكن إزالته. أخرجه من تلك الرسائل والصفحات أولًا.",
  'assets.address': "العنوان العام:",
  'noun.asset': "ذلك الأصل",
  'noun.assetFolder': "ذلك المجلد",
  'refusal.asset.folderLocked': "لا يعدّل محتوى {folder} إلا محرروه.",
  'refusal.asset.folderName': "اسم المجلد من 1 إلى 120 حرفًا.",
  'refusal.asset.folderTaken': "يوجد مجلد باسم {name} هنا بالفعل.",
  'refusal.asset.name': "أعطِ الأصل اسمًا لا يزيد على 200 حرف.",
  'refusal.asset.empty': "هذا الملف فارغ.",
  'refusal.asset.tooLarge': "الحد الأقصى لملف المكتبة 5 ميغابايت.",
  'refusal.asset.imageType': "الصورة بصيغة PNG أو JPEG أو GIF أو WebP.",
  'refusal.asset.notWhatItSays': "هذا الملف ليس من نوع {type} كما يدّعي.",
  'refusal.asset.documentType': "المستند ملف PDF أو نص أو CSV أو ملف Word أو Excel أو PowerPoint.",
  'refusal.asset.snippetEmpty': "المقتطف يحتاج إلى نصه.",
  'refusal.asset.templateEmpty': "القالب يحتاج إلى كتلة واحدة على الأقل.",
  'refusal.asset.templateBlocks': "لا يمكن حفظ هذا القالب: {problem}",
  'refusal.asset.inUse': "{name} مستخدم في {count} موضع: {places}. أخرجه منها أولًا.",
  'events.title': "الندوات والفعاليات",
  'events.new': "فعالية جديدة",
  'events.none': "لا توجد فعاليات بعد.",
  'events.missing': "لا توجد فعالية على هذا العنوان.",
  'events.save': "احفظ الفعالية",
  'events.status.draft': "مسودة",
  'events.status.open': "التسجيل مفتوح",
  'events.status.closed': "مغلقة",
  'events.kind': "النوع",
  'events.kind.webinar': "ندوة عبر الإنترنت",
  'events.kind.in-person': "حضورية",
  'events.name': "الاسم",
  'events.slug': "العنوان",
  'events.joinLink': "رابط الانضمام (يُرسل في التذكيرات ولا يُنشر)",
  'events.address': "المكان",
  'events.startsAt': "تبدأ",
  'events.endsAt': "تنتهي",
  'events.capacity': "السعة (اتركها فارغة بلا حد)",
  'events.reminders': "التذكيرات بالساعات قبل البداية",
  'events.form': "نموذج التسجيل",
  'events.noForm': "بلا نموذج",
  'events.openRegistration': "مفتوحة للتسجيل",
  'events.attendedJourney': "المتابعة لمن حضر",
  'events.noShowJourney': "المتابعة لمن لم يحضر",
  'events.noFollowUp': "بلا متابعة",
  'events.noJourneys': "فعّل رحلة لاستخدامها متابعةً للحاضرين أو الغائبين.",
  'events.description': "الوصف",
  'events.funnel': "مسار التسجيل",
  'events.stage.registered': "مسجّلون",
  'events.stage.confirmed': "لديهم مقعد",
  'events.stage.reminded': "تم تذكيرهم",
  'events.stage.attended': "حضروا",
  'events.waitlistCount': "{count} في انتظار مقعد",
  'events.split': "حضر {attended} ولم يحضر {noShow}؛ وبدأت كل مجموعة متابعتها.",
  'events.remindersSent': "أُرسل الآن {count} تذكير حان موعده.",
  'events.attendees': "الحضور",
  'events.noAttendees': "لم يسجّل أحد بعد.",
  'events.registration.registered': "مسجّل",
  'events.registration.waitlisted': "في انتظار مقعد",
  'events.registration.cancelled': "ملغى",
  'events.attendance.attended': "حضر",
  'events.attendance.no-show': "لم يحضر",
  'events.minutes': "{count} دقيقة",
  'events.cancelRegistration': "أزله من الفعالية",
  'events.attendance': "الحضور الفعلي",
  'events.attendanceHelp': "الصق تقرير الحضور من مزوّد الفيديو: شخص في كل سطر، البريد ثم دقائق الحضور.",
  'events.recordAttendance': "سجّل الحضور",
  'events.unmatched': "لم يسجّل أحد بهذه العناوين: {emails}",
  'events.close': "أغلق الفعالية وابدأ المتابعات",
  'events.rowCounts': "{registered} مسجّل · {attended} حضر",
  'events.register': "سجّل",
  'events.registrationClosed': "أُغلق التسجيل في هذه الفعالية.",
  'events.fullWaitlist': "هذه الفعالية ممتلئة. سجّل لتكون على قائمة الانتظار، وسنبلغك إن توفّر مقعد.",
  'events.youAreRegistered': "تم تسجيلك. سيصلك تذكير قبل البداية.",
  'events.youAreWaitlisted': "الفعالية ممتلئة، لذا أنت على قائمة الانتظار. سنبلغك إن توفّر مقعد.",
  'events.alreadyRegistered': "أنت مسجّل بالفعل في هذه الفعالية.",
  'event.waitlisted': "الفعالية ممتلئة؛ أنت على قائمة الانتظار.",
  'noun.event': "تلك الفعالية",
  'noun.registration': "ذلك التسجيل",
  'refusal.event.name': "أعطِ الفعالية اسمًا لا يزيد على 200 حرف.",
  'refusal.event.slug': "العنوان أحرف لاتينية صغيرة وأرقام وشرطات مفردة، مثل spring-webinar.",
  'refusal.event.when': "حدّد بداية الفعالية ونهايتها.",
  'refusal.event.endsBeforeStart': "تنتهي الفعالية بعد أن تبدأ.",
  'refusal.event.capacity': "السعة عدد صحيح من المقاعد، أو فارغة بلا حد.",
  'refusal.event.reminder': "التذكير عدد صحيح من الساعات قبل البداية، حتى أسبوعين.",
  'refusal.event.noForm': "اختر نموذج التسجيل قبل فتح التسجيل.",
  'refusal.event.form': "اختر نموذج التقاط ما زال فعّالًا.",
  'refusal.event.flow': "المتابعة رحلة فعّالة من رحلات مساحة العمل.",
  'refusal.event.slugTaken': "توجد فعالية أخرى على /{slug}.",
  'refusal.event.closed': "هذه الفعالية مغلقة؛ الحضور والمتابعات نهائية.",
  'refusal.event.notOver': "تُغلق الفعالية بعد انتهائها، حين يُعرف الحضور.",
  'refusal.event.started': "أُغلق التسجيل في هذه الفعالية.",
  'ab.title': "اختبار A/B",
  'ab.none': "لا يوجد اختبار. أعدّ اختبارًا لإرسال نسخ إلى جزء من الجمهور والنسخة الفائزة إلى الباقين.",
  'ab.setUp': "أعدّ اختبارًا",
  'ab.change': "غيّر الاختبار",
  'ab.kind': "ما الذي نختبره",
  'ab.kind.subject': "سطر الموضوع",
  'ab.kind.content': "محتوى الرسالة",
  'ab.metric': "الفائز حسب",
  'ab.metric.opened': "مرات الفتح",
  'ab.metric.clicked': "النقرات",
  'ab.testPercent': "حصة الاختبار من الجمهور (%)",
  'ab.minimumSample': "الحد الأدنى للإرسال لكل نسخة",
  'ab.windowHours': "مدة جمع النتائج (بالساعات)",
  'ab.subjectFor': "سطر الموضوع للنسخة {label}",
  'ab.emailFor': "الرسالة للنسخة {label}",
  'ab.chooseEmail': "اختر رسالة",
  'ab.addVariant': "أضف نسخة",
  'ab.removeVariant': "أزل",
  'ab.save': "احفظ الاختبار",
  'ab.cancel': "إلغاء",
  'ab.remove': "أزل الاختبار",
  'ab.status.draft': "جاهز - يبدأ عند إرسال الحملة",
  'ab.status.testing': "قيد الاختبار",
  'ab.status.decided': "أُرسلت النسخة الفائزة",
  'ab.status.inconclusive': "غير حاسم - أُرسلت النسخة الأصلية",
  'ab.summary': "{percent}% من الجمهور، الفائز حسب {metric}، بحد أدنى {minimum} إرسال لكل نسخة.",
  'ab.variant': "النسخة",
  'ab.sent': "أُرسلت",
  'ab.rate': "النسبة",
  'ab.lift': "التحسّن مقابل A",
  'ab.winner': "الفائزة",
  'ab.sentToRest': "أُرسلت للباقين",
  'ab.confidence': "الثقة بأن المتصدّرة أفضل",
  'ab.decidesAt': "يُحسم بعد {at}؛ {held} شخصًا ينتظرون النسخة الفائزة.",
  'ab.decide': "احسم الآن",
  'noun.abTest': "ذلك الاختبار",
  'refusal.ab.started': "بدأ الاختبار؛ نسخه وإعداداته ثابتة.",
  'refusal.ab.emailOnly': "اختبارات A/B لحملات البريد الإلكتروني.",
  'refusal.ab.percent': "حصة الاختبار نسبة مئوية صحيحة من 5 إلى 50.",
  'refusal.ab.sample': "الحد الأدنى 10 إرسالات لكل نسخة على الأقل.",
  'refusal.ab.window': "مدة جمع النتائج من 1 إلى 168 ساعة.",
  'refusal.ab.variantCount': "للاختبار من 2 إلى 4 نسخ.",
  'refusal.ab.labels': "لكل نسخة اسم قصير خاص بها.",
  'refusal.ab.subject': "كل نسخة تحتاج إلى سطر موضوعها.",
  'refusal.ab.same': "يجب أن تختلف النسخ، وإلا فلا شيء يُختبر.",
  'refusal.ab.document': "كل نسخة تحتاج إلى رسالتها.",
  'refusal.ab.tooSmall': "يحتاج هذا الاختبار إلى {needed} شخصًا في حصته ({variants} نسخ × {minimum})، و{percent}% من هذا الجمهور {share}. ارفع حصة الاختبار أو اخفض الحد الأدنى أو أرسل دون اختبار.",
  'refusal.ab.decided': "حُسم هذا الاختبار بالفعل.",
  'refusal.ab.window.open': "تنتهي مدة النتائج عند {at}؛ لا يُعلن فائز قبلها.",
  'refusal.ab.belowSample': "لا فائز بعد: كل نسخة تحتاج إلى {minimum} إرسال، و{detail}.",
  'nav.approvals': "الموافقات",
  'approvals.title': "الموافقات",
  'approvals.waitingOn': "بانتظار {names}",
  'approvals.status.pending': "قيد الانتظار",
  'approvals.status.approved': "تمت الموافقة",
  'approvals.status.rejected': "مرفوض",
  'approvals.status.withdrawn': "مسحوب",
  'approvals.mode.any': "يكفي موافقة واحد",
  'approvals.mode.all': "يوافق الجميع",
  'approvals.assignee.pending': "بانتظاره",
  'approvals.assignee.approved': "وافق",
  'approvals.assignee.rejected': "رفض",
  'approvals.assignee.delegated': "أحاله",
  'approvals.assignee.escalated': "صُعّد",
  'approvals.assignee.closed': "لم يعد مطلوبًا",
  'approvals.onBehalfOf': "نيابة عن {name}",
  'approvals.history': "السجل",
  'approvals.system': "النظام",
  'approvals.action.submitted': "قدّم الطلب",
  'approvals.action.approved': "وافق",
  'approvals.action.rejected': "رفض",
  'approvals.action.delegated': "أحال",
  'approvals.action.escalated': "صعّد",
  'approvals.action.recalled': "سحب الطلب",
  'approvals.action.completed': "اكتمل",
  'approvals.viaEmail': "من البريد",
  'approvals.comment': "تعليق (مطلوب عند الرفض)",
  'approvals.approve': "وافق",
  'approvals.reject': "ارفض",
  'approvals.recall': "اسحب الطلب",
  'approvals.process': "مسار الموافقة",
  'approvals.chooseProcess': "اختر مسارًا",
  'approvals.submit': "قدّم للموافقة",
  'approvals.askedBy': "طلبه {name}",
  'approvals.handTo': "أحِل إلى…",
  'approvals.handOn': "أحِل",
  'approvals.inboxEmpty': "لا شيء بانتظارك.",
  'approvals.inboxEmptyBody': "تظهر هنا الطلبات التي تحتاج موافقتك، مع بقية أفراد السلسلة.",
  'approvals.away': "أثناء غيابي",
  'approvals.awaySet': "تذهب موافقاتك إلى مفوَّضك من {from} إلى {to}.",
  'approvals.awayHelp': "اختر من يتلقى موافقاتك أثناء غيابك.",
  'approvals.delegate': "المفوَّض",
  'approvals.chooseDelegate': "اختر شخصًا",
  'approvals.from': "من",
  'approvals.until': "حتى",
  'approvals.saveAway': "احفظ",
  'approvals.clearAway': "أوقف التفويض",
  'approvals.processes': "مسارات الموافقة",
  'approvals.newProcess': "مسار جديد",
  'approvals.noProcesses': "لا توجد مسارات بعد.",
  'approvals.object.deal': "الصفقات",
  'approvals.object.quote': "عروض الأسعار",
  'approvals.object.account': "الشركات",
  'approvals.object.contact': "جهات الاتصال",
  'approvals.object.lead': "العملاء المحتملون",
  'approvals.stepCount': "{count} خطوة",
  'approvals.inactive': "متوقف",
  'approvals.edit': "تعديل",
  'approvals.processName': "الاسم",
  'approvals.appliesTo': "ينطبق على",
  'approvals.active': "فعّال",
  'approvals.criteria': "شروط الدخول",
  'approvals.criteriaHelp': "يجب أن يطابقها السجل ليُقدَّم. بلا شروط يعني كل السجلات.",
  'approvals.stepName': "الخطوة {number}",
  'approvals.approvers': "الموافِقون",
  'approvals.kind.owner-manager': "مدير المالك",
  'approvals.kind.users': "أشخاص محددون",
  'approvals.kind.role': "كل من في دور",
  'approvals.modeLabel': "تكتمل عندما",
  'approvals.chooseRole': "اختر دورًا",
  'approvals.timeout': "صعّد بعد (ساعات، اختياري)",
  'approvals.escalateTo': "صعّد إلى",
  'approvals.removeStep': "أزل الخطوة",
  'approvals.addStep': "أضف خطوة",
  'approvals.saveProcess': "احفظ المسار",
  'approvals.cancel': "إلغاء",
  'approvals.linkInvalid': "رابط الموافقة هذا غير صالح أو منتهي.",
  'approvals.recorded': "تم تسجيل قرارك.",
  'noun.approvalProcess': "مسار الموافقة ذلك",
  'noun.record': "ذلك السجل",
  'refusal.process.name': "أعطِ المسار اسمًا لا يزيد على 200 حرف.",
  'refusal.process.steps': "للمسار من 1 إلى 10 خطوات.",
  'refusal.process.stepName': "الخطوة {step} تحتاج إلى اسم.",
  'refusal.process.noApprover': "الخطوة {step} تحتاج إلى موافِق واحد على الأقل.",
  'refusal.process.noRole': "الخطوة {step} تحتاج إلى دورها.",
  'refusal.process.noEscalation': "الخطوة {step} لها مهلة، فتحتاج إلى من تُصعَّد إليه.",
  'refusal.process.criteria': "لا يمكن استخدام شروط الدخول: {problem}",
  'refusal.process.nobody': "لا يوجد من يوافق على الخطوة \"{step}\".",
  'refusal.process.inactive': "{name} متوقف.",
  'refusal.process.criteriaNotMet': "{record} لا يستوفي شروط {process}.",
  'refusal.process.alreadyPending': "{record} بانتظار موافقة بالفعل.",
  'refusal.process.decided': "هذا الطلب {status} بالفعل.",
  'refusal.process.notYours': "هذه الخطوة بانتظار {names}، لا أنت.",
  'refusal.process.reason': "اذكر سبب الرفض - الرفض بلا سبب رسالة سيضطر أحد للسؤال عنها.",
  'refusal.process.recallOwn': "لا يسحب الطلب إلا من قدّمه.",
  'refusal.process.delegateOwn': "لا يحيل الخطوة إلا من تنتظره.",
  'refusal.process.delegateSelf': "اختر شخصًا آخر لتحيلها إليه.",
  'refusal.process.delegationDates': "ينتهي التفويض في يوم بدئه أو بعده.",
  'jobs.title': "المهام المجدولة",
  'jobs.help': "أعمال تنفذها مساحة العمل وفق جدول. يمكن تشغيل كل مهمة الآن للتحقق منها.",
  'jobs.status.succeeded': "نجح آخر تشغيل",
  'jobs.status.failed': "فشل آخر تشغيل",
  'jobs.status.missed': "فائت",
  'jobs.status.running': "قيد التشغيل",
  'jobs.next': "التالي {at}",
  'jobs.noNext': "لا تشغيل قادم",
  'jobs.off': "متوقفة",
  'jobs.runNow': "شغّل الآن",
  'jobs.details': "التفاصيل",
  'jobs.hide': "إخفاء",
  'jobs.last': "آخر تشغيل {at}، {ms} مللي ثانية: {detail}",
  'jobs.schedule': "الجدول (cron بتوقيت مساحة العمل)",
  'jobs.enabled': "مفعّلة",
  'jobs.save': "احفظ",
  'jobs.cronHelp': "الدقيقة، الساعة، يوم الشهر، الشهر، يوم الأسبوع. \"0 8 * * 1-5\" تعني 08:00 أيام العمل؛ و\"*/15 * * * *\" كل خمس عشرة دقيقة.",
  'jobs.upcoming': "التشغيلات القادمة: {times}",
  'jobs.trigger.schedule': "حسب الجدول",
  'jobs.trigger.manual': "تشغيل يدوي",
  'noun.jobSchedule': "تلك المهمة المجدولة",
  'refusal.job.cron': "لا يمكن استخدام هذا الجدول: {reason}.",
  'refusal.job.never': "هذا الجدول لا يعمل أبدًا.",
  'refusal.job.running': "هذه المهمة قيد التشغيل بالفعل.",
  'webhooks.title': "الويب هوك",
  'webhooks.secretOnce': "سر التوقيع - يظهر مرة واحدة، انسخه الآن:",
  'webhooks.new': "ويب هوك جديد",
  'webhooks.name': "الاسم",
  'webhooks.url': "العنوان",
  'webhooks.events': "الأحداث",
  'webhooks.create': "أضف الويب هوك",
  'webhooks.inbound': "الأحداث الواردة",
  'webhooks.inboundHelp': "يرسل نظام آخر كائن JSON مسطحًا إلى عنوان المصدر؛ فيبدأ الأتمتة المختارة بتلك الحقول.",
  'webhooks.noAutomation': "(حُذفت الأتمتة)",
  'webhooks.off': "متوقف",
  'webhooks.tokenOnce': "عنوان المصدر - يظهر مرة واحدة، انسخه الآن:",
  'webhooks.sourceName': "اسم المصدر",
  'webhooks.automation': "الأتمتة",
  'webhooks.chooseAutomation': "اختر أتمتة",
  'webhooks.createSource': "أضف المصدر",
  'webhooks.countNote': "{count} نقطة نهاية. كل تسليم موقّع (X-Whinerp-Signature) ويُعاد بفواصل متزايدة.",
  'webhooks.recent': "آخر 7 أيام: {ok} سُلّم، {failed} فشل",
  'webhooks.test': "أرسل تجربة",
  'webhooks.log': "سجل التسليم",
  'webhooks.hideLog': "إخفاء السجل",
  'webhooks.status.pending': "قيد الانتظار",
  'webhooks.status.succeeded': "سُلّم",
  'webhooks.status.failed': "فشل - ستُعاد المحاولة",
  'webhooks.status.dead': "توقف بعد كل المحاولات",
  'webhooks.nextTry': "المحاولة التالية {at}",
  'webhooks.replay': "أعد الإرسال",
  'webhooks.payloadAndAttempts': "المحتوى و{count} محاولة",
  'noun.webhook': "ذلك الويب هوك",
  'noun.webhookDelivery': "ذلك التسليم",
  'refusal.webhook.url': "هذا ليس عنوان ويب.",
  'refusal.webhook.https': "يذهب الويب هوك إلى عنوان https://.",
  'refusal.webhook.private': "لا يمكن أن يشير الويب هوك إلى عنوان شبكة خاصة.",
  'refusal.webhook.name': "أعطِ الويب هوك اسمًا.",
  'refusal.webhook.events': "اختر حدثًا واحدًا على الأقل.",
  'refusal.webhook.unknownEvent': "ليس حدثًا يرسله هذا المنتج: {events}.",
  'refusal.inbound.name': "أعطِ المصدر اسمًا.",
  'refusal.inbound.flow': "يبدأ المصدر الوارد إحدى أتمتات مساحة العمل.",
  'stream.title': "تدفق الأحداث",
  'stream.help': "يقرأ المشتركون أحداث السجلات والأحداث المخصصة بالترتيب من موقعهم، ويؤكدون ما عالجوه، ويمكنهم الإعادة.",
  'stream.behind': "متأخر بـ {count}؛ أقدمها ينتظر {minutes} دقيقة",
  'stream.caughtUp': "محدَّث",
  'stream.position': "عند {cursor} من {latest}",
  'stream.polled': "آخر قراءة {at}",
  'stream.neverPolled': "لم يُقرأ قط",
  'stream.replayAll': "أعد من البداية",
  'stream.name': "اسم المشترك",
  'stream.events': "الأحداث، مثل deal.* أو contact.created",
  'stream.fromStart': "اقرأ الأحداث الموجودة أيضًا",
  'stream.add': "أضف مشتركًا",
  'noun.eventSubscriber': "ذلك المشترك",
  'refusal.stream.name': "يُسمّى الحدث المخصص بأحرف لاتينية صغيرة وشرطات سفلية، مثل order_shipped.",
  'refusal.stream.subscriberName': "أعطِ المشترك اسمًا.",
  'refusal.stream.pattern': "الأحداث مثل deal.* أو contact.created أو custom.* أو *.",
  'recipes.browse': "تصفّح القوالب الجاهزة",
  'recipes.title': "قوالب الأتمتة",
  'backup.notRestorable': 'هذه الجداول موجودة في النسخة الاحتياطية لكن لا يمكن استعادتها، لذا ستبقى كما هي:',
  'viewings.title': "المعاينات والزيارات الميدانية",
  'viewings.none': "لا توجد معاينات محجوزة بعد.",
  'viewings.status.planned': "محجوزة",
  'viewings.status.arrived': "تم الوصول",
  'viewings.status.done': "تمت",
  'viewings.status.missed': "فاتتنا",
  'viewings.status.no-show': "لم يحضروا",
  'viewings.status.cancelled': "ملغاة",
  'viewings.status.rescheduled': "نُقلت",
  'viewings.interest': "مدى الاهتمام",
  'viewings.interest.hot': "مهتم جداً",
  'viewings.interest.warm': "مهتم",
  'viewings.interest.cold': "فاتر",
  'viewings.interest.not-interested': "غير مهتم",
  'viewings.outcome': "سجّل ما حدث",
  'viewings.note': "ملاحظة",
  'viewings.held': "تمت المعاينة",
  'viewings.noShow': "لم يحضروا",
  'viewings.cancel': "إلغاؤها",
  'viewings.moved': "نُقلت {count} مرة.",
  'viewings.starts': "تبدأ",
  'viewings.ends': "تنتهي",
  'viewings.location': "المكان",
  'viewings.book': "احجز معاينة",
  'dealDocuments.title': "المستندات",
  'dealDocuments.unsorted': "غير مصنّف بعد",
  'dealDocuments.kind': "نوع المستند",
  'dealDocuments.kind.proposal': "العروض",
  'dealDocuments.kind.drawing': "المخططات",
  'dealDocuments.kind.specification': "المواصفات",
  'dealDocuments.kind.contract': "العقود",
  'dealDocuments.kind.other': "أخرى",
  'dealDocuments.version': "الإصدار {n}",
  'dealDocuments.onQuote': "على عرض السعر {number}",
  'dealDocuments.generated': "مُنشأ تلقائياً",
  'activityOutcomes.title': "ما سبق الصفقات الرابحة",
  'activityOutcomes.help': "الصفقات المغلقة خلال العام الماضي: نسبة الفوز لمن سبقها كل نوع من الأنشطة قبل الإغلاق، مقابل من لم يسبقها. يبيّن ما جرى عادةً، لا ما سبّب الفوز.",
  'activityOutcomes.none': "لا توجد صفقات مغلقة بها أنشطة بعد.",
  'activityOutcomes.activity': "النشاط",
  'activityOutcomes.withIt': "نسبة الفوز معه",
  'activityOutcomes.withoutIt': "نسبة الفوز بدونه",
  'activityOutcomes.beforeWin': "المعتاد قبل الفوز",
  'activityOutcomes.beforeLoss': "المعتاد قبل الخسارة",
  'activityOutcomes.tooFew': "صفقات أقل من أن يُحكم عليها (أقل من {n} في أحد الجانبين)",
  'billingPlan.title': "خطة الدفع",
  'billingPlan.help': "قسّم كل طلب إلى دفعة مقدمة ودفعات مراحل. الفاتورة المبدئية تطلب الدفع دون أن تكون فاتورة ضريبية، ولا يُحتسب شيء مستحقاً حتى تُحوَّل.",
  'billingPlan.order': "الطلب {number} - {planned}% مخطط",
  'billingPlan.draft': "مسودة",
  'billingPlan.stageInvoiced': "تمت فوترتها ({number})",
  'billingPlan.stageRequested': "مطلوبة بالفاتورة المبدئية {number}",
  'billingPlan.convert': "تم الدفع - أصدر الفاتورة",
  'billingPlan.requestProforma': "اطلبها بفاتورة مبدئية",
  'billingPlan.invoice': "فوتر هذه المرحلة",
  'billingPlan.invoiced': "أُنشئت مسودة فاتورة بمبلغ {amount}. أصدرها من الفواتير.",
  'billingPlan.requested': "أُصدرت الفاتورة المبدئية {number} بمبلغ {amount}.",
  'billingPlan.converted': "أُنشئت الفاتورة بالمبلغ الذي طلبته الفاتورة المبدئية تماماً. أصدرها من الفواتير.",
  'billingPlan.label': "المرحلة",
  'billingPlan.percent': "% من الطلب",
  'billingPlan.add': "أضف مرحلة",
  'revenue.more': "المزيد عن الإيرادات",
  'commission.title': "العمولة",
  'commission.me': "أنا",
  'commission.person': "الشخص",
  'commission.period': "الفترة",
  'commission.noPlan': "{name} ليس على خطة عمولة.",
  'commission.onPlan': "على خطة {plan}",
  'commission.credited': "المُحتسب",
  'commission.target': "المستهدف",
  'commission.noTarget': "لا يوجد مستهدف - النسبة الأساسية فقط",
  'commission.attainment': "من المستهدف",
  'commission.payout': "العمولة",
  'commission.band': "الشريحة",
  'commission.amount': "المُحتسب في الشريحة",
  'commission.rate': "النسبة",
  'commission.base': "أساسية",
  'commission.above': "فوق {from}% من المستهدف",
  'commission.approved': "اعتُمد بمبلغ {amount}.",
  'commission.approvedDiffers': "اعتُمد بمبلغ {amount}. تغيّرت الصفقات منذ ذلك الحين: الفرق اليوم {difference}. يبقى الكشف المعتمد كما هو.",
  'commission.approve': "اعتمد هذا الكشف",
  'commission.deals': "الصفقات المحتسبة",
  'commission.split': "{split}% من {amount}",
  'commission.leftOut': "مستبعدة: {reason}",
  'commission.plans': "خطط العمولة",
  'commission.planRates': "{base}% أساسية؛ {tiers}",
  'commission.assignWho': "ضع على خطة:",
  'commission.putOn': "ضعه على هذه الخطة",
  'commission.planName': "اسم الخطة",
  'commission.currency': "العملة",
  'commission.basePercent': "النسبة الأساسية %",
  'commission.tiers': "المسرّعات (من % المستهدف : النسبة)",
  'commission.savePlan': "أضف خطة",
  'commission.failed': "تعذّر تحميل العمولة.",
  'capacity.title': "السعة والتغطية - {period}",
  'capacity.wanted': "التغطية المطلوبة",
  'capacity.person': "الشخص",
  'capacity.remaining': "المتبقي من الحصة",
  'capacity.pipeline': "الصفقات المفتوحة هذه الفترة",
  'capacity.coverage': "التغطية",
  'capacity.noTarget': "لا مستهدف",
  'capacity.short': "ناقصة",
  'capacity.uncovered': "{accounts} شركة، ولا أحد نشط مكلّف بها",
  'capacity.territory': "{accounts} شركة، {people} أشخاص، {each} لكل شخص",
  'capacity.headcount': "حصة {target} تحتاج {needed} أشخاص بمعدل {each} لكل شخص في الربع، وهو ما حققه الشخص فعلاً. لدى {have} مستهدفات.",
  'capacity.headcountUnknown': "التاريخ غير كافٍ لتقدير عدد الأشخاص اللازمين ({quarters} ربع من الأعمال الرابحة؛ يلزم ربعان).",
  'capacity.otherCurrency': "استُبعد {count} من المستهدفات أو مجموعات الصفقات بعملة أخرى؛ هذا يقرأ {currency} فقط.",
  'sendTime.title': "أفضل وقت للمراسلة",
  'sendTime.unknown': "لا يوجد تفاعل كافٍ بعد لتحديد ذلك. تُرسل الرسائل عند إرسالها.",
  'sendTime.person': "نحو الساعة {hour}، بناءً على {clicks} نقرة و{opens} فتح من جانبه.",
  'sendTime.workspace': "نحو الساعة {hour} - تاريخه غير كافٍ، فهذه الساعة التي يتفاعل فيها جمهورك أكثر.",
  'directMail.title': "ملف البريد والردود",
  'directMail.help': "جهّز الملف لشركة البريد. يحصل كل شخص فيه على رمز لبطاقة الرد؛ تبقى الرموز كما هي إن جهّزته مرة أخرى.",
  'directMail.rate': "أُرسل {mailed}، ورد {responded}",
  'directMail.prepare': "جهّز ملف البريد",
  'directMail.included': "{count} في الملف",
  'directMail.excluded': "استُبعد {count}: {reason}",
  'directMail.code': "رمز الرد",
  'directMail.note': "ملاحظة",
  'directMail.record': "سجّل الرد",
  'directMail.recorded': "سُجّل لـ {name}.",
  'directMail.already': "سبق أن رد {name}؛ يُحتسب مرة واحدة.",
  'capture.title': "من التقيناهم في الفعالية",
  'capture.scan': "امسح شارة",
  'capture.scanHint': "امسح بالماسح اليدوي أو الصق نص الشارة",
  'capture.firstName': "الاسم الأول",
  'capture.lastName': "اسم العائلة",
  'capture.email': "البريد الإلكتروني",
  'capture.phone': "الهاتف",
  'capture.company': "الشركة",
  'capture.jobTitle': "المسمى الوظيفي",
  'capture.interest': "الاهتمام",
  'capture.interest.hot': "مرتفع",
  'capture.interest.warm': "متوسط",
  'capture.interest.cold': "منخفض",
  'capture.note': "ما سألوا عنه",
  'capture.save': "احفظ",
  'capture.newLead': "حُفظ كعميل محتمل جديد.",
  'capture.known': "معروف مسبقاً - أُضيف إلى سجله.",
  'capture.duplicate': "سبق التقاطه - لم يُضف شيء مرتين.",
  'capture.queued': "لا اتصال: حُفظ على هذا الجهاز وسيُرسل عند توفر الاتصال.",
  'referrals.title': "الإحالات",
  'referrals.code': "رمز الإحالة الخاص به:",
  'referrals.issue': "امنحه رمز إحالة",
  'referrals.none': "لم يُحِل أحداً بعد.",
  'referrals.status.referred': "مُحال",
  'referrals.status.won': "رابحة",
  'referrals.status.lost': "خاسرة",
  'referrals.reward.pending': "مكافأة {amount} مستحقة الدفع",
  'referrals.reward.paid': "مكافأة {amount} مدفوعة",
  'referrals.markPaid': "علّمها مدفوعة",
  'referrals.referredBy': "أحاله (الرمز)",
  'referrals.link': "اربط",
  'referrals.linked': "أحاله {name}.",
  'referrals.programme': "برنامج الإحالة",
  'referrals.active': "مفعّل",
  'referrals.rewardKind': "المكافأة",
  'referrals.kind.fixed': "مبلغ ثابت لكل صفقة رابحة",
  'referrals.kind.percent': "نسبة من الصفقة الرابحة",
  'referrals.amount': "المبلغ",
  'referrals.currency': "العملة",
  'referrals.save': "احفظ",
  'budget.title': "الميزانية والإنفاق",
  'budget.planned': "المخطط",
  'budget.actual': "المنفق",
  'budget.remaining': "المتبقي",
  'budget.unknown': "غير مسجّل",
  'budget.over': "تجاوز بمقدار {amount}",
  'budget.setPlanned': "الميزانية المخططة",
  'budget.save': "احفظ",
  'budget.remove': "احذف",
  'budget.otherCurrency': "بعملة {currency} - لم تُجمع",
  'budget.amount': "المبلغ",
  'budget.description': "الغرض",
  'budget.supplier': "المورّد",
  'budget.record': "سجّل الإنفاق",
  'segment.kind.live': "حية",
  'segment.kind.static': "قائمة مجمّدة",
  'segment.list.freeze': "جمّدها كقائمة",
  'lifecycle.title': "دورة الحياة",
  'lifecycle.stage.subscriber': "مشترك",
  'lifecycle.stage.lead': "عميل محتمل",
  'lifecycle.stage.mql': "مؤهل تسويقياً",
  'lifecycle.stage.sql': "مؤهل للمبيعات",
  'lifecycle.stage.customer': "عميل",
  'lifecycle.stage.advocate': "مناصر",
  'lifecycle.reportTitle': "إلى أين وصل القادمون الجدد",
  'lifecycle.period': "وصلوا خلال",
  'lifecycle.lastDays': "آخر {days} يوماً",
  'lifecycle.move': "من {from} إلى {to}: {went} من {reached}، عادةً خلال {days} يوماً",
  'frequencyCap.title': "حد التكرار",
  'frequencyCap.help': "عبر كل الحملات والرحلات، لكل قناة. لا تُحجب أبداً الإيصالات والفواتير والرسائل المعاملاتية.",
  'frequencyCap.atMost': "بحد أقصى",
  'frequencyCap.messages': "الرسائل",
  'frequencyCap.every': "رسائل تسويقية لكل شخص كل",
  'frequencyCap.days': "الأيام",
  'frequencyCap.daysSuffix': "أيام",
  'frequencyCap.save': "احفظ",
  'journey.earlierVersions': "{count} شخصاً في منتصف نسخة سابقة. سيُكملون النسخة التي دخلوها؛ التغييرات هنا (من النسخة {version}) تصل إلى من يبدأ من الآن.",
  'influence.title': "الحملات وراء الصفقات الرابحة",
  'influence.period': "صفقات رُبحت خلال",
  'influence.lastDays': "آخر {days} يوماً",
  'influence.help': "رُبحت {deals} صفقة بقيمة {won}. كل حملة وصلت إلى أحد أطراف الصفقة قبل ربحها تحتسب الصفقة كاملة، لذا تتداخل الأرقام ولا تُجمع - الإسناد يوزعها.",
  'influence.none': "لم تصل أي حملة إلى أحد في صفقة رُبحت خلال هذه الفترة.",
  'influence.campaign': "الحملة",
  'influence.engaged': "تفاعل (فتح، نقر، رد)",
  'influence.reached': "وصلت (أُرسلت)",
  'abm.title': "الحسابات المستهدفة",
  'abm.list': "قائمة الاستهداف",
  'abm.company': "الشركة",
  'abm.tier': "الفئة",
  'abm.engaged': "المتفاعلون (30 يوماً)",
  'abm.lastTouch': "آخر تواصل",
  'abm.pipeline': "الصفقات المفتوحة",
  'abm.ofPeople': "{engaged} من {people}",
  'abm.never': "أبداً",
  'abm.emptyList': "لا شركات في هذه القائمة بعد. أضفها من سجل الشركة.",
  'abm.audience.1': "جمهور: الفئة 1",
  'abm.audience.2': "جمهور: الفئتان 1-2",
  'abm.audience.3': "جمهور: كل الفئات",
  'abm.audienceMade': "حُفظ جمهور من {count} شخصاً في الجماهير.",
  'abm.newList': "قائمة استهداف جديدة",
  'abm.create': "أنشئ",
  'abm.onLists': "قوائم الاستهداف",
  'abm.notTargeted': "ليست في أي قائمة استهداف.",
  'abm.tierN': "الفئة {tier}",
  'abm.chooseList': "اختر قائمة",
  'abm.add': "أضف",
  'dynamic.title': "من يرى كل كتلة",
  'dynamic.help': "يرى الجميع الكتلة ما لم تحددها. تظهر تذييلة إلغاء الاشتراك دائماً.",
  'dynamic.who': "من يراها",
  'dynamic.kind.everyone': "الجميع",
  'dynamic.kind.field': "من لديهم…",
  'dynamic.kind.segment': "جمهور",
  'dynamic.kind.engaged': "النقرون مؤخراً",
  'dynamic.field': "الحقل",
  'dynamic.field.country': "الدولة",
  'dynamic.field.city': "المدينة",
  'dynamic.field.company': "الشركة",
  'dynamic.field.title': "المسمى الوظيفي",
  'dynamic.field.language': "اللغة",
  'dynamic.operator': "الشرط",
  'dynamic.operator.eq': "يساوي",
  'dynamic.operator.neq': "لا يساوي",
  'dynamic.operator.set': "معروف",
  'dynamic.operator.unset': "غير معروف",
  'dynamic.value': "القيمة",
  'dynamic.inOrOut': "داخل أو خارج",
  'dynamic.inSegment': "داخل",
  'dynamic.notInSegment': "ليس داخل",
  'dynamic.clicked': "نقر رسالة خلال آخر",
  'dynamic.notClicked': "لم ينقر خلال آخر",
  'dynamic.days': "أيام",
  'dynamic.block.text': "نص",
  'dynamic.block.heading': "عنوان",
  'dynamic.block.image': "صورة",
  'dynamic.block.button': "زر",
  'dynamic.block.columns': "أعمدة",
  'dynamic.block.quote': "اقتباس",
  'dynamic.block.video': "فيديو",
  'dynamic.block.line-items': "البنود",
  'chatbot.script': "نص المحادثة",
  'chatbot.none': "بلا نص - كل محادثة تذهب مباشرة إلى شخص",
  'chatbot.save': "احفظ",
  'chatbot.try': "جرّب المحادثة",
  'chatbot.message': "الرسالة",
  'chatbot.send': "أرسل",
  'bookingPage.missing': "صفحة الحجز هذه غير متاحة.",
  'bookingPage.booked': "تم حجزك",
  'bookingPage.bookedWith': "{title} مع {owner}، {when}. تأكيد في الطريق إليك.",
  'bookingPage.addToCalendar': "أضفه إلى تقويمك",
  'bookingPage.refused.taken': "حجز أحدهم ذلك الوقت بينما كنت تختار. اختر وقتاً آخر - ما كتبته محفوظ.",
  'bookingPage.refused.not-offered': "هذا الوقت غير معروض. اختر أحد الأوقات الظاهرة.",
  'bookingPage.refused.missing': "صفحة الحجز هذه غير متاحة.",
  'bookingPage.noSlots': "لا أوقات متاحة خلال الأسبوعين القادمين.",
  'bookingPage.name': "اسمك",
  'bookingPage.email': "بريدك الإلكتروني",
  'bookingPage.note': "أي شيء تود إخبارهم به أولاً؟",
  'bookingPage.confirm': "احجز هذا الوقت",
  'bookingPages.title': "ساعات عملك وصفحات الحجز",
  'bookingPages.help': "الساعات التي يمكن حجزك فيها، والصفحات التي يُحجز منها.",
  'bookingPages.day.0': "الأحد",
  'bookingPages.day.1': "الإثنين",
  'bookingPages.day.2': "الثلاثاء",
  'bookingPages.day.3': "الأربعاء",
  'bookingPages.day.4': "الخميس",
  'bookingPages.day.5': "الجمعة",
  'bookingPages.day.6': "السبت",
  'bookingPages.from': "من",
  'bookingPages.to': "إلى",
  'bookingPages.length': "مدة الاجتماع",
  'bookingPages.minutes': "{n} دقيقة",
  'bookingPages.saveHours': "احفظ الساعات",
  'bookingPages.pageTitle': "عنوان الصفحة",
  'bookingPages.address': "العنوان",
  'bookingPages.create': "أنشئ صفحة حجز",
  'inbound.outcome.case-created': "فُتحت حالة",
  'inbound.outcome.case-threaded': "أُضيفت إلى حالتها",
  'inbound.purpose': "ما يستقبله",
  'inbound.purpose.leads': "استفسارات - تصبح عملاء محتملين",
  'inbound.purpose.cases': "دعم - تصبح حالات",
  'chat.toCase': "حوّلها إلى حالة",
  'chat.caseMade': "فُتحت {number}",
  'chat.caseExists': "موجودة مسبقاً {number}",
  'case.new': "سجّل حالة",
  'case.cameIn': "وصلت عبر",
  'case.source.phone': "الهاتف",
  'case.source.manual': "حضورياً",
  'case.source.email': "البريد الإلكتروني",
  'case.source.chat': "المحادثة",
  'case.source.portal': "البوابة",
  'case.whatTheySaid': "ما قالوه",
  'case.log': "سجّل الحالة",
  'portal.help.title': "اطلب المساعدة",
  'portal.help.subject': "ما الموضوع؟",
  'portal.help.body': "ماذا حدث؟",
  'portal.help.send': "أرسل",
  'portal.help.raised': "شكراً - رقم طلبك {number}. اذكره إن راسلتنا بشأنه.",
  'survey.thanks': "شكراً - هذا يساعدنا.",
  'survey.already': "تمت الإجابة على هذا مسبقاً. شكراً لك.",
  'survey.missing': "رابط الاستبيان هذا غير صالح أو منتهي.",
  'survey.question.csat': "ما مدى رضاك عن طريقة تعاملنا مع هذا؟",
  'survey.question.nps': "ما مدى احتمال أن توصي بنا لزميل؟",
  'survey.question.ces': "ما مدى سهولة حل هذه المسألة؟",
  'survey.scale.csat': "1 = غير راضٍ إطلاقاً، 5 = راضٍ جداً",
  'survey.scale.nps': "0 = غير محتمل إطلاقاً، 10 = محتمل جداً",
  'survey.scale.ces': "1 = صعب جداً، 7 = سهل جداً",
  'survey.comment': "هل تود إضافة شيء؟",
  'survey.send': "أرسل",
  'survey.panel': "رضا العملاء",
  'survey.measure': "المقياس",
  'survey.off': "الاستبيانات متوقفة",
  'survey.kind.csat': "رضا العملاء (CSAT)",
  'survey.kind.nps': "صافي نقاط الترويج (NPS)",
  'survey.kind.ces': "الجهد (CES)",
  'survey.summary': "{measure} {score} - أجاب {answered} من {sent}، آخر 90 يوماً",
  'survey.unassigned': "غير معيّنة",
  'custHealth.title': "صحة العميل",
  'custHealth.noData': "لا يوجد ما يكفي لتقييمه بعد.",
  'custHealth.score': "{score} - {band}",
  'custHealth.band.healthy': "بصحة جيدة",
  'custHealth.band.watch': "تستحق المتابعة",
  'custHealth.band.at-risk': "معرّض للخطر",
  'custHealth.signal.usage': "الاستخدام",
  'custHealth.signal.support': "الدعم",
  'custHealth.signal.engagement': "التواصل",
  'custHealth.signal.payment': "الدفع",
  'custHealth.missing': "غير مقيّم لعدم وجود بيانات: {signals}.",
  'custHealth.listTitle': "عملاء يحتاجون اهتماماً",
  'custHealth.allWell': "يبدو كل العملاء الـ {count} بصحة جيدة.",
  'custOnboarding.title': "التهيئة",
  'custOnboarding.none': "لا تجري تهيئته حالياً.",
  'custOnboarding.start': "ابدأ التهيئة",
  'custOnboarding.complete': "اكتملت التهيئة.",
  'custOnboarding.progress': "أُنجزت {done} من {total} خطوات",
  'custOnboarding.doneBy': "أنجزها {name}",
  'custOnboarding.overdue': "متأخرة منذ {date}",
  'custOnboarding.due': "تستحق {date}",
  'custOnboarding.template': "خطوات تهيئة العملاء الجدد",
  'custOnboarding.templateHelp': "تبدأ هذه الخطوات للعميل عند ربح الصفقة. تحتفظ الخطط الجارية بخطواتها.",
  'custOnboarding.stepName': "الخطوة",
  'custOnboarding.days': "الأيام",
  'custOnboarding.daysAfter': "يوماً بعد الربح",
  'custOnboarding.addStep': "أضف خطوة",
  'custOnboarding.save': "احفظ الخطوات",
  'success.title': "خطة النجاح",
  'success.reviewOverdue': "مراجعة الأعمال مستحقة الآن.",
  'success.reviewDue': "مراجعة الأعمال القادمة مستحقة في {date}.",
  'success.status': "الحالة",
  'success.status.on-track': "على المسار",
  'success.status.at-risk': "معرّض للخطر",
  'success.status.achieved': "تحقق",
  'success.status.dropped': "أُلغي",
  'success.objective': "الهدف",
  'success.measure': "كيف سيعرف الطرفان أنه تحقق",
  'success.due': "الاستحقاق",
  'success.addObjective': "أضف هدفاً",
  'success.agreed': "تم الاتفاق: {text}",
  'success.reviewSummary': "ما غطته المراجعة",
  'success.reviewAgreed': "ما تم الاتفاق عليه",
  'success.recordReview': "سجّل مراجعة اليوم",
  'dispatch.title': "المهام الميدانية",
  'dispatch.day': "اليوم",
  'dispatch.none': "لا مهام ميدانية في هذا اليوم.",
  'dispatch.technician': "الفني",
  'dispatch.unassigned': "لم يُعيّن أحد",
  'dispatch.caseTitle': "الزيارات الميدانية",
  'dispatch.report': "ما أُنجز في الموقع",
  'dispatch.complete': "علّمها منجزة",
  'dispatch.starts': "تبدأ",
  'dispatch.ends': "تنتهي",
  'dispatch.location': "المكان",
  'dispatch.schedule': "احجز زيارة",
  'installed.title': "الأصول المركّبة",
  'installed.cover.under-warranty': "ضمن الضمان حتى {date}",
  'installed.cover.expired': "انتهى الضمان في {date}",
  'installed.cover.none': "لا ضمان مسجّل",
  'installed.name': "ما هو",
  'installed.serial': "الرقم التسلسلي",
  'installed.location': "مكانه",
  'installed.installed': "تاريخ التركيب",
  'installed.register': "سجّل الأصل",
  'installed.caseTitle': "الأصل",
  'installed.noneChosen': "لا تتعلق بأصل مسجّل",
  'complaints.title': "سجل الشكاوى",
  'complaints.policy': "الإقرار خلال {ack} أيام؛ الرد النهائي خلال {respond} يوماً.",
  'complaints.acknowledged': "أُقرّ بها {date}",
  'complaints.ackDue': "الإقرار قبل {date}",
  'complaints.responded': "الرد النهائي {date}",
  'complaints.respondDue': "الرد النهائي مستحق {date}",
  'complaints.caseTitle': "شكوى رسمية",
  'complaints.category': "موضوع الشكوى",
  'complaints.mark': "سجّلها كشكوى",
  'complaints.finalPlaceholder': "الرد النهائي المكتوب للعميل",
  'complaints.sendFinal': "أرسل الرد النهائي",
  'dealAnalysis.title': "الصفقات المغلقة حسب الشريحة",
  'dealAnalysis.by': "التفصيل حسب",
  'dealAnalysis.period': "أُغلقت خلال",
  'dealAnalysis.lastDays': "آخر {days} يوماً",
  'dealAnalysis.dimension.owner': "المسؤول",
  'dealAnalysis.dimension.source': "المصدر",
  'dealAnalysis.dimension.industry': "القطاع",
  'dealAnalysis.dimension.segment': "الشريحة",
  'dealAnalysis.dimension.size': "حجم الصفقة",
  'dealAnalysis.closed': "المغلقة",
  'dealAnalysis.winRate': "نسبة الفوز",
  'dealAnalysis.wonValue': "قيمة الفوز",
  'dealAnalysis.avgSize': "متوسط الفوز",
  'dealAnalysis.daysToWin': "أيام حتى الفوز",
  'dealAnalysis.few': "(قليلة جداً لاستنتاج الكثير)",
  'dealAnalysis.lossReasons': "أسباب خسارة {lost}",
  'dealAnalysis.competitors': "مقابل المنافسين",
  'dealAnalysis.record': "فزنا {won}، خسرنا {lost}",
  'roi.title': "العائد حسب المصدر",
  'roi.by': "التجميع حسب",
  'roi.byCampaign': "الحملة",
  'roi.bySource': "المصدر",
  'roi.campaign': "الحملة",
  'roi.source': "المصدر",
  'roi.leads': "العملاء المحتملون",
  'roi.won': "الرابحة",
  'roi.revenue': "الإيراد",
  'roi.cost': "التكلفة",
  'roi.return': "العائد",
  'roi.noCost': "غير مسجّلة",
  'marginReport.title': 'الإيرادات والهامش',
  'marginReport.by': 'التجميع حسب',
  'marginReport.group.product': 'المنتج',
  'marginReport.group.owner': 'المسؤول',
  'marginReport.group.month': 'الشهر',
  'marginReport.revenue': 'الإيرادات',
  'marginReport.cost': 'التكلفة',
  'marginReport.margin': 'الهامش',
  'marginReport.marginPct': 'نسبة الهامش',
  'marginReport.uncosted': 'إيرادات بلا تكلفة',
  'marginReport.unknown': 'غير معروف',
  'marginReport.basis': 'الطلبات المؤكدة في السنة الماضية، محسوبة بالتكلفة الحالية لكل منتج. إيرادات المنتجات بلا تكلفة معروضة على حدة ومستبعدة من الهامش.',
  'execPack.title': "ملخص الإدارة",
  'execPack.span': "من {from} إلى {to}، مقابل {prevFrom} إلى {prevTo}",
  'execPack.period': "الفترة",
  'execPack.period.month': "شهر",
  'execPack.period.quarter': "ربع سنة",
  'execPack.period.year': "سنة",
  'execPack.print': "طباعة",
  'execPack.current': "هذه الفترة",
  'execPack.previous': "السابقة",
  'execPack.change': "التغير",
  'execPack.marginWithheld': "الهامش غير معروض: ليس لديك صلاحية رؤية التكلفة.",
  'execPack.metric.bookings': "الحجوزات (قيمة الصفقات الرابحة)",
  'execPack.metric.dealsWon': "الصفقات الرابحة",
  'execPack.metric.winRate': "نسبة الفوز",
  'execPack.metric.newPipeline': "فرص جديدة",
  'execPack.metric.ordered': "الطلبات المؤكدة",
  'execPack.metric.margin': "هامش الطلبات",
  'execPack.metric.invoiced': "المفوتر",
  'execPack.metric.overdue': "المتأخر",
  'execPack.metric.casesOpened': "الحالات المفتوحة",
  'execPack.metric.casesResolved': "الحالات المحلولة",
  'execPack.lastYear': "العام الماضي",
  'execPack.baseline': "المعتاد",
  'execPack.againstBaseline': "مقابل المعتاد",
  'execPack.baselineNote': "العام الماضي هو الفترة نفسها قبل سنة. المعتاد هو متوسط الفترات الأربع السابقة.",
  'recordTrigger.title': "ما الذي يبدأها",
  'recordTrigger.object': "نوع السجل",
  'recordTrigger.choose': "اختر…",
  'recordTrigger.field': "فقط عند تغير هذا الحقل",
  'recordTrigger.anyField': "أي حقل",
  'recordTrigger.to': "إلى هذه القيمة",
  'recordTrigger.anyValue': "أي قيمة",
  'recordTrigger.note': "الأتمتة لا تعيد تشغيل نفسها: التغييرات التي تجريها لا تبدأها مرة أخرى. وإذا فشلت يبقى السجل محفوظًا ويظهر الفشل في سجل التشغيل.",
  'recordTrigger.objectName.contact': "جهة اتصال",
  'recordTrigger.objectName.account': "شركة",
  'recordTrigger.objectName.lead': "عميل محتمل",
  'recordTrigger.objectName.deal': "صفقة",
  'recordTrigger.objectName.quote': "عرض سعر",
  'recordTrigger.objectName.support_case': "حالة",
  'recordTrigger.objectName.campaign': "حملة",
  'routing.object': "ما الذي تسنده القواعد",
  'routing.object.lead': "العملاء المحتملون",
  'routing.object.support_case': "الحالات",
  'routing.priority': "فقط الحالات ذات الأولوية",
  'routing.priority.any': "أي أولوية",
  'routing.casesNote': "تذهب الحالة الجديدة إلى أول قاعدة تطابقها. الحالة التي لا تطابقها أي قاعدة تنتظر في قائمة غير المسندة، ولا تأخذ أي قاعدة حالة من الموظف الذي يعمل عليها.",
  'escalationRules.title': "قواعد التصعيد",
  'escalationRules.body': "عندما يبقى العمل دون إنجاز لفترة طويلة، يُبلَّغ المسؤول وأول دور مشغول فوقه، أو الشخص الذي تحدده. يُصعَّد كل سجل مرة واحدة لكل قاعدة، ويجري الفحص كل 15 دقيقة.",
  'escalationRules.name': "الاسم",
  'escalationRules.object': "راقب",
  'escalationRules.object.task': "المهام المتأخرة",
  'escalationRules.object.deal': "صفقات مفتوحة دون نشاط",
  'escalationRules.object.lead': "عملاء محتملون دون متابعة",
  'escalationRules.object.case': "حالات دون رد",
  'escalationRules.after': "بعد (ساعات)",
  'escalationRules.who': "أبلغ",
  'escalationRules.tellsManager': "المسؤول ومديره",
  'escalationRules.tells': "يبلغ {name}",
  'escalationRules.fired': "{count} تصعيدًا حتى الآن",
  'escalationRules.add': "أضف قاعدة",
  'escalationRules.when.task': "مهمة متأخرة {hours} ساعة",
  'escalationRules.when.deal': "صفقة مفتوحة دون أي نشاط منذ {hours} ساعة",
  'escalationRules.when.lead': "عميل محتمل دون متابعة بعد {hours} ساعة من وصوله",
  'escalationRules.when.case': "حالة دون رد بعد {hours} ساعة من فتحها",
  'setup.import.object': "ما الذي تستورده",
  'setup.import.object.account': "الشركات",
  'setup.import.object.contact': "جهات الاتصال",
  'setup.import.object.lead': "العملاء المحتملون",
  'setup.import.source': "مُصدَّر من",
  'setup.import.source.other': "جدول بيانات أو أداة أخرى",
  'setup.import.source.salesforce': "Salesforce",
  'setup.import.source.hubspot': "HubSpot",
  'setup.import.source.zoho': "Zoho CRM",
  'setup.import.source.pipedrive': "Pipedrive",
  'setup.import.file': "ملف CSV",
  'setup.import.unmatched': "لن تُستورد إلا إذا ربطتها: {columns}",
  'dataQuality.title': "جودة البيانات",
  'dataQuality.body': "مدى اكتمال السجلات وتكرارها وقِدمها. المكررات تشترك في بريد إلكتروني أو اسم شركة مطابق، والقديمة لم تُعدَّل ولم يُسجَّل عليها شيء خلال المدة.",
  'dataQuality.object.contact': "جهات الاتصال",
  'dataQuality.object.account': "الشركات",
  'dataQuality.object.lead': "العملاء المحتملون المفتوحون",
  'dataQuality.object.deal': "الصفقات المفتوحة",
  'dataQuality.complete': "مكتمل بنسبة {percent}",
  'dataQuality.duplicates': "في {groups} مجموعة مكررة",
  'dataQuality.stale': "دون أي نشاط منذ {days} يومًا",
  'dataQuality.topDuplicates': "الأكثر تكرارًا: {list}",
  'dataQuality.check.noEmail': "بلا بريد إلكتروني",
  'dataQuality.check.noPhone': "بلا هاتف",
  'dataQuality.check.noCompany': "بلا شركة",
  'dataQuality.check.noTitle': "بلا مسمى وظيفي",
  'dataQuality.check.noOwner': "بلا مسؤول",
  'dataQuality.check.noDomain': "بلا نطاق أو موقع",
  'dataQuality.check.noIndustry': "بلا قطاع",
  'dataQuality.check.noContacts': "بلا جهات اتصال",
  'dataQuality.check.noSource': "بلا مصدر",
  'dataQuality.check.noAmount': "بلا مبلغ",
  'dataQuality.check.noCloseDate': "بلا تاريخ إغلاق",
  'dataQuality.check.closeDatePassed': "تاريخ الإغلاق مضى",
  'audit.export': "صدِّر السجل",
  'audit.exported': "صُدِّر {count} إدخالًا بصيغة JSON Lines، كل سطر مربوط بتجزئة السطر السابق. آخر تجزئة {hash}… احتفظ بها للتحقق من التصدير التالي.",
  'sessions.title': "الجلسات النشطة",
  'sessions.body': "كل من سجّل الدخول، وعلى أي جهاز ومن أين. تسجيل خروج شخص ينهي جميع جلساته دفعة واحدة، ويُسجَّل السبب في سجل التدقيق.",
  'sessions.signOutOthers': "سجّل خروجي من أجهزتي الأخرى",
  'sessions.othersEnded': "تم تسجيل الخروج من {count} جهاز آخر.",
  'sessions.reason': "سبب تسجيل خروج شخص",
  'sessions.reasonFirst': "اذكر السبب أولًا",
  'sessions.count': "{count} جلسة",
  'sessions.forceSignOut': "سجّل خروجه من كل مكان",
  'sessions.unknownDevice': "جهاز غير معروف",
  'sessions.lastSeen': "آخر ظهور {when}",
  'sessions.thisOne': "هذه الجلسة",
  'sessions.support': "زيارة دعم",
  'ipAllowlist.title': "الشبكات المسموح بها",
  'ipAllowlist.body': "اتركها فارغة للسماح بالدخول من أي مكان. عند إضافة أي نطاق، يُرفض الأشخاص ورموز API من خارج جميع النطاقات. لا تتأثر زيارات الدعم.",
  'ipAllowlist.yourIp': "أنت على {ip}.",
  'ipAllowlist.off': "لا قيود: يمكن استخدام مساحة العمل من أي مكان.",
  'ipAllowlist.range': "العنوان أو النطاق",
  'ipAllowlist.label': "أي شبكة",
  'ipAllowlist.add': "اسمح",
  'evidence.title': "أدلة الأمان",
  'evidence.body': "كل ضابط مقيس من مساحة العمل هذه حتى {when}، مع موضعه المعتاد في SOC 2 وISO 27001.",
  'evidence.download': "تنزيل",
  'evidence.print': "طباعة",
  'evidence.status.met': "مستوفى",
  'evidence.status.partial': "مستوفى جزئيًا",
  'evidence.status.not-met': "غير مستوفى",
  'evidence.status.hosting': "مزوّد الاستضافة",
  'evidence.control.mfa': "عامل ثانٍ عند الدخول",
  'evidence.control.sso': "الدخول الموحد",
  'evidence.control.privilegedAccess': "صلاحيات المسؤولين",
  'evidence.control.leavers': "خروج المغادرين",
  'evidence.control.network': "تقييد الشبكة",
  'evidence.control.sessions': "مراقبة الجلسات",
  'evidence.control.apiTokens': "رموز API",
  'evidence.control.auditTrail': "سجل التدقيق",
  'evidence.control.backups': "النسخ الاحتياطي واختبار الاستعادة",
  'evidence.control.encryption': "التشفير",
  'evidence.control.secrets': "تشفير الأسرار المخزنة",
  'wsSandbox.banner': "{name} بيئة تجريبية: التغييرات هنا لا تصل إلى بيئة الإنتاج، وسجلاتها ليست عملاء حقيقيين.",
  'wsSandbox.mark': "مساحة العمل هذه بيئة تجريبية",
  'wsSandbox.how': "لإعدادها: أنشئ مساحة عمل ثانية (تسجيل منفصل)، وحدّدها كبيئة تجريبية هنا، ثم صدّر هذا الإعداد وطبّقه هناك. تنتقل خطوط المبيعات والحقول والتخطيطات ومجموعات الصلاحيات والأتمتة (كمسودات) وقواعد التوزيع والتصعيد وأسباب الإغلاق، أما السجلات فلا تنتقل أبدًا. انقل التغييرات عائدًا بالطريقة نفسها.",
  'ask.title': "اطرح سؤالًا",
  'ask.placeholder': "كم عدد الصفقات المفتوحة حسب المرحلة؟ إجمالي قيمة الصفقات حسب المسؤول؟",
  'ask.submit': "اسأل",
  'ask.byModel': "فهمه النموذج، ونُفّذ كتقرير بصلاحياتك.",
  'ask.byKeywords': "قُرئ من كلمات السؤال، ونُفّذ كتقرير بصلاحياتك.",
  'ask.modelProblem': "(تعذّر استخدام خطة النموذج: {problem})",
  'ask.save': "احفظه كتقرير",
  'account.signOut': "تسجيل الخروج",
  'account.signingOut': "جارٍ تسجيل الخروج…",
  'invoices.title': "الفواتير",
  'nurture.title': "رعاية العملاء غير الجاهزين بعد",
  'nurture.help': "يدخل العملاء المحتملون غير الجاهزين للمبيعات رحلة رعاية تلقائياً - مرة واحدة لكل منهم، وفقط إن كانوا سيتلقون رسائلها - ويخرجون فور تأهلهم تسويقياً أو تحويلهم.",
  'nurture.journey': "رحلة الرعاية",
  'nurture.off': "بلا رعاية",
  'nurture.days': "رعاية العميل الذي بقي دون الحد بعد هذا العدد من الأيام",
  'nurture.days.off': "متوقف",
  'nurture.onRejection': "رعاية العميل الذي يعيده فريق المبيعات، لهذه الأسباب:",
  'nurture.reasons': "أسباب تعني «ليس بعد»",
  'nurture.reasons.none': "لم يُختر سبب، لذا لن يُرعى أي عميل معاد.",
  'nurture.save': "حفظ الرعاية",
  'nurture.wayIn.rejected': "أعاده فريق المبيعات",
  'nurture.wayIn.not-ready': "غير جاهز بعد",
  'nurture.inNow': "في الرعاية الآن",
  'nurture.left': "خرج: {reason}",
  'journey.emails': "ما تقوله الرسائل",
  'journey.emails.help': "كل خطوة بريد تُرسل إلى الشخص في الرحلة إذا وافق على رسائل التسويق. تُملأ {firstName} و{lastName} و{company} لكل شخص.",
  'journey.emails.step': "الرسالة {n}",
  'journey.emails.empty': "لم يُكتب شيء بعد - لن تُرسل هذه الخطوة",
  'journey.emails.subject': "الموضوع",
  'journey.emails.body': "الرسالة",
  'inbound.title': "صناديق البريد الواردة",
  'inbound.help': "البريد المرسل إلى صندوق يصبح عميلاً محتملاً، أو يُضاف إلى الشخص المعروف أنه مرسله. تُتجاهل الردود الآلية ورسائل الارتداد، وتبقى الردود مع محادثتها. يرسل مزوّد البريد الرسائل إلى هنا عبر خطاف الوارد.",
  'inbound.empty': "لا توجد صناديق بريد واردة بعد.",
  'inbound.label': "اسم الصندوق",
  'inbound.create': "إنشاء صندوق",
  'inbound.created.once': "انسخها الآن. لن تظهر مرة أخرى - لا يُحفظ إلا بصمة العنوان.",
  'inbound.created.url': "أرسل البريد إلى (على عنوان واجهة البرمجة لمساحة العمل):",
  'inbound.created.secret': "مفتاح التوقيع (HMAC-SHA256 للمحتوى، في ترويسة x-whinerp-signature):",
  'inbound.created.done': "نسختها",
  'inbound.state.on': "مفعّل",
  'inbound.state.off': "موقوف",
  'inbound.received': "وصل {count}",
  'inbound.switchOff': "إيقاف",
  'inbound.switchOn': "تفعيل",
  'inbound.recent': "البريد الأخير",
  'inbound.recent.none': "لم يصل شيء بعد.",
  'inbound.outcome.lead-created': "عميل محتمل جديد",
  'inbound.outcome.lead-matched': "أُضيف إلى عميله المحتمل",
  'inbound.outcome.contact-matched': "أُضيف إلى جهة اتصاله",
  'inbound.outcome.threaded': "أُضيف إلى المحادثة",
  'inbound.outcome.ignored': "تم تجاهله",
  'customerHistory.title': "السجل",
  'customerHistory.filter': "عرض",
  'customerHistory.empty': "لا يوجد شيء مسجّل مع هذه الشركة بعد.",
  'customerHistory.more': "عرض الأقدم",
  'customerHistory.withheld': "{count} من {kind} لا تملك صلاحية الاطلاع عليها.",
  'customerHistory.balance.clear': "لا يوجد مبلغ مستحق.",
  'customerHistory.balance.detail': "مستحق على {invoices} فاتورة، منها {overdue} متأخرة",
  'customerHistory.kind.all': "الكل",
  'customerHistory.kind.deal': "الصفقات",
  'customerHistory.kind.quote': "عروض الأسعار",
  'customerHistory.kind.order': "الطلبات",
  'customerHistory.kind.invoice': "الفواتير",
  'customerHistory.kind.payment': "المدفوعات",
  'customerHistory.kind.ticket': "التذاكر",
  'customerHistory.kind.campaign': "الحملات",
  'customerHistory.kind.subscription': "الاشتراكات",
  'customerPrefs.title': "التفضيلات",
  'customerPrefs.edit': "تعديل",
  'customerPrefs.save': "حفظ التفضيلات",
  'customerPrefs.cancel': "إلغاء",
  'customerPrefs.notSaid': "غير محدد",
  'customerPrefs.source.contact': "خاص به",
  'customerPrefs.source.account': "من الشركة",
  'customerPrefs.source.workspace': "الافتراضي لمساحة العمل",
  'customerPrefs.source.none': "غير مضبوط",
  'customerPrefs.terms': "شروط الدفع",
  'customerPrefs.terms.days': "{days} يوماً",
  'customerPrefs.terms.field': "شروط الدفع (بالأيام)",
  'customerPrefs.terms.default': "الافتراضي لمساحة العمل",
  'customerPrefs.language': "اللغة",
  'customerPrefs.language.field': "اللغة (حرفان)",
  'customerPrefs.language.en': "الإنجليزية",
  'customerPrefs.language.ar': "العربية",
  'customerPrefs.language.notHonoured': "يفضّل «{stated}»، ولا يمكن كتابة المستندات بها، لذا تصدر المستندات بـ{used}.",
  'customerPrefs.channel': "يفضّل التواصل عبر",
  'customerPrefs.channel.email': "البريد الإلكتروني",
  'customerPrefs.channel.phone': "الهاتف",
  'customerPrefs.channel.sms': "الرسائل النصية",
  'customerPrefs.channel.whatsapp': "واتساب",
  'customerPrefs.channel.post': "البريد",
  'customerPrefs.channel.in-person': "شخصياً",
  'customerPrefs.channel.notConsent': "تفضيل وليس موافقة: التسويق ما زال يحتاج إذنه.",
  'customerPrefs.delivery': "تُرسل المستندات عبر",
  'customerPrefs.delivery.email': "البريد الإلكتروني",
  'customerPrefs.delivery.portal': "بوابة العملاء",
  'customerPrefs.delivery.post': "البريد",
  'customerPrefs.instructions': "تعليمات التسليم",
  'relmap.title': "من يتبع من",
  'relmap.add': "إضافة علاقة",
  'relmap.empty': "لا يوجد أحد مسجّل من هذه الشركة بعد.",
  'relmap.withheld': "شخص لا تملك صلاحية الاطلاع عليه",
  'relmap.unnamed': "بلا اسم",
  'relmap.unknown': "غير معروف",
  'relmap.dotted': "يتبع أيضاً {name} (خط منقّط)",
  'relmap.influences': "يؤثر في {name}",
  'relmap.remove': "إزالة",
  'relmap.removeLine': "إزالة هذه العلاقة",
  'relmap.removeManager': "إزالة خط التبعية",
  'relmap.kind': "العلاقة",
  'relmap.kind.reports-to': "يتبع",
  'relmap.kind.dotted-line': "له خط منقّط إلى",
  'relmap.kind.influences': "يؤثر في",
  'relmap.from': "الشخص",
  'relmap.to': "الشخص الآخر",
  'relmap.choose': "اختر…",
  'relmap.save': "حفظ العلاقة",
  'relmap.cancel': "إلغاء",
  'relmap.role.decision-maker': "صاحب القرار",
  'relmap.role.champion': "المناصر",
  'relmap.role.blocker': "المعارض",
  'relmap.role.influencer': "المؤثر",
  'relmap.role.user': "المستخدم",
  'relmap.role.technical': "الفني",
  'address.title': "العناوين",
  'address.add': "إضافة عنوان",
  'address.empty': "لا توجد عناوين بعد.",
  'address.default': "افتراضي",
  'address.makeDefault': "جعله افتراضياً",
  'address.remove': "إزالة",
  'address.noDefault': "لا يوجد الآن عنوان {kind} افتراضي. اختر واحداً لتعرف المستندات وجهتها.",
  'address.kind': "النوع",
  'address.kind.billing': "الفوترة",
  'address.kind.shipping': "الشحن",
  'address.kind.site': "الموقع",
  'address.kind.branch': "الفرع",
  'address.label': "الاسم (اختياري)",
  'address.line1': "سطر العنوان 1",
  'address.line2': "سطر العنوان 2",
  'address.city': "المدينة",
  'address.region': "الإمارة / المنطقة",
  'address.postcode': "الرمز البريدي",
  'address.country': "رمز الدولة (حرفان)",
  'address.save': "حفظ العنوان",
  'address.cancel': "إلغاء",
  'palette.group.create': "إنشاء",
  'palette.group.go': "انتقال إلى",
  'palette.create.contact': "جهة اتصال جديدة",
  'palette.create.contact.hint': "إضافة شخص",
  'palette.create.product': "منتج جديد",
  'palette.create.product.hint': "إضافة إلى الكتالوج",
  'palette.empty': "لا شيء يطابق «{query}».",
  'palette.searchHint': "تبحث عن سجل؟ اضغط / للبحث.",
  'palette.keys': "↑ ↓ للتنقل · Enter للتنفيذ · Esc للإغلاق",
  'renewals.title': "التجديدات",
  'renewals.help': "كل فترة اشتراك قادمة تُتابَع كصفقة. كسبها أو خسارتها على اللوحة هو ما يمدّد الفترة أو يوقف تجديدها.",
  'renewals.open': "فتح التجديدات الآن",
  'renewals.opened': "فُتح {count} تجديد",
  'renewals.atRisk': "ستنقضي ما لم يكسبها أحد",
  'renewals.atRisk.help': "مستحقة قريباً، ولم تُلتزم بعد، ومضبوطة على عدم التجدد تلقائياً.",
  'renewals.atRisk.none': "لا شيء معرّض للانقضاء.",
  'renewals.daysLeft': "متبقٍ {days} يوماً",
  'renewals.total': "{count} تجديد قيد المتابعة، بقيمة {amount} {currency} سنوياً. هذا إيراد قائم لا حجوزات جديدة، ويُستبعد من التوقعات والحصص.",
  'renewals.otherCurrency': "{count} تجديد بعملة أخرى محتسبة عدداً ومستبعدة من الإجمالي.",
  'renewals.empty': "لا توجد تجديدات قادمة",
  'renewals.empty.help': "تُفتح صفقة تجديد لكل فترة اشتراك عند دخولها فترة الإشعار.",
  'economics.title': "اقتصاديات الوحدة",
  'economics.help': "ما يساويه العميل مقابل تكلفة كسبه. كل رقم هنا إما قياس أو توقّع أو غير متاح — ويُذكر أيّها هو.",
  'economics.ltv': "القيمة العمرية المتوقعة",
  'economics.ltv.detail': "ما سيساويه العميل لو استمر معدل الفقد الحالي إلى الأبد. ليست مالاً بحوزة أحد.",
  'economics.cac': "تكلفة التسويق لكل عميل",
  'economics.cac.detail': "إنفاق الحملات فقط. رواتب المبيعات والمصاريف العامة غير محتسبة هنا.",
  'economics.ratio': "القيمة إلى التكلفة",
  'economics.ratio.detail': "القيمة العمرية المتوقعة مقسومة على ما أُنفق لكسب عميل.",
  'economics.payback': "استرداد التكلفة",
  'economics.payback.detail': "أشهر الإيراد اللازمة لاسترداد تكلفة كسب عميل. إيراد لا هامش.",
  'economics.months': "شهراً",
  'economics.caveats': "ما لا تشمله هذه الأرقام",
  'economics.campaigns': "بحسب الحملة",
  'economics.campaigns.help': "يأتي الإسناد من تقرير الإسناد نفسه، فلا تتعارض هذه الشاشة معه.",
  'economics.column.campaign': "الحملة",
  'economics.column.cost': "التكلفة",
  'economics.column.deals': "الصفقات المكسوبة",
  'economics.column.perDeal': "لكل صفقة",
  'economics.cost.unknown': "غير مسجلة",
  'economics.empty': "لا توجد بيانات كافية بعد",
  'economics.empty.help': "بعد تشغيل الاشتراكات وتسجيل تكاليف الحملات ستظهر هنا اقتصاديات الوحدة.",
  'churn.title': "الفقد والاحتفاظ",
  'churn.help': "لا يوجد معدل فقد واحد. فقدان العملاء وفقدان الإيراد أمران مختلفان يتعارضان كثيراً، وهذا التعارض هو الخلاصة عادةً.",
  'churn.logo': "العملاء المفقودون",
  'churn.logo.detail': "{lost} من {opening} كانوا يدفعون في البداية",
  'churn.net': "صافي الاحتفاظ بالإيراد",
  'churn.net.detail': "قيمة العملاء الموجودين أصلاً الآن. ما يتجاوز 100% يعني أن التوسع فاق الخسائر.",
  'churn.gross': "إجمالي الاحتفاظ بالإيراد",
  'churn.gross.detail': "الخسائر فقط، فلا يتجاوز 100% أبداً.",
  'churn.no-rate': "لا يوجد معدل — لم يكن أحد يدفع",
  'churn.cohorts': "الاحتفاظ بحسب شهر الانضمام",
  'churn.cohorts.help': "تُقاس كل دفعة مقابل شهرها الأول. تتوقف الدفعات عند حدود تاريخها بدل حشوها بأصفار.",
  'churn.cohorts.incomplete': "{count} من الدفعات أحدث من أن تغطي النافذة كاملة، لذا تُترك أشهرها اللاحقة فارغة لا صفراً.",
  'churn.column.cohort': "الانضمام",
  'churn.column.size': "الحجم",
  'churn.column.retained': "ما زالوا يدفعون",
  'churn.empty': "لا شيء لقياسه بعد",
  'churn.empty.help': "بعد أن تمضي الاشتراكات شهراً أو شهرين سيظهر هنا الفقد والاحتفاظ.",
  'dunning.title': "متابعة الفواتير المتأخرة",
  'dunning.help': "ماذا يحدث ومتى، بعد تجاوز الفاتورة تاريخ استحقاقها. يُتحقق من السداد لحظة إرسال كل رسالة، فلا يُطالَب عميل قد دفع.",
  'dunning.limits': "درجة واحدة في كل تشغيل: الفاتورة المتأخرة شهوراً لا تتلقى السلّم كله في صباح واحد. ولا شيء هنا يلغي شيئاً — الدرجة الأخيرة تعرض الأمر على شخص، لأن قطع الخدمة عن عميل لا يمكن التراجع عنه.",
  'dunning.after': "بعد {days} يوماً من الاستحقاق",
  'dunning.action.email-customer': "مراسلة العميل",
  'dunning.action.notify-owner': "إبلاغ مسؤول الحساب",
  'dunning.action.escalate': "عرض الأمر على شخص ليقرر",
  'dunning.inactive': "(موقوفة)",
  'dunning.run': "متابعة الفواتير المتأخرة الآن",
  'dunning.run.result': "{sent} روسلوا، و{notified} من المسؤولين أُبلغوا، و{escalated} رُفعت، و{skipped} تُخطيت",
  'dunning.empty': "لا يوجد سلّم متابعة",
  'dunning.empty.help': "أضف الدرجة الأولى لتبدأ متابعة الفواتير المتأخرة من حينها.",
  'recognition.title': "المُحقَّق والمؤجَّل",
  'recognition.help': "الفاتورة ليست إيراداً. المبالغ المحصّلة مقدماً تبقى ديناً كخدمة حتى تُقدَّم، لذا يظهر ما تحقق في الشهر بجوار ما لا يزال مستحقاً في نهايته.",
  'recognition.column.month': "الشهر",
  'recognition.column.recognised': "المُحقَّق",
  'recognition.column.deferred': "مستحق كخدمة",
  'usage.meters.title': "الاستهلاك المُقاس",
  'usage.meters.help': "كيف يحوّل كل عدّاد قراءاته إلى رقم واحد للفترة. الخطأ هنا يضاعف الفاتورة أضعافاً، وتبقى الفاتورة بمظهر عادي.",
  'usage.aggregation.sum': "مجموع كل القراءات، بوحدة {unit}",
  'usage.aggregation.max': "أعلى رقم بلغته الفترة، بوحدة {unit}",
  'usage.aggregation.last': "الرقم الذي انتهت عليه الفترة، بوحدة {unit}",
  'invoices.help': "تُحتسب الاشتراكات في تاريخها الخاص كل فترة. المسودة هنا فاتورة رفض النظام إصدارها، وليست عملاً غير مكتمل.",
  'invoices.run': "تشغيل الفوترة الآن",
  'invoices.run.result': "صدرت {issued}، وبقيت {drafted} مسودة، وتم تخطي {skipped}",
  'invoices.drafts': "{count} فاتورة بقيت مسودة لتعذر احتساب الضريبة عليها. لم يُطلب من أحد دفعها.",
  'invoices.empty': "لا توجد فواتير بعد",
  'invoices.empty.help': "حدد للاشتراك تاريخ فوترة ونطاقاً ضريبياً، وستصدر فواتيره من ذلك الحين.",
  'invoices.unnumbered': "لم تُرقّم بعد",
  'invoices.column.number': "الرقم",
  'invoices.column.account': "الحساب",
  'invoices.column.period': "الفترة",
  'invoices.column.status': "الحالة",
  'invoices.column.gross': "الإجمالي",
  'invoices.status.draft': "مسودة",
  'invoices.status.issued': "صادرة",
  'invoices.status.paid': "مدفوعة",
  'invoices.status.void': "ملغاة",
  'recipes.help': "أتمتات ورحلات جاهزة. تثبيت أحدها يفتحه كمسودة في المحرر - ولا يفعل شيئًا حتى تشغّله.",
  'recipes.category': "الفئة",
  'recipes.category.all': "الكل",
  'recipes.category.routing': "التوزيع",
  'recipes.category.hygiene': "نظافة البيانات",
  'recipes.category.nurture': "الرعاية",
  'recipes.category.follow-up': "المتابعة",
  'recipes.category.alerting': "التنبيهات",
  'recipes.palette.automation': "أتمتة",
  'recipes.palette.journey': "رحلة",
  'recipes.install': "ثبّت كمسودة",
  'noun.recipe': "ذلك القالب",
  'governor.title': "حدود الأتمتة",
  'governor.runs': "تشغيلات الأتمتة هذه الساعة",
  'governor.calls': "الاتصالات الخارجية هذه الساعة",
  'governor.steps': "يتوقف التشغيل بعد {steps} خطوة. عند 80% من الحد الساعي يُنبَّه المسؤولون؛ وعند الحد تُرفض التشغيلات الجديدة حتى تنقضي الساعة.",
  'governor.designWarning': "من المرجح أن تكون هذه الأتمتة مكلفة:",
  'refusal.governor.runs': "بلغت حد الأتمتة: {cap} تشغيل في الساعة، وكلها مستخدمة. يعود الرصيد مع مرور ساعة على كل تشغيل.",
  'subscribe.title': "أرسل وفق جدول",
  'subscribe.help': "تُرسل الأرقام في نص الرسالة نفسها مع رابط. يمكن لأي شخص استلامها؛ وتعرض ما يحق لك رؤيته.",
  'subscribe.next': "التالي {at}",
  'subscribe.sendNow': "أرسل الآن",
  'subscribe.stop': "أوقف الإرسال",
  'subscribe.frequency': "كم مرة",
  'subscribe.preset.daily': "كل يوم الساعة 08:00",
  'subscribe.preset.weekdays': "أيام العمل الساعة 08:00",
  'subscribe.preset.weekly': "كل اثنين الساعة 08:00",
  'subscribe.preset.monthly': "أول كل شهر الساعة 08:00",
  'subscribe.recipients': "عناوين البريد مفصولة بفواصل",
  'subscribe.includeRows': "أرفق الصفوف الأولى",
  'subscribe.save': "اشترك",
  'refusal.subscription.recipients': "أرسلها إلى ما بين 1 و50 شخصًا.",
  'refusal.subscription.email': "ليس عنوان بريد: {emails}.",
  'analyses.title': "التحليلات",
  'cohorts.title': "الأفواج",
  'cohorts.basis': "جمّع الشركات حسب",
  'cohorts.basis.first-purchase': "أول شراء",
  'cohorts.basis.created': "تاريخ الإضافة",
  'cohorts.grain': "الفترة",
  'cohorts.grain.month': "شهر",
  'cohorts.grain.quarter': "ربع سنة",
  'cohorts.measure': "اعرض",
  'cohorts.measure.retention': "من اشترى مجددًا",
  'cohorts.measure.expansion': "ما أنفقوه",
  'cohorts.none': "لا توجد صفقات رابحة مرتبطة بشركة بعد، فلا أفواج لعرضها.",
  'cohorts.cohort': "الفوج",
  'cohorts.size': "الشركات",
  'cohorts.note': "الأعمدة فترات منذ بداية الفوج. كل خلية تعرض رقمها والتظليل يكرره فقط. الإيرادات تحسب صفقات {currency} فقط.",
  'cohorts.otherCurrency': "{count} صفقة رابحة بعملات أخرى ليست ضمن أرقام الإيرادات.",
  'coaching.title': "عرض التوجيه",
  'coaching.help': "نشاط كل شخص ونتائجه بجانب الوسيط للفريق - نقطة بداية لحوار توجيهي، لا ترتيب.",
  'coaching.period': "الفترة",
  'coaching.lastDays': "آخر {days} يومًا",
  'coaching.none': "لا نشاط ولا صفقات مغلقة في هذه الفترة.",
  'coaching.person': "الشخص",
  'coaching.type.call': "المكالمات",
  'coaching.type.email': "الرسائل",
  'coaching.type.meeting': "الاجتماعات",
  'coaching.type.task': "المهام",
  'coaching.won': "الرابحة",
  'coaching.winRate': "نسبة الفوز",
  'coaching.wonValue': "قيمة الرابحة",
  'coaching.trend': "آخر 8 أسابيع",
  'coaching.teamMedian': "وسيط الفريق",
  'coaching.atOrAbove': "عند وسيط الفريق أو أعلى",
  'coaching.worthTalking': "دون وسيط الفريق - يستحق الحديث عنه",
  'export.title': "تصدير البيانات",
  'export.objects': "ما الذي تصدّره",
  'export.schedule': "الجدول (cron، اختياري)",
  'export.run': "صدّر الآن",
  'export.fileMeta': "{rows} صف · {at}",
  'export.download': "تنزيل",
  'export.feeds': "تغذيات ذكاء الأعمال",
  'export.feedsHelp': "عنوان CSV حي لنوع واحد من السجلات، لـ Power BI أو Excel أو Google Sheets أو Metabase. الاتصال المباشر عبر SQL غير متاح بعد.",
  'export.lastRead': "آخر قراءة {at}",
  'export.neverRead': "لم تُقرأ بعد",
  'export.stopFeed': "أوقف هذه التغذية",
  'export.feedObject': "السجلات المطلوبة",
  'export.createFeed': "أنشئ تغذية",
  'export.feedOnce': "يظهر العنوان مرة واحدة - انسخ سطر أداتك:",
  'noun.dataExport': "ذلك التصدير",
  'noun.exportFile': "ذلك الملف",
  'refusal.export.object': "لا يمكن تصدير ذلك.",
  'refusal.export.objects': "اختر ما تريد تصديره.",
  'deliver.title': "قابلية التسليم (آخر 30 يومًا)",
  'deliver.sent': "أُرسلت {count}",
  'deliver.bounces': "الارتدادات {rate} (أبقها دون {line})",
  'deliver.complaints': "شكاوى البريد المزعج {rate} (أبقها دون {line})",
  'deliver.suppressed': "{count} عنوانًا محظورًا",
  'deliver.warning': "فوق الحد الذي يبدأ عنده مزودو البريد بالتصفية. نظّف الجمهور قبل الإرسال التالي.",
  'deliver.noWarmup': "لا إحماء: الحملات تُرسل كاملة.",
  'deliver.warmedUp': "انتهى الإحماء: الحملات تُرسل كاملة.",
  'deliver.warmupDay': "يوم الإحماء {day}: أُرسل {sent} من {cap} اليوم؛ {held} بانتظار الغد.",
  'deliver.startWarmup': "ابدأ الإحماء",
  'deliver.stopWarmup': "أوقف الإحماء",
  'deliver.domains': "نطاقات الإرسال",
  'deliver.domain.pending': "لم يُفحص بعد",
  'deliver.domain.verified': "موثّق",
  'deliver.domain.failed': "سجل غير صحيح - انظر أدناه",
  'deliver.verify': "افحص DNS الآن",
  'deliver.copy': "نسخ",
  'deliver.domain': "النطاق",
  'deliver.addDomain': "أضف نطاقًا",
  'web.title': "النشاط على الموقع",
  'web.seen': "أول ظهور {first}، آخر ظهور {last}؛ {sessions} زيارة",
  'web.sessionLine': "{pages} صفحة، نحو {minutes} دقيقة",
  'web.from': "من {referrer}",
  'journey.entry': "ما الذي يبدأ هذه الرحلة",
  'journey.startsWhen': "تبدأ عندما",
  'journey.trigger.entered-segment': "ينضمون إلى الجمهور",
  'journey.trigger.page-viewed': "يشاهدون صفحة",
  'journey.trigger.form-submitted': "يرسلون نموذجًا",
  'journey.trigger.email-clicked': "ينقرون رسالة بريد",
  'journey.trigger.custom-event': "يرسل منتجك حدثًا",
  'journey.trigger.field-changed': "يتغير حقل",
  'journey.urlContains': "عنوان الصفحة يحتوي على",
  'journey.embedId': "النموذج (فارغ لأي نموذج)",
  'journey.campaignId': "الحملة (فارغ لأي حملة)",
  'journey.eventName': "اسم الحدث",
  'journey.field': "الحقل",
  'journey.value': "يتغير إلى (فارغ لأي قيمة)",
  'journey.recentEvents': "الأحداث الأخيرة",
  'journey.anonymous': "لم يُعرّف بعد",
  'journey.noEvents': "لم يصل شيء بعد. أضف سكربت التتبع إلى موقعك لترى مشاهدات الصفحات هنا.",
  'sms.title': "قوالب الرسائل النصية",
  'sms.segmentsShort': "{count} مقطع، {encoding}",
  'sms.dlt.not-needed': "لا يلزم DLT",
  'sms.dlt.pending': "DLT قيد المراجعة",
  'sms.dlt.approved': "DLT معتمد",
  'sms.dlt.rejected': "DLT مرفوض",
  'sms.name': "اسم القالب",
  'sms.body': "الرسالة",
  'sms.bodyHint': "مرحبًا {{firstName|there}}، …",
  'sms.counter': "{chars} حرف · {segments} مقطع · {encoding} · يتبقى {left} في هذا المقطع",
  'sms.unicode': "\"{char}\" ليس ضمن أبجدية الرسائل القياسية، لذا يتسع كل مقطع لـ 70 حرفًا بدل 160.",
  'sms.to': "تقدير لـ",
  'sms.country': "الدولة",
  'sms.cost': "≈ {amount} (تقدير)",
  'sms.dltLabel': "تسجيل DLT",
  'sms.dltId': "معرّف قالب DLT",
  'sms.save': "احفظ القالب",
  'sms.campaignTemplate': "قالب الرسالة النصية",
  'sms.chooseTemplate': "اختر قالبًا",
  'noun.smsTemplate': "ذلك القالب",
  'refusal.sms.name': "أعطِ القالب اسمًا.",
  'refusal.sms.body': "اكتب الرسالة.",
  'refusal.sms.tooLong': "الرسالة 10 مقاطع كحد أقصى.",
  'refusal.sms.dltId': "قالب DLT المعتمد له معرّفه.",
  'refusal.sms.country': "الدولة رمزها المكوّن من حرفين، مثل AE أو IN.",
  'refusal.sms.dltEntity': "الإرسال إلى الهند يحتاج معرّف كيان DLT.",
  'refusal.sms.notDraft': "لا يأخذ قالبًا نصيًا إلا حملة رسائل نصية في المسودة.",
  'refusal.sms.noTemplate': "لا قالب لهذه الحملة النصية بعد.",
  'refusal.sms.dlt': "{count} مستلمًا في الهند، و\"{template}\" غير معتمد في DLT. سجّله لدى المشغّل وسجّل اعتماده أولًا.",
  'predict.title': "احتمال التحويل",
  'predict.waiting': "لا يكفي التاريخ بعد: يلزم {needed} عميلًا محتملًا مغلقًا إضافيًا (حتى الآن {closed}). حتى ذلك الحين استخدم الدرجة المبنية على القواعد.",
  'predict.chance': "احتمال التحويل {percent}",
  'predict.ruleScore': "الدرجة المبنية على القواعد: {score}",
  'predict.trust': "تعلّم من {closed} عميلًا محتملًا مغلقًا؛ وعند اختباره على عملاء لم يرهم، رتّب تحويلًا حقيقيًا فوق عدم التحويل بنسبة {auc}.",
  'nav.cadences': "التسلسلات",
  'cadence.title': "التسلسلات",
  'cadence.new': "تسلسل جديد",
  'cadence.none': "لا توجد تسلسلات بعد.",
  'cadence.on': "مفعّل",
  'cadence.off': "متوقف",
  'cadence.name': "اسم التسلسل",
  'cadence.exitOnReply': "أوقفه للشخص فور أن يرد أو يحجز اجتماعًا",
  'cadence.exitWarning': "مع إيقاف هذا، يستمر الناس في تلقي الخطوات بعد ردهم. نادرًا ما يريد أحد ذلك.",
  'cadence.wait': "انتظر {days} يوم",
  'cadence.channel': "الخطوة",
  'cadence.channel.email': "بريد",
  'cadence.channel.call': "مكالمة",
  'cadence.channel.linkedin': "لينكدإن",
  'cadence.channel.task': "مهمة",
  'cadence.after': "بعد",
  'cadence.days': "أيام",
  'cadence.removeStep': "أزل",
  'cadence.subject': "الموضوع",
  'cadence.words': "النص",
  'cadence.emailHint': "مرحبًا {{firstName|there}}، …",
  'cadence.taskHint': "ما يجب أن يفعله المندوب",
  'cadence.addStep': "أضف خطوة",
  'cadence.switchOn': "مفعّل",
  'cadence.save': "احفظ التسلسل",
  'cadence.enrolled': "المسجّلون",
  'cadence.nobody': "لا أحد مسجّل.",
  'cadence.atStep': "عند الخطوة {step}، التالية {at}",
  'cadence.status.completed': "انتهى",
  'cadence.status.exited': "غادر",
  'cadence.status.paused': "متوقف مؤقتًا",
  'cadence.status.active': "نشط",
  'cadence.takeOut': "أخرجه",
  'cadence.pick': "اختر تسلسلًا",
  'cadence.pickBody': "اختر واحدًا على اليسار، أو ابدأ تسلسلًا جديدًا.",
  'cadence.stepShort': "الخطوة {step}",
  'cadence.choose': "اختر تسلسلًا",
  'cadence.enrol': "سجّل",
  'cadence.theyReplied': "لقد ردّوا",
  'noun.cadence': "ذلك التسلسل",
  'refusal.cadence.name': "أعطِ التسلسل اسمًا.",
  'refusal.cadence.steps': "للتسلسل من 1 إلى 20 خطوة.",
  'refusal.cadence.email': "الخطوة {step} رسالة بريد وتحتاج إلى موضوع ونص.",
  'refusal.cadence.delay': "التأخير من 0 إلى 90 يومًا.",
  'refusal.cadence.inactive': "{name} غير مفعّل.",
};
